import { AreaDict } from './index';

/**
 * Role-portal translations: Property Management (owner), Tenant, CEO, Guest,
 * EA admin console, role select, portal chrome and the owner PM preview.
 *
 * Prefixes: pm = PropertyManagementView, tp = TenantPortal, ceo = CEOPortal,
 * gp = GuestPortal, ea = EAAdminConsole, rs = RoleSelectScreen,
 * pc = PortalChrome, opm = OwnerPMPreview.
 *
 * Labels already covered by LanguageContext (dashboard, properties, documents,
 * expenses, payments, requests, paid, vacant, occupied, summary, …) are reused
 * from there rather than duplicated here.
 */
export const portalsDict: AreaDict = {
  en: {
    // ==================== PROPERTY MANAGEMENT (OWNER) ====================
    pmRented: 'Rented',
    // Lifecycle steps
    pmStepSnagging: 'Snagging',
    pmStepSnaggingDesc: 'Defect inspection & fixes.',
    pmStepHandover: 'Handover',
    pmStepHandoverDesc: 'Keys received, DEWA activated.',
    pmStepPlacement: 'Tenant Placement',
    pmStepPlacementDesc: 'Tenant found & Ejari.',
    pmStepContracts: 'Contracts',
    pmStepContractsDesc: 'Tenancy contract signed.',
    pmStepRental: 'Rental Tracking',
    pmStepRentalDesc: 'Cheque schedule & payouts.',
    pmInProgressBadge: 'IN PROGRESS',
    // Tabs & chrome
    pmOverview: 'Overview',
    pmInspection: 'Inspection',
    pmMaintenance: 'Maintenance',
    pmInventory: 'Inventory',
    pmRefresh: 'Refresh',
    pmRefreshing: 'Refreshing…',
    pmLoadFailedTitle: 'Could not load your properties',
    pmLoadFailedBody: 'The connection timed out. Your portfolio is safe — this is only the app reaching the server. Please try again.',
    pmSampleNote: 'Sample data shown for preview — this fills in from your management team\'s records.',
    // Dashboard
    pmNoPropsText: 'Once a property is under management it will appear here with its income and expenses.',
    pmTotalRentalIncome: 'Total rental income',
    pmOf: 'of',
    pmRentedLower: 'rented',
    // Status filter above the property list. Active counts everything still
    // owned, so a rented unit is counted under Active and Rented both.
    pmStatusActive: 'Active',
    pmStatusRented: 'Rented',
    pmStatusSelling: 'Selling',
    pmStatusSold: 'Sold',
    pmNoneInStatus: 'Nothing here yet',
    // Document actions on a property. Wording matches the dashboard card the
    // owner already knows, so the two read as the same service.
    pmNoInspections: 'No inspections yet',
    pmNoInspectionsText: 'Move-in, mid-term and move-out inspections appear here once they have been carried out.',
    pmNoMaintenance: 'No maintenance raised',
    pmNoMaintenanceText: 'Repairs and callouts on this unit appear here, with what each one cost.',

    // Portfolio-wide maintenance board. {n} is substituted in code.
    pmBoardTitle: 'Maintenance requests',
    pmBoardOpenSub: '{n} of {total} still to close',
    pmBoardClearSub: 'Nothing open — every request has been closed off.',
    pmBoardEmptyText: 'Requests you or your tenants raise on any property appear here, from the day they are reported until they are closed.',
    pmBoardNoneInFilter: 'No requests with this status.',
    pmProperty: 'Property',
    pmPaid: 'Paid',
    pmOutstanding: 'Outstanding',
    pmNoExpensesYet: 'Nothing booked against this property yet. Service charges are invoiced to you directly and are often not recorded here.',
    pmNoInventory: 'No inventory recorded',
    pmNoInventoryText: 'Once the furnishings and appliances in this unit are listed, they appear here with their condition.',
    pmNoSampleUnit: 'No sample record for this property',
    pmNoSampleUnitText: 'The showcase carries full management records for a few properties only. Open one of those to see tenancy, inspections, maintenance and documents.',
    pmSubmitDocs: 'Submit Documents',
    pmSubmitDocsSub: 'Email us your files',
    pmMonthsLeft: 'months left',
    pmOrEmailDocs: 'or send your documents to',
    pmEmailDocs: 'Send your documents to',
    pmSmartUpload: 'AI Smart Upload',
    pmSmartUploading: 'Processing...',
    pmNoneInStatusText: 'No properties currently have this status.',
    pmOccupiedSuffix: 'occupied',
    pmRentPerYear: 'Rent / year',
    pmNetPerYear: 'Net / year',
    pmRentCollected: 'Rent collected',
    pmPaymentsDue: 'Payments due',
    pmLeaseRenewals: 'Lease renewals',
    pmDueSuffix: 'due',
    pmAllTenanciesCurrent: 'All tenancies are current — none expiring within the next 3 months.',
    pmExpiredPre: 'Expired',
    pmDayShort: 'd',
    pmAgo: 'ago',
    pmExpiresToday: 'Expires today',
    pmDay: 'day',
    pmDays: 'days',
    pmLeft: 'left',
    pmEnds: 'ends',
    pmRentalIncomeByProperty: 'Rental income by property',
    pmNoEarning: 'No properties are earning rent yet — income appears here once a lease is active.',
    pmOtherProperty: 'other property',
    pmOtherProperties: 'other properties',
    pmNotYetEarning: 'not yet earning rent',
    // --- Owner PM — portfolio figures derived from real unit data ---
    pmPortfolioValue: 'Portfolio value',
    pmGrossYield: 'Gross yield',
    pmYield: 'Yield',
    pmDueNext90: 'Due in 90 days',
    pmAcrossN: 'across',
    // --- Owner PM — get-more-from-your-portfolio prompts ---
    pmGrowTitle: 'Get more from your portfolio',
    pmGrowSub: 'Based only on what your portfolio shows today.',
    pmGrowIdle: 'properties are not earning rent yet',
    pmGrowIdle1: 'property is not earning rent yet',
    pmGrowIdleCta: 'Ask us to rent it out',
    pmGrowDocs: 'rented properties have no tenancy contract on file',
    pmGrowDocs1: 'rented property has no tenancy contract on file',
    pmGrowDocsCta: 'Send us the contract',
    pmGrowRenew: 'leases end within 90 days',
    pmGrowRenew1: 'lease ends within 90 days',
    pmGrowRenewCta: 'Ask us to renew',
    pmGrowAllGood: 'Every property is earning, documented and on a current lease. Nothing needs you right now.',
    // --- Owner PM — property management offer ---
    pmOfferTitle: 'Let us manage this property',
    pmOfferBody: 'Tenant placement, Ejari, rent collection, inspections and maintenance — handled by our team, and all of it appears on this screen.',
    pmOfferCta: 'Talk to us',
    // --- Owner PM — prefilled chat messages for the prompts above ---
    pmChatIdle: 'I have properties that are not earning rent yet. Can Asset Desk rent them out and manage them for me?',
    pmChatDocs: 'I would like to send you the tenancy contract for my rented properties.',
    pmChatRenew: 'I have a lease ending soon. Can you handle the renewal?',
    pmChatManage: 'I would like Asset Desk to manage this property:',
    pmOccupancyRate: 'Occupancy rate',
    pmOccupancySub: 'Sample data — average across your properties.',
    pmRentReceived: 'Rent received',
    pmPaidSuffix: 'paid',
    pmExpense: 'Expense',
    pmNoActivity: 'No activity yet.',
    // Properties list
    pmNoPropsManagedText: 'Properties under management will be listed here.',
    pmTenant: 'Tenant',
    pmLeaseEnds: 'Lease ends',
    pmMaintJob: 'maintenance job',
    pmMaintJobs: 'maintenance jobs',
    pmInProgressLower: 'in progress',
    // Property facts — construction, costs, mortgage, letting mode
    pmUnderConstruction: 'Under construction',
    pmBuilt: 'built',
    pmConstruction: 'Construction',
    pmExpectedHandover: 'Expected handover',
    pmExpectedRent: 'Expected rent',
    pmMarketEstimate: 'DLD market estimate',
    pmNoRentYet: 'No rent yet',
    pmExpectedRentFrom: 'Estimated from',
    pmRentalContracts: 'comparable DLD rental contracts',
    pmConfHigh: 'high confidence',
    pmConfMedium: 'medium confidence',
    pmConfLow: 'low confidence',
    pmServiceCharge: 'Service charge',
    pmPerYear: '/yr',
    pmPerSqftYear: 'AED/sqft/yr',
    pmFromHandover: 'from handover',
    pmServiceChargeNotYet: 'Service charges start at handover, so nothing is deducted yet.',
    pmNetAfterCosts: 'Net income after all costs',
    pmServiceChargeUnknown: 'No service charge is published for this building yet, so the figure above does not include one.',
    pmServiceChargeBooked: 'Taken from the service-charge invoice recorded under expenses.',
    pmMortgage: 'Mortgage',
    pmMortgageYes: 'Mortgaged',
    pmMortgageNo: 'No mortgage',
    pmMortgageLender: 'Bank / lender',
    pmMortgageOutstanding: 'Outstanding balance',
    pmMortgageMonthly: 'Monthly instalment',
    pmLettingMode: 'Letting',
    pmShortLet: 'Short-let',
    pmLongLet: 'Long-term let',
    pmShortLetBrand: 'Operator (e.g. DreamHaven)',
    pmPropertyFacts: 'Property Facts',
    pmPropertyFactsSub: 'Two things no registry publishes per unit. Tell us once and they appear on every report.',
    pmNotStated: 'Not stated',
    pmYes: 'Yes',
    pmNo: 'No',
    pmSave: 'Save',
    pmSaved: 'Saved',
    pmSaveFailed: 'Could not save — please try again.',
    pmCopy: 'Copy',
    pmCopied: 'Copied',
    pmCopyName: 'Copy property name',
    pmCopyRef: 'Copy reference number',
    pmRegistryDetails: 'Registry & utilities',
    pmAddExpense: 'Add expense',
    pmWithTeam: 'With your management team to confirm',
    pmSpendByCategory: 'What it went on',
    pmSpendByProperty: 'Cost by property',
    pmCostliest: 'your costliest property',
    pmNoTenants: 'No tenancies yet',
    pmNoTenantsText: 'Once a unit is let and the tenancy is registered, the tenant and their rent appear here.',
    pmAllRentOnTime: 'All rent on time',
    pmOverdue: 'Overdue',
    pmOnTime: 'On time',
    pmDaysLower: 'days',
    pmNextCheque: 'Next cheque',
    pmExpenseWhat: 'What was it for?',
    pmExpenseSaveError: 'Could not save that expense. Please try again.',
    pmProjectPhoto: 'Photo of this project',
    pmAddPhoto: 'Add photo',
    pmUploading: 'Uploading…',
    pmPhotoUploadFailed: 'The photo could not be uploaded — please try again.',
    pmNoPhotosYet: 'No photos of this property yet.',
    pmPhotoHint: 'Add one and it becomes the picture on your property card and in your reports.',
    pmSourceUnit: 'from your purchase contract',
    pmSourceDldRegistry: 'DLD project registry',
    pmSourceDldProgress: 'DLD project progress',
    pmSourceFeed: 'developer construction report',
    pmSourceIndex: 'DLD service-charge index',
    pmSourceInvoice: 'from the building invoice',
    pmSourceManual: 'entered by your management team',
    // Documents
    pmNoDocsText: 'Ejari, tenancy contracts and Emirates ID copies appear here once uploaded.',
    pmDocOpenError: 'Sorry — this document could not be opened. Please try again.',
    pmDocument: 'Document',
    pmView: 'View',
    pmDownload: 'Download',
    // Expenses
    pmTotalExpensesYear: 'Total expenses (year)',
    pmNoExpenses: 'No expenses recorded',
    pmNoExpensesText: 'Service charges and maintenance costs will show up here.',
    pmDue: 'Due',
    // Unit detail
    pmCurrentTenant: 'Current Tenant',
    pmMaintainedBy: 'Maintained by your management team',
    pmFinancialOverview: 'Financial Overview',
    pmOwnershipProgress: 'Ownership Progress',
    pmPhotosVideo: 'Photos & Video',
    pmTenantAnalytics: 'Tenant Analytics',
    pmPropertyNo: 'Property No.',
    pmPlotNo: 'Plot No.',
    pmStillNeeded: 'Still needed for the DLD Rental Index',
    pmDewaPremises: 'DEWA (Premise No.)',
    pmEjariNo: 'Ejari Contract Number',
    pmMakaniNo: 'Makani No.',
    pmContractStart: 'Contract Start Date',
    pmContractEnd: 'Contract End Date',
    pmCurrentAnnualRent: 'Current Annual Rent (AED)',
    pmUsage: 'Usage',
    pmFlat: 'Flat',
    pmRentDue: 'Rent due',
    pmOccupancy: 'Occupancy',
    pmOpenRequests: 'Open requests',
    pmLeaseUntil: 'Lease until',
    pmPhone: 'Phone',
    pmNextPaymentApproval: 'Next payment — needs your approval',
    pmRentCollectedFull: 'Rent collected in full',
    pmLastPayment: 'Last payment',
    pmNextPayment: 'Next payment',
    pmApprovePayment: 'Approve this payment',
    pmAllPaymentsConfirmed: 'All payments confirmed',
    pmNetIncomeYear: 'Net income (year)',
    pmEstimated: 'Estimated',
    pmLifecycle: 'Lifecycle',
    pmTenants: 'Tenants',
    // Airbnb income from DreamHaven (AirbnbIncome.tsx)
    pmAirbnbTab: 'Airbnb',
    pmLongTermTab: 'Long-term rentals',
    // Apartments / Offices split (PropertyManagementView)
    pmCatAll: 'All',
    pmCatApartments: 'Apartments',
    pmCatOffices: 'Offices',
    pmExpRental: 'Rental',
    pmExpPurchase: 'Purchase',
    pmAirbnbIncome: 'Airbnb income',
    pmAirbnbManagedBy: 'Managed by DreamHaven',
    pmAirbnbNet12: 'Net to you, last 12 months',
    pmAirbnbThisMonth: 'This month so far',
    pmAirbnbOccupancy: 'Occupancy',
    pmAirbnbNightsBooked: 'Nights booked',
    pmAirbnbGross: 'Host payout (gross)',
    pmAirbnbCleaning: 'Cleaning',
    pmAirbnbTourism: 'Tourism Dirham',
    pmAirbnbFee: 'DreamHaven fee (20%)',
    pmAirbnbNet: 'Net to you',
    pmAirbnbProvisional: 'Provisional',
    pmAirbnbCorrected: 'Corrected',
    pmAirbnbMonths: 'Month by month',
    pmAirbnbBookings: 'Bookings',
    pmAirbnbBookingsEmpty: 'No confirmed stays in the last 12 months.',
    pmAirbnbBookingsError: 'The bookings could not be loaded just now.',
    pmAirbnbNights: 'nights',
    pmAirbnbNight: 'night',
    pmAirbnbGuests: 'guests',
    pmAirbnbGuest: 'guest',
    pmAirbnbBookingsNote: 'One row per confirmed stay, reported by DreamHaven. The amount is the platform payout for the whole stay, before cleaning, Tourism Dirham and the management fee; your net is shown month by month above.',
    pmAirbnbProvisionalNote: 'The current month can still change while bookings are added or cancelled.',
    pmAirbnbHowNet: 'Net = (host payout − cleaning − Tourism Dirham) × 80%. Each amount counts in the month of the stay.',
    pmAirbnbEmpty: 'No months reported yet. They appear here after the next daily update.',
    pmAirbnbSample: 'Sample data',
    pmAirbnbUpdated: 'Updated',
    pmAirbnbOpen: 'Open',
    pmAirbnbNetSince: 'Net to you since',
    pmAirbnbChartTitle: 'Net to you per month',
    pmAirbnbSoFar: 'so far',
    pmAirbnbApartment: 'Apartment',
    pmAirbnbTotal: 'Total',
    pmAirbnbViewListing: 'View listing on Airbnb',
    pmRetained: 'Retained',
    pmRetention: 'Retention',
    pmWhyTenantsLeft: 'Why tenants left',
    pmAllRenewed: 'Every past tenant renewed — excellent retention. 🎉',
    pmPrivacyNote: 'Tenant maintenance requests are handled directly by our internal team — you won\'t be disturbed unless a decision or cost approval is needed.',
    pmSignedInAs: 'Signed in as',
    pmOwner: 'Owner',
    pmPreviewData: 'Preview data',
    // Inspection sub-tab
    pmInspectionReports: 'Inspection reports',
    pmInspectionSub: 'Move-in, move-out & periodic checks by your team.',
    pmReports: 'Reports',
    pmPassed: 'Passed',
    pmAttention: 'Attention',
    pmPass: 'Pass',
    pmViewReport: 'View report',
    // Maintenance sub-tab
    pmMaintSub: 'Handled directly by your internal team.',
    pmStatusOpen: 'Open',
    pmOpen: 'Open',
    pmInternalTeam: 'Internal team',
    pmInternalMaintTeam: 'Internal maintenance team',
    pmGeneral: 'General',
    pmMaintReport: 'Maintenance report',
    // Inventory sub-tab
    pmAssetInventory: 'Asset inventory',
    pmInventorySub: 'Furniture & appliances handed over with the unit.',
    pmRooms: 'Rooms',
    pmItems: 'Items',
    pmQty: 'Qty',
    pmCondGood: 'Good',
    pmCondFair: 'Fair',
    pmCondPoor: 'Poor',
    // Sample records (shown until the management team supplies real records)
    pmSampleInsp1Type: 'Move-in Inspection',
    pmSampleInsp1Notes: '12 items snagged at handover; 10 resolved. 2 minor items pending: living-room AC vent rattle and balcony door-lock alignment.',
    pmSampleInsp2Type: 'Periodic Inspection',
    pmSampleInsp2Notes: 'Property in good condition. AC filters and cleaning up to date. No new issues found.',
    pmSampleMaint1: 'AC servicing (annual)',
    pmSampleMaint1Cat: 'HVAC',
    pmSampleMaint2: 'Kitchen sink leak repair',
    pmSampleMaint2Cat: 'Plumbing',
    pmSampleMaint3: 'Water heater replacement',
    pmSampleMaint3By: 'Awaiting your approval',
    pmRoomLiving: 'Living Room',
    pmRoomKitchen: 'Kitchen',
    pmRoomMaster: 'Master Bedroom',
    pmItemSofa: '3-seat sofa',
    pmItemTv: 'Smart TV 55"',
    pmItemCoffeeTable: 'Coffee table',
    pmItemFridge: 'Refrigerator',
    pmItemOven: 'Oven & hob',
    pmItemWasher: 'Washing machine',
    pmItemBed: 'King bed & mattress',
    pmItemWardrobe: 'Built-in wardrobe',
    pmItemCurtains: 'Curtains',

    // ==================== TENANT PORTAL ====================
    tpMyHome: 'My Home',
    tpPreviewData: 'Preview data',
    tpStaff: 'Staff',
    tpTenantWorkspace: 'Tenant workspace',
    tpOwnerWorkspace: 'Owner workspace',
    // Login
    tpAccessEyebrow: 'TENANT ACCESS',
    tpLoginTitle: 'Tenant Login',
    tpCheckInbox: 'Check your inbox',
    tpLoginSub: 'Enter your email and we’ll send you a login code',
    tpCodeSub: 'Enter the 6-digit code we emailed you',
    tpCodeLabel: '6-digit code',
    tpSendCode: 'Send code',
    tpVerifySignIn: 'Verify & sign in',
    tpChangeEmail: 'Change email',
    tpResendCode: 'Resend code',
    tpEnterEmail: 'Please enter your email',
    tpCodeSentTo: 'We emailed a 6-digit code to',
    tpCodeSendFailed: 'Could not send the code. Please try again.',
    tpEnterCode: 'Enter the 6-digit code',
    tpCodeInvalid: 'Incorrect or expired code.',
    // Overview
    tpRentOverdueBy: 'Rent payment overdue by',
    tpRentDueToday: 'Rent payment due today',
    tpRentDueIn: 'Rent payment due in',
    tpDay: 'day',
    tpDays: 'days',
    tpDaysDat: 'days',
    tpDueLower: 'due',
    tpTapToView: 'tap to view',
    tpDueToday: 'Due today',
    tpDueIn: 'Due in',
    tpLeaseEnded: 'Lease ended',
    tpDaysLeft: 'days left',
    tpMonthsLeft: 'months left',
    tpNextPaymentDue: 'Next payment due',
    tpViewSchedule: 'View schedule',
    tpRentPaidThisYear: 'Rent paid this year',
    tpPaidLower: 'paid',
    tpTotalLower: 'total',
    tpRecentRequest: 'Recent request',
    tpNoOpenRequests: 'No open requests',
    tpTapToReport: 'Tap to report a maintenance issue',
    tpLatestDocuments: 'Latest documents',
    tpReportIssue: 'Report an issue',
    tpMyDocuments: 'My documents',
    tpPaymentPlan: 'Payment plan',
    tpContactTeam: 'Contact team',
    // --- Valuation dispute. An automated estimate is evidence, not gospel;
    //     an owner who knows something the DLD record does not (a full
    //     renovation, a rented-out unit, the wrong floor on file) needs a way
    //     to say so and get a human answer. ---
    vrRecheckTitle: 'Does this look wrong?',
    vrRecheckBody: 'The estimate is built from registered DLD transactions and cannot see an upgraded kitchen, a premium view or a unit that is already rented. Tell us what it is missing and our team will review it by hand and send you a written valuation.',
    vrRecheckCta: 'Ask us to recheck this valuation',
    vrRecheckAction: 'Request re-evaluation',
    prTargetSet: 'Set your target',
    prTargetChange: 'Change target',
    prTargetLabel: 'Annual net rent you are aiming for',
    prTargetSave: 'Save target',
    prTargetCancel: 'Cancel',
    prTargetReset: 'Use suggested',
    prTargetHint: 'Saved on this device. Tell your manager if you would like it on your account.',
    vrDisclaimerBody: 'This report is an automated market indication for information purposes, generated from Dubai Land Department transaction records. It is not a RICS-certified appraisal and does not replace a professional valuation. The estimate assumes the property is in standard condition and vacant; upgrades, fit-out, floor, view and service-charge levels are not reflected. Do not rely on it as the price for a transaction.',
    vrCostNote: 'Standard Dubai schedule \u2014 DLD transfer 4% plus AED 580 admin, trustee office fee plus 5% VAT, agency commission 2% plus 5% VAT. Mortgage registration, NOC and service-charge apportionment are not included.',
    vrCostNoteThin1: 'Calculated on an indicative value backed by fewer than',
    vrCostNoteThin2: 'comparables \u2014 treat the total as approximate.',
    vrThinCompare: 'Too few recent same-building, same-bedroom resales to compare against your purchase price with confidence. The market estimate above still stands as an indication.',
    // --- Lease renewal reminder. Dubai practice is a 90-day notice before a
    //     contract ends; the card counts down to that window so a tenant is
    //     never surprised by it. ---
    tpRenewalTitle: 'Lease renewal',
    tpRenewalEndsIn: 'Your lease ends in',
    tpRenewalEndsOn: 'ends on',
    tpRenewalStepNotice: 'Renewal notice window opens',
    tpRenewalStepNoticeSub: '90 days before the contract ends — either side must give notice by then',
    tpRenewalStepDecide: 'Renew or give notice',
    tpRenewalStepDecideSub: 'Tell us early and we hold the unit for you',
    tpRenewalStepNew: 'New contract starts',
    tpRenewalStepNewSub: 'Ejari renewed, cheques rescheduled',
    tpRenewalTalk: 'Talk to us about renewing',
    tpRenewalOpenNow: 'Open now',
    tpRenewalDone: 'Done',
    tpRenewalDoc: 'View',
    tpSampleDocTitle: 'Document preview',
    tpSampleDocNote: 'Sample document',
    tpSampleDocProperty: 'Property',
    tpSampleDocTenant: 'Tenant',
    tpSampleDocPeriod: 'Contract period',
    tpSampleDocRent: 'Annual rent',
    tpSampleDocEjari: 'Ejari number',
    tpSampleDocClose: 'Close',
    // Payments
    tpAnnualRent: 'Annual rent',
    tpCheque: 'cheque',
    tpCheques: 'cheques',
    tpChequeLabel: 'Cheque',
    tpPayment: 'payment',
    tpPayments: 'payments',
    tpOverdueLower: 'overdue',
    tpContactToSettle: 'Please contact the management team to settle.',
    tpChequeSchedule: 'Cheque schedule',
    // Requests
    tpRequestsNote: 'Your requests go straight to our internal management team. We handle them directly — the property owner is not disturbed.',
    tpNewRequest: 'New maintenance request',
    tpMyRequests: 'My Requests',
    tpNoRequests: 'No requests yet.',
    tpSubmitted: 'Submitted',
    tpInReview: 'In review',
    tpResolved: 'Resolved',
    tpNewRequestTitle: 'New Request',
    tpTitle: 'Title',
    tpTitlePlaceholder: 'e.g. AC not cooling',
    tpDetails: 'Details',
    tpDetailsPlaceholder: 'Describe the issue...',
    tpSubmitToTeam: 'Submit to management team',
    tpJustNow: 'Just now',
    tpRecently: 'Recently',
    tpTenant: 'Tenant',
    tpYourUnit: 'Your unit',
    tpDocument: 'Document',
    // Documents
    tpNeedAnotherDoc: 'Need another document?',
    tpMessageTeam: 'Message the management team',

    // ==================== CEO PORTAL ====================
    ceoExecutive: 'Executive',
    ceoOwed: 'Owed',
    ceoVat: 'VAT',
    ceoCompliance: 'Compliance',
    ceoExecutiveSummary: 'Executive Summary',
    ceoMoneyOwed: 'Money Owed',
    ceoVatPosition: 'VAT Position',
    ceoPreviewData: 'Preview data',
    // Access screens
    ceoAccessEyebrow: 'EXECUTIVE ACCESS',
    ceoOpening: 'Opening your company',
    ceoOneMoment: 'One moment — using your Asset Desk sign-in',
    ceoNotAvailable: 'Not available yet',
    ceoNoCompanyLinked: 'This account doesn’t have a company linked',
    ceoNotOwner: 'This account isn’t listed as a company owner.',
    ceoUseDifferentEmail: 'Use a different email',
    ceoAccessTitle: 'CEO Access',
    ceoCheckInbox: 'Check your inbox',
    ceoLoginSub: 'Enter your email and we’ll send you a login code',
    ceoCodeSub: 'Enter the 6-digit code we emailed you',
    ceoCodeLabel: '6-digit code',
    ceoSendCode: 'Send code',
    ceoVerifyContinue: 'Verify & continue',
    ceoChangeEmail: 'Change email',
    ceoResendCode: 'Resend code',
    ceoViewPreview: 'View preview',
    ceoNotConfigured: 'This build isn’t connected to the company portal yet. Open preview to see the executive screens.',
    ceoEnterEmail: 'Please enter your email',
    ceoCodeSentTo: 'We emailed a 6-digit code to',
    ceoCodeSendFailed: 'Could not send the code. Please try again.',
    ceoEnterCode: 'Enter the 6-digit code',
    ceoCodeInvalid: 'Incorrect or expired code.',
    // Executive summary
    ceoYearToDate: 'year to date',
    ceoMargin: 'margin',
    ceoRevenue: 'Revenue',
    ceoOutstanding: 'Outstanding',
    ceoInvoice: 'invoice',
    ceoInvoices: 'invoices',
    ceoNetVat: 'Net VAT',
    ceoDuePrefix: 'Due',
    ceoLicence: 'Licence',
    ceoCashAtBank: 'Cash at bank',
    ceoAccountsInTotal: 'accounts in total',
    ceoTotal: 'Total',
    ceoBalancesUpdated: 'Balances last updated',
    ceoNetOfVat: 'net of VAT',
    ceoTopCustomers: 'Top customers',
    ceoRevenueLower: 'revenue',
    ceoNoRevenue: 'No revenue recorded yet.',
    ceoTopSuppliers: 'Top suppliers',
    ceoSpendLower: 'spend',
    ceoNoExpenses: 'No expenses recorded yet.',
    ceoPeak: 'peak',
    ceoMonth: 'month',
    ceoMonths: 'months',
    ceoChartAria: 'Monthly revenue against expenses',
    // Receivables
    ceoTotalOutstanding: 'Total outstanding',
    ceoWithinTerms: 'within terms',
    ceoOverdueLower: 'overdue',
    ceoPastDue: 'past due',
    ceoChaseFirst: 'Chase these first — it is the fastest cash in the business.',
    ceoLargestOverdue: 'Largest overdue balances',
    ceoNothingOverdue: 'Nothing overdue. Every invoice is within terms.',
    ceoDueLower: 'due',
    ceoDayShort: 'd',
    ceoLate: 'late',
    // VAT
    ceoNoVatPeriod: 'No VAT period configured',
    ceoNoVatPeriodSub: 'Ask your accountant to set the filing frequency in the company portal.',
    ceoDueIn: 'Due in',
    ceoPayableFta: 'payable to the FTA',
    ceoRefundableFta: 'refundable from the FTA',
    ceoFilingDue: 'filing due',
    ceoOutputVat: 'Output VAT',
    ceoChargedOnSales: 'charged on sales',
    ceoInputVat: 'Input VAT',
    ceoReclaimable: 'reclaimable',
    ceoPeriod: 'Period',
    ceoCovers: 'Covers',
    ceoSpend: 'Spend',
    ceoInclVat: '(incl. VAT)',
    ceoNet: '(net)',
    ceoVatNote: 'Figures come straight from the company portal ledger. The return itself is prepared and filed there — this screen is the executive view of the position.',
    // Compliance
    ceoEntity: 'Entity',
    ceoJurisdiction: 'Jurisdiction',
    ceoLicenceNo: 'Licence no.',
    ceoRenewals: 'Renewals',
    ceoNext90Days: 'next 90 days',
    ceoNothingExpiring: 'Nothing expiring in the next 90 days.',
    ceoNoDateOnFile: 'no date on file',
    ceoNoDate: 'no date',
    ceoExpiredSuffix: 'expired',
    ceoExpiresToday: 'expires today',
    ceoLeft: 'left',
    ceoAllClear: 'All clear',

    // ============ AIRBNB GUEST PORTAL (short-stay booking) ============
    gpAccessEyebrow: 'SHORT STAY GUEST',
    gpBookStay: 'Book a Stay',
    gpSub: 'Short stays in Asset Desk homes',
    gpFeaturedStays: 'Featured stays',
    gpGuests: 'guests',
    gpPerNight: '/ night',
    gpAllStays: 'All stays',
    gpBookingSoon: 'Booking coming soon',
    gpBookingSoonText: 'The full short-stay booking flow (dates, guests, payment) is being finalised. Tell us how you’d like guests to book and we’ll build it here.',
    gpReserve: 'Reserve',

    // ==================== MESSAGES (owner <-> management) ====================
    // om = OwnerMessagesView, inb = AdminInbox. Deliberately plain wording:
    // this is the human team, and it must not read like the AI assistant.
    omTitle: 'Messages',
    omSectionTitle: 'Your management team',
    omSubtitle: 'Direct line to your property managers',
    omEmptyTitle: 'No messages yet',
    omEmptyText: 'Write to your management team here. They answer inside the app, and everything stays on one thread.',
    omPlaceholder: 'Write a message…',
    omSend: 'Send',
    omYou: 'You',
    omLoadFailed: 'Could not load your messages. Please try again.',
    omSendFailed: 'Your message was not sent. Please try again.',

    // ==================== ADMIN INBOX ====================
    inbFilterOpen: 'Inbox',
    inbFilterArchived: 'Archived',
    inbFilterAll: 'All',
    inbSearch: 'Search conversations...',
    inbSearchOwners: 'Search owners...',
    inbClear: 'Clear',
    inbNew: 'New',
    inbNewTitle: 'New conversation',
    inbEmptyTitle: 'Nothing waiting',
    inbEmptyText: 'Owner messages arrive here. Start one yourself with New.',
    inbThreadEmptyTitle: 'No messages yet',
    inbThreadEmptyText: 'Write the first message. The owner sees it in their app straight away.',
    inbPlaceholder: 'Reply to the owner…',
    inbConversation: 'Conversation',
    inbYouPrefix: 'You:',
    inbNewCount: 'new',
    inbStatusOpen: 'Open',
    inbNoMessagesYet: 'No messages yet',
    inbArchive: 'Archive',
    inbUnarchive: 'Restore',
    inbCancel: 'Back',
    inbNoOwners: 'No owners found',
    inbNoOwnersText: 'Owners appear here once they are on the system.',
    inbNoEmail: 'No email on file',
    inbHasThread: 'Open thread',
    inbLoadFailed: 'Could not load the inbox. Please try again.',
    inbSendFailed: 'Your reply was not sent. Please try again.',

    // ==================== EA ADMIN CONSOLE ====================
    eaTabMessages: 'Messages',
    eaBackToConsole: 'Admin console',
    eaTabCustomers: 'Customers',
    eaTabAssetDesk: 'Asset Desk',
    eaTabPm: 'Property Management',
    eaTabChat: 'Chat',
    eaTabIssues: 'Issues',
    eaTabAccess: 'Access log',
    eaOpenFullScreen: 'Full screen',
    eaOpenConsole: 'Open console',
    adFilterWaiting: 'Awaiting review',
    adFilterVerified: 'Verified',
    adFilterRejected: 'Rejected',
    adNoneHere: 'Nothing here',
    adAllClearText: 'Every document has been reviewed. New uploads land here.',
    adVerify: 'Verify',
    adRevoke: 'Revoke',
    adReject: 'Reject',
    adConfirm: 'Confirm',
    adRejectReason: 'Reason for the investor',
    adDecisionFailed: 'That did not save. Please try again.',
    isStart: 'Start',
    isResolve: 'Resolve',
    isReopen: 'Reopen',
    isUpdateFailed: 'The status did not save. Please try again.',
    adSignIn: 'Sign in',
    adSigningIn: 'Signing in…',
    adSignOut: 'Sign out',
    adPassword: 'Admin password',
    adEnterPassword: 'Enter the admin password.',
    adSignInFailed: 'Could not sign in. Please check the password.',
    adSignInWhy: 'Asset Desk needs the admin password — the same one the console uses. It is asked once and remembered on this device.',
    adFullConsole: 'Full console',
    adUsers: 'Users',
    adLogins: 'Logins',
    adPageViews: 'Page views',
    adDocViews: 'Doc views',
    adAwaiting: 'awaiting review',
    adTotalDocs: 'documents',
    adNothingWaiting: 'Nothing waiting',
    adNothingWaitingText: 'Documents needing verification appear here.',
    adLoadFailed: 'Could not load Asset Desk. Please try again.',
    pdOverview: 'Overview',
    pdProperties: 'Properties',
    pdRented: 'Rented',
    pdVacant: 'Vacant',
    pdOffplan: 'Off-plan',
    pdRenewals: 'Renewals in 3 months',
    pdAnnualRent: 'Annual rent',
    pdOverdue: 'Rent overdue',
    pdAcross: 'across',
    pdUnit: 'unit',
    pdUnits: 'units',
    pdDefaulters: 'Defaulters',
    pdDefaultersSub: 'with a cheque past its date',
    pdRentCollection: 'Rent collection',
    pdInvoiced: 'Invoiced',
    pdCollected: 'Collected',
    pdOutstanding: 'Outstanding',
    pdServiceRequests: 'Service requests',
    pdIncomeByProperty: 'Rental income by property',
    apSearch: 'Search units, owners, tenants...',
    apUnits: 'properties',
    apNoUnits: 'No properties yet',
    apNoUnitsText: 'Units appear here as soon as they are on the system.',
    apStatusUnknown: 'No status',
    apVacant: 'Vacant',
    apVacantText: 'Nobody is in this property at the moment.',
    apOpenJobs: 'open',
    apAllUnits: 'All properties',
    apTenant: 'Tenant',
    apName: 'Name',
    apEmail: 'Email',
    apPhone: 'Phone',
    apContract: 'Contract',
    apEjari: 'Ejari',
    apRent: 'Rent collected',
    apCheque: 'Cheque',
    apPaid: 'Paid',
    apDue: 'Due',
    apNoRent: 'No rent schedule on this unit.',
    apMaintenance: 'Maintenance',
    apNoMaintenance: 'Nothing reported.',
    apNoDocuments: 'No documents on this unit.',
    apNoInspections: 'No inspections recorded.',
    apNoExpenses: 'No expenses recorded.',
    apEditInConsole: 'Uploads and edits in the console',
    apLoadFailed: 'Could not load the properties. Please try again.',
    isOpenCases: 'Open cases',
    isResolved: 'Resolved',
    isSearch: 'Search issues, units, tenants...',
    isNoneOpen: 'Nothing open',
    isNoneResolved: 'Nothing resolved yet',
    isEmptyText: 'Maintenance and tenant requests across every property appear here, newest first.',
    isUnknownUnit: 'Unassigned unit',
    isFromTenant: 'raised by tenant',
    isStatusNew: 'New',
    isStatusOpen: 'Open',
    isStatusInProgress: 'In progress',
    isStatusResolved: 'Resolved',
    isLoadFailed: 'Could not load the issues. Please try again.',
    eaConsoleNotEmbedded: 'Opens in a new tab for now. Showing it inside the app needs the framing headers relaxed on the console — a security change that has not been made yet.',
    eaAssetDeskNote: 'Asset Desk · investors, units, documents',
    eaPmNote: 'Property Management · tenancies, rent, handover',
    eaConsoleNarrow: 'This console is built for a desktop screen. It works here, but it is easier to use on a laptop.',
    eaConsoleTitle: 'Admin · EA Console',
    eaMyView: 'My view',
    eaExit: 'Exit',
    eaExitViewAs: 'Exit view-as',
    eaViewingAs: 'Viewing as',
    eaSearchCustomers: 'Search customers...',
    eaCustomers: 'customers',
    eaUnit: 'unit',
    eaUnits: 'units',
    eaTenant: 'Tenant',
    eaPropertyOwner: 'Property Owner',
    eaPmActive: 'PM active',
    eaAllCustomers: 'All customers',
    eaOpenAsCustomer: 'Open the app as this customer',
    eaSeeExactly1: 'See exactly what',
    eaSeeExactly2: 'sees, with their live data.',
    eaOpenAppPre: 'Open ',
    eaOpenAppPost: ' app',
    eaDocsOnFile: 'Documents on file',
    eaNoUnitLinked: 'No unit linked to this customer yet.',
    eaUploadDocument: 'Upload document',
    eaFile: 'File',
    eaChooseFile: 'Tap to choose a file',
    eaUploadTo: 'Upload to',

    // ==================== ROLE SELECT ====================
    rsWelcome: 'Welcome',
    rsChooseHow: 'Choose how you would like to continue',
    rsSecurityNote: 'Encrypted connection · Passwords never stored in plain text',
    rsLegal: 'Legal',
    rsTerms: 'Terms of Service',
    rsPrivacy: 'Privacy Policy',
    rsOwnerTitle: 'Property Owner',
    rsOwnerSub: 'Portfolio, documents and property management',
    rsTenantTitle: 'Tenant',
    rsTenantSub: 'Your home, contract, payments and requests',
    rsGuestTitle: 'Short Stay Guest',
    rsGuestSub: 'Book a short stay',
    rsDemoTitle: 'Guest Access',
    rsDemoSub: 'Access code required — explore every view with sample data',
    rsCeoTitle: 'CEO',
    rsCeoSub: 'Company performance, receivables, VAT and compliance',
    rsPartnerTitle: 'Partner',
    rsPartnerSub: 'Cooperation and referral partnerships, commissions',
    rsServicesTitle: 'What We Offer',
    rsServicesSub: 'Every service, from company formation to selling your unit',

    rsTourTitle: 'How The App Works',
    rsTourSub: 'A short film, every screen explained, and how we get you started',

    // ==================== WELCOME (first screen) ====================
    weHeadline: 'Your property wealth. In one place.',
    wePoint1: 'Know what you own',
    wePoint2: 'See what you earn',
    wePoint3: 'Know what to do next',
    weSub: 'For owners of property in Dubai — from the first apartment to the whole portfolio.',
    weGetStarted: 'Get Started',
    weTour: 'See how it works',
    weEyebrow: 'Private client access',
    weHeadlineA: 'Your property wealth,',
    weHeadlineB: 'in one place.',
    weSignIn: 'Sign in',
    weCreateAccount: 'Create account',
    weTourSub: 'A short guided tour of the app',

    // ==================== ROLE SWITCH (on the sign-in screens) ====================
    rwCaption: 'Select who you are and get started',

    // ==================== APP TOUR (pre-login) ====================
    tourBack: 'Back',
    tourEyebrow: 'A Guided Tour',
    tourTitle: 'See the app before you sign in',
    tourSub: 'What we do for your property, and the screen it all lands on.',
    tourPlay: 'Watch the tour',
    tourSilent: 'Screen recording, no sound — what each screen does is written out below.',

    // Sample figures shown right after the film, labelled as an example.
    tourKpiLabel: 'Sample portfolio',
    tourKpiValueLabel: 'Portfolio value',
    tourKpiValue: 'AED 24,850,000',
    tourKpiGrowthLabel: 'Value growth',
    tourKpiGrowth: '+ AED 3,420,000 · +15.9 %',
    tourKpiRentLabel: 'Net rent',
    tourKpiRent: 'AED 782,000\u00a0/\u00a0year',
    tourKpiNextLabel: 'Next payment',
    tourKpiNext: 'AED 425,000 in\u00a023\u00a0days',
    tourKpiNote: 'Example figures. After sign-in, this is your own portfolio.',

    tourWhyKicker: 'Three questions',
    tourWhyTitle: 'Not more features. Three answers.',
    tourWhyLede: 'You are in Europe, your property is not. Everything in the app answers one of three questions — and the valuation comes from Dubai Land Department records, not from our opinion of your property.',
    tourQ1Title: 'What do I own?',
    tourQ1a: 'Every unit, in one list',
    tourQ1b: 'Contracts, title deeds and payment plans',
    tourQ1c: 'Market value from real DLD transactions',
    tourQ2Title: 'What do I earn?',
    tourQ2a: 'Rent received, and rent still due',
    tourQ2b: 'Net rent per year and yield',
    tourQ2c: 'Value growth since purchase',
    tourQ2d: 'Profit on units already sold',
    tourQ3Title: 'What do I need to do?',
    tourQ3a: 'The next instalment to the developer',
    tourQ3b: 'Leases that are running out',
    tourQ3c: 'Handover and missing documents',
    tourQ3d: 'What a sale would leave you with',

    // Heading and closing line for the management chapter. The twelve services
    // themselves are the scPmB* copy from the showcase, reused rather than
    // rewritten — one source for what we actually do.
    // (No straight apostrophes in comments here: check-i18n-parity scans for
    // quotes without stripping comments, and one breaks the key pairing.)
    tourPmFullTitle: 'Full Property Management',
    tourPmFullSub: 'From the day the keys are handed over, our team does the work — and every bit of it lands on a screen you can open.',
    tourPmMeansLabel: 'What that leaves you with:',
    tourPmMeans: 'A property that earns, a decision only when one is genuinely yours, and a written record of everything else.',
    tourPmSeeAll: 'The full management service',

    tourBenKicker: 'And the app itself',
    tourBenTitle: 'What the software adds',
    tourBenLede: 'The work above is done by people. These four are the things only the app can give you.',
    tourBen1Title: 'Valued from real transactions',
    tourBen1Body: 'Every unit priced off Dubai Land Department sales records, with the comparable sales, the six-month trend and the price per sqft behind the number — and a button to have it rechecked by hand if you disagree.',
    tourBen2Title: 'One screen for everything waiting on you',
    tourBen2Body: 'Handover dates, instalments falling due, DEWA activation, lease renewals. Ordered by what happens next, not by when it was filed.',
    tourBen3Title: 'Both currencies, both languages',
    tourBen3Body: 'Every figure in AED and EUR, every screen and every report in English or German — so your accountant at home reads the same numbers you do.',
    tourBen4Title: 'Reports you can hand to a bank',
    tourBen4Body: 'Portfolio and per-unit valuation reports, exportable as PDF, written to be read by someone who was not in the conversation.',

    tourShotsKicker: 'What is inside',
    tourShotsTitle: 'The screens, one by one',
    tourShotsLede: 'Real captures from the owner portal — the same panels your management team works from. Swipe through them.',
    tourShotDashTitle: 'Dashboard',
    tourShotDashCap: 'What the portfolio is worth, and everything waiting on a decision from you.',
    tourShotPortTitle: 'Portfolio at a glance',
    tourShotPortCap: 'Total invested, annual net rent, progress against your rent target, and income from units sold.',
    tourShotValTitle: 'Valuation',
    tourShotValCap: 'DLD market value per unit, the six-month trend, and the comparable sales it was built from.',
    tourShotRepTitle: 'Portfolio report',
    tourShotRepCap: 'A private report in euros or dirhams, written to be read — and exported as a PDF.',
    tourShotPmTitle: 'Property management',
    tourShotPmCap: 'Rent collected against rent due, occupancy, expenses and service charges across every unit.',
    tourShotUnitTitle: 'Each property',
    tourShotUnitCap: 'Overview, inspection, maintenance, expenses and documents — per unit, with its tenant and cheque dates.',
    tourShotLifeTitle: 'Ownership lifecycle',
    tourShotLifeCap: 'Snagging, handover, tenant placement, contracts and rental tracking, ticked off as they complete.',
    tourShotTenantTitle: 'Tenant and payments',
    tourShotTenantCap: 'Who is in your unit, until when, and the next cheque with its due date.',
    tourShotSignTitle: 'Signing in',
    tourShotSignCap: 'Email and password, Face ID after the first time, or a WhatsApp code.',

    tourStepsKicker: 'Getting started',
    tourStepsTitle: 'How we onboard you',
    tourStepsLede: 'Four steps, and only one of them is yours.',
    tourStep1Title: 'We invite you',
    tourStep1Body: 'An invitation goes to the email address we hold for you. Nothing to install — it runs in your browser, and as an app on iPhone and Android.',
    tourStep2Title: 'You sign in and set a password',
    tourStep2Body: 'Once. After that it is Face ID or a WhatsApp code.',
    tourStep3Title: 'We load your portfolio',
    tourStep3Body: 'Your units, contracts, payment plans and documents are entered from the records we already hold. You do not type anything in.',
    tourStep4Title: 'We keep it current',
    tourStep4Body: 'New instalments, handover dates, renewals and valuations appear as they happen. Anything unclear goes to your manager on WhatsApp.',

    tourEndTitle: 'Ready when you are',
    tourEndSub: 'Sign in with the invitation we sent you, or ask us what management would cost for your unit — a real person answers.',
    tourEndSignIn: 'Continue to sign in',
    tourEndAsk: 'Ask us on WhatsApp',
    tourWaMessage: 'Hello, I have just watched the app tour. I would like to know more about property management and the owner portal for my property: ',
    tourAiKicker: 'Ask the Asset Desk AI',
    tourAiFab: 'Ask the Asset Desk AI',
    tourAiTitle: 'Questions? Asset Desk AI answers.',
    tourAiLede: 'Our AI assistant shows you how the app works, then answers your questions about property in Dubai, with real figures and honest risks.',
    tourAiGreeting: 'Hello. I am Asset Desk AI, your personal AI Wealth Co-Pilot from Asset Desk in Dubai. I can show you what the app does for owners, and then answer your questions about property in Dubai.\n\nWhere would you like to start?\n1. How the app works\n2. How is a property valued?\n3. I am considering buying in Dubai',
    tourAiPlaceholder: 'Type your question…',
    tourAiSend: 'Send',
    tourAiThinking: 'Asset Desk AI is thinking…',
    tourAiError: 'The AI could not answer just now. Please try again.',
    tourAiLimit: 'You have reached the limit of questions for now. Please try again later, or ask us on WhatsApp.',
    tourAiInterested: 'I am interested',
    tourAiNote: 'AI assistant: answers can contain errors and are not investment, tax or legal advice. Please do not enter sensitive data. If you share contact details, we use them only to contact you about your enquiry.',


    // ==================== PARTNER PORTAL ====================
    // Chrome
    pnPartner: 'Partner',
    pnProgramme: 'Programme',
    pnCooperation: 'Cooperation',
    pnReferral: 'Referral',
    pnSignOut: 'Sign out',

    // --- The pitch (PartnerPitch). Every claim maps to something the app
    //     actually does; see cooperationRoutes for the statement fields. ---
    pnPitchEyebrow: 'REFERRAL PARTNER PROGRAMME',
    pnPitchTitle: 'Know someone buying?',
    pnPitchTitleEm: 'Introduce them. Earn.',
    pnPitchSub: 'Become an Asset Desk Referral Partner. Sign one agreement, then introduce buyers straight from the app — name, email, phone number — and follow every introduction you make. No fee to join, and no property expertise required.',
    pnStatDevelopers: 'Developer partners',
    pnStatCost: 'Cost to join',
    pnStatSteps: 'Steps to start',
    pnBenefitsEyebrow: 'WHAT YOU GET',
    pnBenefitsTitle: 'Introduce in seconds, then watch it move',
    pnBenefitsLede: 'Everything you need to send us someone and know what happened next, without chasing anyone for an update.',
    pnBenefit1: 'Submit a referral from your phone in three fields — name, email and contact number.',
    pnBenefit2: 'Your introduction reaches our sales team instantly, recorded against your name.',
    pnBenefit3: 'A dashboard showing every person you referred and where each one stands.',
    pnBenefit4: 'We tell you straight away if a contact is already in our system, so you never work a dead lead.',
    pnBenefit5: 'Access to the Dubai inventory we sell — Emaar, Meraas, Nakheel, Omniyat, Sobha and more.',
    pnStepsEyebrow: 'HOW IT WORKS',
    pnStepsTitle: 'Four steps, no account needed',
    pnStep1Title: 'Apply in a minute',
    pnStep1: 'Your name, email and phone number. No account and no password needed to start.',
    pnStep2Title: 'Sign the agreement',
    pnStep2: 'We email you the partnership agreement between Asset Desk and you. Review it and sign it online — nothing to print.',
    pnStep3Title: 'Sign in with your email',
    pnStep3: 'The moment your agreement is signed your account is active. We email you a code each time — there is no password to remember.',
    pnStep4Title: 'Refer, and follow it',
    pnStep4: 'Submit an introduction in seconds and watch its progress in your dashboard.',
    pnClosingTitle: 'Ready to introduce your first buyer?',
    pnClosingLede: 'Applying is free and takes about a minute.',
    pnCtaApply: 'Become a referral partner',
    pnCtaContinue: 'Go to my application',

    // ---- Referral partner lifecycle (rf*) ----
    rfPartnerTitle: 'Referral Partner',
    rfNavRefer: 'Refer',
    rfNavReferrals: 'My Referrals',
    rfNoteTitle: 'WHAT YOU EARN',
    rfNote: 'Your commission is set out in the agreement we send you, and is agreed before you introduce anyone. We do not publish a rate here because it belongs in a signed contract, not a headline.',
    rfAlreadyPartner: 'I am already a partner',
    rfApplyEyebrow: 'REFERRAL PARTNER APPLICATION',
    rfApplyTitle: 'Apply to the programme',
    rfApplySub: 'Tell us who you are. We will email your agreement straight away.',
    rfApplyCta: 'Apply to become a partner',
    rfApplyFailed: 'We could not submit your application. Please try again.',
    rfName: 'Full name',
    rfPhone: 'Contact number',
    rfCompany: 'Company (optional)',
    rfEmailHint: 'Your agreement is sent here, and this is how you will sign in.',
    rfNeedNameEmail: 'Please enter your name and a valid email address.',
    rfSending: 'Sending\u2026',
    rfBack: 'Back',
    rfAppliedTitle: 'Application received',
    rfAppliedBody: 'Thank you. Our team is preparing your partnership agreement and will email it to you shortly.',
    rfContractSentTitle: 'Your agreement is on its way',
    rfContractSentBody: 'We have emailed you the partnership agreement between Asset Desk and you. Sign it online and your account becomes active immediately \u2014 no printing, no scanning.',
    rfContractSpamHint: 'Not arrived within a few minutes? Check your spam folder before requesting another copy.',
    rfAlreadySigned: 'I have signed \u2014 sign me in',
    rfSignInNote: 'Signed your agreement already? Use the same email address you applied with.',
    rfReferEyebrow: 'NEW REFERRAL',
    rfReferTitle: 'Refer someone',
    rfReferSub: 'Three details is all we need. Our team takes it from there.',
    rfReferCta: 'Submit referral',
    rfReferNeedAll: 'Please give a name, a valid email address and a contact number.',
    rfReferOk: 'Referral submitted. You can follow its progress in your dashboard.',
    rfReferFailed: 'We could not submit that referral. Please try again.',
    rfReferConsentNote: 'Only refer people who are happy to hear from us. We will contact them about property in Dubai, and they can opt out at any time.',
    rfDashEyebrow: 'YOUR REFERRALS',
    rfDashTitle: 'People you introduced',
    rfDashOne: 'referral',
    rfDashMany: 'referrals',
    rfDashEmptyTitle: 'No referrals yet',
    rfDashEmptyBody: 'When you submit your first introduction it will appear here, with its progress.',
    rfDashFailed: 'We could not load your referrals.',
    rfLoading: 'Loading\u2026',
    rfRefresh: 'Refresh',
    rfActivePartner: 'Active referral partner',
    rfDuplicateFlag: 'Already in our system',
    rfStatus_submitted: 'Submitted',
    rfStatus_contacted: 'Contacted',
    rfStatus_in_progress: 'In progress',
    rfStatus_closed: 'Closed',
    rfStatus_not_proceeding: 'Not proceeding',
    rfStatus_duplicate_claim: 'Under review',


    // --- Sign in ---
    pnAccessEyebrow: 'PARTNER ACCESS',
    pnAccessTitle: 'Partner Portal',
    pnLoginSubCode: 'Enter your email and we will send you a six-digit code. No password, and no account needed beforehand.',
    pnLoginSubPassword: 'Sign in with the Asset Desk account you already use for Property Owner.',
    pnYourName: 'Full name',
    pnNamePlaceholder: 'How we should address you',
    pnPassword: 'Password',
    pnSendCode: 'Send Code',
    pnSignIn: 'Sign In',
    pnUsePassword: 'I have a password',
    pnUseCode: 'Email me a code instead',
    pnSameAccountNote: 'This is the same account you use for Property Owner — signing in here signs you in there too.',
    pnConsentPre: 'I agree to the',
    pnConsentMid: ' and the ',
    pnConsentPost: '.',
    pnConsentRequired: 'Please accept the Terms and Privacy Policy to continue.',
    pnEnterEmail: 'Please enter your email address.',
    pnEnterCredentials: 'Please enter your email and password.',
    pnSignInFailed: 'Sign-in failed. Please check your details and try again.',
    pnCheckInbox: 'Check Your Inbox',
    pnCheckAuthenticator: 'Two-Factor Check',
    pnCodeSub: 'Enter the six-digit code we just emailed you. It expires in 10 minutes.',
    pn2faSub: 'Enter the six-digit code from your authenticator app.',
    pnCodeLabel: 'Verification code',
    pnVerifyContinue: 'Verify & Continue',
    pnCodeInvalid: 'That code was not accepted. Please try again.',
    pnEnterCode: 'Please enter the six-digit code.',
    pnBackToSignIn: 'Change email',
    pnResendCode: 'Resend code',
    pnCodeSentTo: 'Code sent to',
    pnBackToProgramme: 'Back to the programme',

    // ============ SERVICES SHOWCASE (pre-login "what we offer") ============
    scBack: 'Back',
    scEyebrow: 'WHAT WE OFFER',
    scHeroEyebrow: 'DUBAI PROPERTY INVESTMENT',
    scDevsTitle: 'Currently Available For Selected Developers',
    scDevsAlt: 'Emaar, Meraas, Nakheel, Omniyat, Select Group, Sobha, Beyond, Imtiaz, Samana and Avenew',
    scTitle: 'Our Services',
    scIntro: 'Vetted Dubai inventory, below-market deals, and one team from your first tax question to the day you sell.',
    scHeroTitleA: 'Everything,',
    scHeroTitleB: 'in order.',
    scClose: 'Close',
    scStatServices: 'Services',
    scStatStages: 'Stages',
    scStatTeam: 'Team',
    scStatUnits: 'Units for sale',
    scTabFractional: 'Antavio',
    scTabFractionalCap: 'Shares',

    // ---- Antavio: fractional ownership (waiting list, licence pending) ----
    // Copy follows antavio-c7405.web.app. Two things from that page are
    // deliberately NOT reproduced: the BaFin / MiFID II / "EU-regulated"
    // claims, which the source itself marks as unconfirmed placeholders, and
    // any wording that presents this as an available investment. The licence
    // is not in place, so this is an early-access list and nothing more.
    scFrEyebrow: 'TOKENIZED DUBAI REAL ESTATE · FOR GERMANY, AUSTRIA & SWITZERLAND',
    scFrTitleA: 'Dubai real estate.',
    scFrTitleB: 'Now from',
    scFrTitleC: 'AED 4,300.',
    scFrStakeCap: 'shares \u2014 your stake',
    scFrShareIllus: 'Illustrative \u2014 your holding is a fraction of the whole, not a promised amount.',
    scFrViewProject: 'View this project',
    scFrWhyS1: '35',
    scFrWhyS1Sub: 'years in the market',
    scFrWhyS2: '~20,000',
    scFrWhyS2Sub: 'investors',
    scFrWhyS3: '~\u20ac350M',
    scFrWhyS3Sub: 'placed every year',
    scFrWhyS4: '100%',
    scFrWhyS4Sub: 'digital \u00b7 German support',
    scFrTeamAlt: 'The Antavio team in Dubai',
    scFrLede: 'The German-speaking world\u2019s most trusted Dubai real estate firm will let you invest in prime Dubai property digitally, from AED 4,300 — and earn from the income it generates.',
    scFrOneProperty: 'ONE PROPERTY, MANY SHARES',

    scFrStat1: '35 years',
    scFrStat1Sub: 'in the Dubai market',
    scFrStat2: '~20,000',
    scFrStat2Sub: 'investors',
    scFrStat3: '~\u20ac350M',
    scFrStat3Sub: 'placed every year',

    scFrStat4: 'EU-regulated',
    scFrStat4Sub: 'security structure',
    scFrCrestCaption: 'TRANQUILITY ALONG THE SHORELINE',
    scFrAppEyebrow: 'THE ANTAVIO APP',
    scFrAppTitle: 'Track everything in one place',
    scFrAppWatch: 'Watch the intro',
    scFrApp1: 'Portfolio updates 24/7',
    scFrApp2: 'Secure Face ID login',
    scFrApp3: 'Portfolio management',
    scFrMediaNote: 'Video and property imagery are served from the Antavio project.',
    scFrNoticeTitle: 'Not open yet — join the early-access list',
    scFrNotice: 'Licensing and structuring are still being completed. Nothing on this page is an offer or an invitation to invest, and the figures shown are illustrative. Join the list and we will contact you the moment it opens.',

    scFrMissionEyebrow: 'OUR MISSION',
    scFrMission: 'To open one of the world\u2019s most dynamic property markets to everyone. For 35 years we have helped investors buy Dubai real estate — but only those who could afford a whole unit. Our mission is to remove that barrier, so any investor can put their money into prime Dubai property from AED 4,300, with the transparency, security and trust of an established brokerage behind them.',

    scFrConceptEyebrow: 'THE CONCEPT',
    scFrConceptTitle: 'A small share in something whole',
    scFrGridAria: 'Illustration: one share of twenty-four in a single property.',
    scFrHeroGridAria: 'Illustration: four shares of twenty-four in a single property.',
    scFrConcept: 'You start with a fraction — and share in the income and growth of the whole property. Your share is one of many; the rest is owned alongside you. Capital at risk; returns are not guaranteed.',

    scFrHowEyebrow: 'HOW IT WORKS',
    scFrHowTitle: 'Four simple steps',
    scFrHow1Cap: 'BROWSE',
    scFrHow1Title: 'Explore vetted properties',
    scFrHow1: 'Explore vetted Dubai properties, each with clear figures and projections.',
    scFrHow2Cap: 'INVEST',
    scFrHow2Title: 'Choose your amount',
    scFrHow2: 'Choose your amount, from AED 4,300, and complete a quick, secure sign-up.',
    scFrHow3Cap: 'EARN',
    scFrHow3Title: 'Receive distributions',
    scFrHow3: 'Receive your share of the property\u2019s income, paid out as distributions.',
    scFrHow4Cap: 'TRACK & EXIT',
    scFrHow4Title: 'Follow & sell',
    scFrHow4: 'Follow your investment in your dashboard, and sell on the resale marketplace when you wish.',
    scFrHowNote: 'Projected figures are illustrative only. Capital at risk; returns are not guaranteed.',

    scFrProjectEyebrow: 'OUR FIRST PROJECT',
    scFrProjectName: 'SAMANA Ocean Crest',
    scFrProjectLede: 'A waterfront tower of just 56 residences on Dubai Islands, by SAMANA Developers. The first property you will be able to invest in — from AED 4,300.',
    scFrProjectLoc: 'Dubai Islands, Dubai · ~20 min to Downtown, Airport & DIFC',
    scFrProjectTitle: 'A symphony by the sea',
    scFrProjectBody: 'Curved, layered architecture inspired by the movement of the sea — with private balcony pools, a rooftop infinity deck and a full wellness floor. Homes range from 1- to 4-bedroom in one of Dubai\u2019s most exciting new island communities.',
    scFrProjS1: '56', scFrProjS1Sub: 'Residences only',
    scFrProjS2: '~11.5%', scFrProjS2Sub: 'Target total p.a.',
    scFrProjS3: '6.5%', scFrProjS3Sub: 'Projected yield',
    scFrProjS4: 'AED 4,300', scFrProjS4Sub: 'Minimum from',
    scFrAmen1: 'Rooftop infinity pool',
    scFrAmen2: 'Private balcony pools',
    scFrAmen3: 'Indoor & outdoor gym',
    scFrAmen4: 'Steam & sauna',
    scFrAmen5: 'Jogging track',
    scFrAmen6: 'Kids\u2019 pool & splash pad',
    scFrProjectNote: 'Projected figures are targets, illustrative only. Capital at risk; returns are not guaranteed. You would invest in a digital security tied to the property\u2019s income — not direct ownership of an apartment.',

    scFrWhyEyebrow: 'WHY ANTAVIO',
    scFrWhyTitle: 'The #1 German-speaking agency in Dubai',
    scFrWhyLede: 'A family business and an established brokerage — on the ground in Dubai, sourcing, vetting and managing every property ourselves.',
    scFrWhy1: 'On the ground in Dubai — we source, vet and manage the properties ourselves.',
    scFrWhy2: 'German-language support and a fully digital experience.',
    scFrWhy3: 'Transparent, projected figures — never guaranteed returns.',

    scFrChooseEyebrow: 'WHY INVESTORS CHOOSE US',
    scFrChooseTitle: 'Everything stacked in your favour',
    scFrChoose1: 'Start from just AED 4,300',
    scFrChoose2: 'Income-generating Dubai real estate',
    scFrChoose3: '100% digital — no crypto needed',
    scFrChoose4: 'Curated, professionally vetted',
    scFrChoose5: 'A resale option for flexibility',
    scFrChoose6: 'German-speaking team, on the ground',

    scFrTrustEyebrow: 'TRUST & SECURITY',
    scFrTrustTitle: 'Trusted structure, end to end',
    scFrTrust1Title: '35-year brokerage',
    scFrTrust1: 'Properties sourced and managed by an established Dubai brokerage — on the ground, every day.',
    scFrTrust2Title: 'Transparent reporting',
    scFrTrust2: 'Verifiable documents and clear reporting at every step of your investment.',
    scFrTrust3Title: 'Structure in preparation',
    scFrTrust3: 'The regulated structure and its supervisory wording are being finalised with our issuing partner and legal counsel. We will publish the detail before anything opens.',

    scFrRisk: 'Capital at risk. Returns are not guaranteed. This is not investment advice. All figures shown are illustrative and do not constitute an offer or solicitation.',

    // ---- Early-access list form ----
    scFrFaqEyebrow: 'GOOD TO KNOW',
    scFrFaqTitle: 'Frequently asked questions',
    scFrQ1: 'Do I own the apartment?',
    scFrA1: "Not the physical apartment directly. You hold a digital security that gives you a share of the property's economic performance — the income it generates and any gain when it's sold. It's a modern, regulated way to invest in real estate without the cost, paperwork, and responsibilities of owning a whole unit yourself. A dedicated company holds the property, and your security represents your stake in it.",
    scFrQ2: 'How much do I need to start?',
    scFrA2: "From AED 4,300. That's the whole idea — instead of needing several hundred thousand euros to buy a Dubai apartment outright, you can invest a small amount into a prime property and still share in its returns. You decide how much, starting at AED 4,300.",
    scFrQ3: 'How do I receive my returns?',
    scFrA3: "Through regular distributions paid to your account. For an off-plan property like our pilot, you earn a projected developer-backed return during the construction phase; once the building is completed and handed over, your return comes from rental income and any increase in the property's value. All returns are projected, paid net of fees, and are not guaranteed.",
    scFrQ4: 'Do I need to understand crypto?',
    scFrA4: "No. Although the investment uses secure digital-security technology in the background, you never need to touch crypto, manage a wallet, or learn anything technical. You invest, track, and receive distributions through a simple app — the same way you'd use any online banking or investment platform.",
    scFrQ5: 'Can I sell my investment?',
    scFrA5: "Investments like this are designed to be held for the medium to long term. Where a resale option is available, you will be able to offer your holding to other investors through our platform, subject to a buyer being available — so liquidity is not guaranteed. We will always be clear in each offering about the expected holding period and any exit options before you invest.",
    scFrQ6: 'Is my money at risk?',
    scFrA6: 'Yes — as with any real estate or investment product, your capital is at risk and returns are not guaranteed. The value of your investment can go down as well as up, returns may be lower than projected, and in the worst case you could lose some or all of your investment. We set out the specific risks for each property clearly before you invest, so you can make an informed decision.',
    scFrJoin: 'Join the early-access list',
    scFrCtaEyebrow: 'BE FIRST IN LINE',
    scFrCtaTitle: 'Early access to our first Dubai properties',
    scFrCtaBody: 'We are opening access to a limited group of investors. Join the early-access list and we will notify you the moment our first Dubai properties go live.',
    scFrStep: 'Question {n} of {total}',
    scFrLastStep: 'Last step',
    scFrBack: 'Back',
    scFrQCountry: 'Which market interests you most?',
    scFrQCountryO1: 'United Arab Emirates (Dubai)',
    scFrQCountryO2: 'Germany',
    scFrQCountryO3: 'Spain',
    scFrQCountryO4: 'No preference',
    scFrQInterest: 'What matters most to you?',
    scFrQInterestO1: 'Capital appreciation',
    scFrQInterestO2: 'Distributions from rental income',
    scFrQInterestO3: 'A regulated structure',
    scFrQInterestO4: 'All of the above',
    scFrQAmount: 'Roughly how much would you consider?',
    scFrQAmountO1: '\u20ac1,000 – \u20ac10,000',
    scFrQAmountO2: '\u20ac10,000 – \u20ac100,000',
    scFrQAmountO3: '\u20ac100,000+',
    scFrQAmountO4: 'Not sure yet',
    scFrQTiming: 'When would you want to start?',
    scFrQTimingO1: 'As soon as it opens',
    scFrQTimingO2: '1–3 months',
    scFrQTimingO3: '3–6 months',
    scFrQTimingO4: 'Just exploring',
    scFrNote: 'Anything you would like us to know? (optional)',
    scFrConsent: 'You may contact me about Antavio fractional ownership. I can withdraw this at any time.',
    scFrSubmit: 'Join the early-access list',
    scFrSubmitting: 'Sending…',
    scFrDoneTitle: 'You are on the list',
    scFrDone: 'Thank you — we will be in touch as soon as our first Dubai properties go live.',
    scFrRequired: 'Please enter your name, a valid email address, and tick the consent box.',
    scFrError: 'That did not go through. Please try again, or reach us on WhatsApp.',
    scViewAllShort: 'Open',
    scHomeSoon: 'soon',
    scHomeFilmEyebrow: 'THE FILM',
    scHomeFilmTitle: 'Six moments, start to finish',
    scHomeFilmLede: 'Fifteen seconds: first light over the city, the structure, the keys, a home that earns, a place for the family, and the years after.',
    scHomeBeat1: 'Dubai, at first light',
    scHomeBeat2: 'The structure, settled first',
    scHomeBeat3: 'The keys, in your hand',
    scHomeBeat4: 'A home that earns',
    scHomeBeat5: 'And a place for the family',
    scHomeBeat6: 'For the years after',
    scHomeFilmPlay: 'Play the film',
    scLanguage: 'Language',
    scTabHome: 'Home',
    scTabHomeCap: 'Overview',
    scHomeEyebrow: 'START TO FINISH',
    scHomeTitle: 'Your Dubai investment, handled end to end',
    scHomeLede: 'Buy the unit, take the handover, let it earn on a long lease or through short stays, set up the company, secure the Golden Visa \u2014 and when you sell, we handle that too. One team, one point of contact, every step in-house.',
    scHomeCall: 'Book a call',
    scHomeSee: 'See how it works',
    scHomeProof1Sub: 'services in-house',
    scHomeProof2Sub: 'stages, start to finish',
    scHomeProof3: 'One',
    scHomeProof3Sub: 'point of contact',
    scHomeChainEyebrow: 'THE WHOLE JOURNEY',
    scHomeChainTitle: 'Nothing changes hands halfway',
    scHomeChainLede: 'Most people buy in Dubai through one firm, then go hunting for another to carry out the snagging inspection, another to let it, another to open the company, another for the residency. Things get lost at every handover. We do all of it.',
    scHomeStageServices: '{n} services',
    scHomeExplore: 'Explore all services',
    scHomeWhoEyebrow: 'WHO WE ARE',
    scHomeWhoTitle: 'Built by investors. For investors.',
    scHomeWhoLede: 'The people who vet these properties have their own capital in them. We do not just run the platform \u2014 we buy through it ourselves.',
    scHomeWhoPoint1: 'Principals invest alongside clients',
    scHomeWhoPoint2: 'Vetted listings only',
    scHomeWhoPoint3: 'On the ground in Dubai',
    scHomeWhoCta: 'Talk to one of us',
    scHomeWhoNote: 'Members of the team may hold or invest in properties listed on the platform.',
    scHomeRoleChairman: 'Chairman',
    scHomeRoleFounder: 'Co-Founder',
    // How a developer gets into the app. Deliberately attributed to the
    // investment committee rather than to a named person: nobody has put
    // their own name on this process, and a borrowed credential would be
    // worse than none. Every claim here is one we can actually stand behind,
    // and the verdict line refuses the "vetted therefore safe" reading.
    scGateEyebrow: 'HOW WE CHOOSE',
    scGateTitle: 'Not every developer gets into this app.',
    scGateLede: 'Dubai has hundreds of developers and thousands of launches. Our investment committee puts every one of them through the same four checks, in the same order, before a single unit reaches this app. The list is short on purpose.',
    scGate1Title: 'The track record',
    scGate1: 'What has actually been handed over, and when. Announced towers do not count — delivered ones do. We look at how late the last completions ran, and at what the building looked like on the day the keys changed hands.',
    scGate2Title: 'The balance sheet',
    scGate2: 'Who is funding the current phase, whether the escrow account is run the way the law says it should be, and what happens to the project if sales slow down.',
    scGate3Title: 'The build',
    scGate3: 'Somebody from our Dubai team stands inside a finished unit by the same developer. The render in the brochure and the delivered finish are two different products, and only one of them is for sale.',
    scGate4Title: 'The numbers',
    scGate4: 'Price against comparable registered transactions, service charge per square foot, and how readily a unit like it has resold. Where the exit is thin, the entry price has to carry it.',
    scGateVerdict: 'A developer can pass three of these and fail the fourth. That is still a no. And being on the list is not advice to buy — it means we would not have put the unit in front of you otherwise. The decision, and the risk, stay yours.',
    scGateStat1Sub: 'developers on the list',
    scGateStat2Sub: 'units listed right now',
    scGateCta: 'See what made the list',
    scGateAsk: 'Ask about a developer',
    scGateNote: 'These checks describe how we select. They are not a guarantee of completion, yield or resale value — no assessment can be. Your own due diligence still applies.',
    // ===== Property management tab =====================================
    // The five stages reuse the pmStep* keys above rather than restating
    // them, so the pitch and the screen an owner is handed stay in step.
    // (No straight apostrophes in comments here: check-i18n-parity.js reads
    // one as an opening quote and mis-pairs the rest of the file.)
    scTabPm: 'Property',
    scTabPmCap: 'Management',
    scPmEyebrow: 'PROPERTY MANAGEMENT',
    scPmTitle: 'Buying takes an afternoon. Owning takes ten years.',
    scPmLede: 'A tenant has to be found, an Ejari registered, cheques banked, a service charge queried, a cistern fixed at an hour that suits nobody. We take that on from the day the building is handed over — and everything we do lands on a screen you can open whenever you want.',
    scPmGetEyebrow: 'WHAT YOU GET',
    scPmGetTitle: 'Everything a landlord would otherwise do themselves.',
    scPmGetLede: 'Not a letting agent who disappears once the tenant signs. The list below is the whole job, and all of it is already running behind the screen on this page.',
    scPmGrp1Title: 'Getting it earning',
    scPmB1Title: 'Tenant placement',
    scPmB1: 'We market the unit, screen and reference the tenant, and register the Ejari in your name.',
    scPmB2Title: 'Rent collection',
    scPmB2: 'Cheques banked to schedule, with what has landed and what is still outstanding shown per property.',
    scPmB3Title: 'Renewals and rent reviews',
    scPmB3: 'Every lease flagged ninety days out, with what the unit should now be asking rather than a rollover at last year’s figure.',
    scPmB4Title: 'Tenant retention',
    scPmB4: 'Who renewed, who left and why — so the next lease is priced on evidence instead of hope.',
    scPmGrp2Title: 'Keeping it right',
    scPmB5Title: 'Snagging and handover',
    scPmB5: 'Defects logged against the developer before you accept the keys, then DEWA activated and the unit made ready.',
    scPmB6Title: 'Inspections',
    scPmB6: 'Move-in, move-out and periodic checks, each one a report you can open rather than a phone call you have to trust.',
    scPmB7Title: 'Maintenance, in-house',
    scPmB7: 'Our own team, not a broker’s subcontractor. Every job quoted before anyone touches anything.',
    scPmB8Title: 'Asset inventory',
    scPmB8: 'Every item of furniture and every appliance, room by room, with its condition — so a check-out is settled against a list, not a memory.',
    scPmGrp3Title: 'Leaving you alone',
    scPmB9Title: 'One screen, any hour',
    scPmB9: 'Income, occupancy, documents and costs in one place, from wherever you happen to be.',
    scPmB10Title: 'Nothing spent without you',
    scPmB10: 'Tenant requests go to our team, not to your phone. You are asked only when a decision or a cost needs approving.',
    scPmB11Title: 'Documents where they belong',
    scPmB11: 'Ejari, tenancy contract, title deed and Emirates ID filed against the property itself — not in somebody’s inbox.',
    scPmB12Title: 'Net that means net',
    scPmB12: 'Service charges, DEWA and maintenance totalled against the rent, so the figure you read is the figure you keep.',
    scPmScreenEyebrow: 'WHAT YOU SEE',
    scPmScreenTitle: 'Your property, on one screen.',
    scPmScreenLede: 'Not a PDF once a month. These are the panels your management team works from, open to you at any hour, filled in from their records as the year runs.',
    scPmScreen1Title: 'Rent, tracked',
    scPmScreen1: 'Rent collected, payments still due, and net per year for each property rather than one lump figure for the portfolio.',
    scPmScreen2Title: 'Occupancy and yield',
    scPmScreen2: 'Which units are earning, which are sitting empty, and the gross yield across everything you hold.',
    scPmScreen3Title: 'Lease renewals',
    scPmScreen3: 'Every end date in one list, with anything inside ninety days pulled to the front — so a renewal is a decision, not a surprise.',
    scPmScreen4Title: 'Documents',
    scPmScreen4: 'Ejari, tenancy contracts and Emirates ID copies filed against the property they belong to, not sitting in somebody’s inbox.',
    scPmScreen5Title: 'Maintenance',
    scPmScreen5: 'Jobs raised, quoted, and held for your approval before anyone spends your money on them.',
    scPmScreen6Title: 'Inventory',
    scPmScreen6: 'What is in each room, room by room, so a departing tenant is checked against a list instead of somebody’s memory.',
    scPmNotice: 'Service charges, DEWA, Ejari registration and government fees stay the owner’s own costs — they appear on the expenses screen rather than being quietly absorbed. What the management itself costs depends on the property, and is agreed with you before any work starts.',
    scPmCloseEyebrow: 'TALK TO THE TEAM',
    scPmCloseTitle: 'Ask about your property.',
    scPmCloseLede: 'Property management has its own line, answered by the people who actually run the buildings rather than the sales desk. Tell us the tower and the unit and you will get a straight answer about what it needs.',
    scPmWhats: 'Message us on WhatsApp',
    scPmNumberLabel: 'Property management line',
    scPmKeysAlt: 'Brass keys and a tied bundle of documents on a dark stone console, lit by low evening light.',
    scPmStagesLabel: 'The five stages of management',
    scPmDeskSnagAlt: 'The snagging screen: twelve defects found, ten resolved, two outstanding, the developer notified, and a table of defects by room with photographs and status.',
    scPmDeskSnagCap: 'Stage I — the snag list, logged against the developer',
    scPmDeskHandAlt: 'The handover screen: a five-point checklist all complete, and the property record showing DEWA premise, Makani number, plot number and handover date.',
    scPmDeskHandCap: 'Stage II — keys, meters and the property record',
    scPmDeskTenAlt: 'The tenant placement screen: a pipeline from listed to Ejari registered, a screening checklist, and the tenancy showing Ejari number, dates, annual rent and cheques.',
    scPmDeskTenCap: 'Stage III — screening, then the Ejari',
    scPmDeskConAlt: 'The documents screen: tenancy contract, Ejari certificate, title deed, tenant Emirates ID and cheque schedule, each with a date, size and a view button.',
    scPmDeskConCap: 'Stage IV — every document, filed against the property',
    scPmDeskAlt: 'The owner dashboard on a desktop monitor: portfolio value, rent collected, occupancy and gross yield; a bar chart of rental income by month; an expense breakdown totalling to net income; and a table of properties with tenant, lease end date, rent and status.',
    scPmDeskCap: 'Stage V — rent, costs and net, month by month',
    scPmShotDashCap: 'Your dashboard',
    scPmShotInsCap: 'Inspection reports',
    scPmShotExpCap: 'Expenses and net',
    scPmShotInsAlt: 'The inspection screen: a move-in report flagged for attention and a periodic check marked pass, each openable, with photographs and a note that maintenance is handled by the team.',
    scPmShotExpAlt: 'The expenses screen: net income for the year, with service charge, maintenance, DEWA and the management fee itemised against the rent collected.',
    scPmShotAlt: 'The Property Management screen: rent collected and occupancy, the property with its tenancy state, a lease renewal and a maintenance quote waiting for approval.',
    scPmShotNote: 'An illustration of the owner screen, drawn rather than captured — a real screenshot would carry a real owner’s name, unit and rent. The panels are the ones on that screen; the figures are not anybody’s.',
    scHomeVoicesEyebrow: 'IN THEIR WORDS',
    scHomeVoicesNote: 'Videos play muted \u2014 turn the sound on to listen.',
    scHomeInsideEyebrow: 'WHERE TO START',
    scHomeInsideTitle: 'Six ways in',
    scHomeInsideUnits: 'Live inventory from our own listings, with the discount to market shown on every unit.',
    scHomeInsideFractional: 'Own a fraction of a vetted Dubai property from AED 4,300 through a digital security.',
    scHomeInsideStays: 'Stay in one of our own apartments before you buy \u2014 pick your dates and book directly.',
    scHomeInsidePm: 'Hand the running of a property to us — tenant, Ejari, rent, inspections and repairs, all on one screen.',
    scHomeInsideJourney: 'Come and see it for yourself: a guided investor trip through the market, the sites and the paperwork.',
    scHomeInsideServices: 'The full catalogue \u2014 company formation, escrow, snagging, letting, tax, residency and succession.',
    scHomeRouteHint: 'Tap a stage to jump to it',
    scHomeStageAria: 'Stage {n} of 6',
    scHomeFamEyebrow: 'MORE THAN AN INVESTMENT',
    scHomeFamTitle: 'A place your family can actually live',
    scHomeFamLede: 'Plenty of our owners start with a unit and end up spending real time here. Dubai is built for that \u2014 short school runs, streets that stay comfortable after dark, beaches and parks minutes from the tower you bought into, and sun for most of the year.',
    scHomeFam1Title: 'Schools you already recognise',
    scHomeFam1: 'German, British, American and IB curricula are all taught across the city, so a move does not mean starting an education from scratch.',
    scHomeFam1Alt: 'A school campus courtyard in Dubai in morning light',
    scHomeFam2Title: 'Everyday life, outdoors',
    scHomeFam2: 'Community parks are kept immaculate, promenades are walkable, and an evening out with children needs no planning.',
    scHomeFam2Alt: 'A community park and playground at golden hour',
    scHomeFam3Title: 'Residency for everyone',
    scHomeFam3: 'A property-based Golden Visa can extend to your spouse and children. We handle the application, the medical and the Emirates ID.',
    scHomeFam3Alt: 'The Dubai Marina promenade at blue hour',
    scHomeFamCta: 'Come and see it for yourself',
    scHomeFamHeroAlt: 'A family walking a Dubai beach promenade at sunset, the skyline beyond',
    scHomeFamNote: 'Golden Visa eligibility depends on the property and on current UAE rules. We confirm what applies to you before anything is filed.',
    scHomeCloseEyebrow: 'THE NEXT STEP',
    scHomeCloseTitle: 'Tell us where you are, and we take it from there',
    scHomeCloseLede: 'Whether you already own in Dubai or are only weighing it up, a short call is the quickest way to see what actually applies to you.',
    scHomeWhats: 'Message on WhatsApp',
    scHomeHeroAlt: 'The Dubai skyline at dusk from a private terrace',
    scTabUnits: 'Units',
    scTabUnitsCap: 'Buy',
    scTabStaysCap: 'Let',
    scTabJourneyCap: 'Process',
    scTabServicesCap: 'In-house',
    scViewAll: 'View all {n} units',
    scTabStays: 'Stays',
    scTabJourney: 'Journey',
    scTabServices: 'Services',

    // ---- Short stays (Airbnb arm, currently in formation) ----
    scStaysEyebrow: 'BOOK A STAY',
    scStaysTitle: 'Short stays, beachfront',
    scStaysLede: 'Furnished apartments in Dubai, booked directly with us.',
    scStaysStat1: 'Apartments',
    scStaysStat2: 'Expected net yield',
    scStaysStat3: 'To the sea',
    scStaysStat3Val: '1st row',
    scStaysPoint1: 'Fully furnished to short-let standard, the specification that earns the highest yields.',
    scStaysPoint2: 'Ten minutes from Dubai Island, directly on the beachfront.',
    scStaysPoint3: 'Run by the Asset Desk Airbnb company, being established under our own umbrella.',
    scStaysPoint4: 'Furnished short lets achieve 5–12% higher rates than long-term tenancies.',
    scStaysNoteTitle: 'Not bookable yet',
    scStaysNote: 'The apartments are still being furnished and the booking system is not live. Tell us your dates and we will come back to you as soon as they open.',
    scStaysCta: 'Ask about a stay',

    // ---- Trust markers + lead capture ----
    scHeroCtaUnits: 'Browse units',

    // ---- Marketing band. Every claim maps to real data: the discount is
    // computed live from the listings, 29 is the catalogue count, and the
    // yield figures come from the advisory knowledge base. ----
    scValueEyebrow: 'WHY INVESTORS COME TO US',
    scValueTitle: 'Not a listings board.',
    scValueLede: 'Anyone can show you what is on the market. We show you what is worth owning — and then we run it.',
    scValue1Title: 'Below market, not just on market',
    scValue1: 'Units priced under independent valuation. The sharpest on the list today is {n}% under market.',
    scValue1Fallback: 'Units priced under independent valuation, sourced before they reach the open market.',
    scValue2Title: 'We invest alongside you',
    scValue2: 'Principals hold their own capital in Dubai property and transact through this same platform.',
    scValue3Title: 'One team, the whole arc',
    scValue3: 'Company formation, escrow, snagging, letting, tax and resale — {n} services, no hand-offs.',
    scValue4Title: 'Furnished lets earn more',
    scValue4: 'Short lets run 5–12% above long-term tenancies, with about 11.3% expected net in the first year.',

    // ---- Journey detail ----
    scJourney1: 'Site visits with the developers, not a sales office tour.',
    scJourney2: 'Market briefings on pricing, yields and where the value sits.',
    scJourney3: 'Time on the ground with the team who will handle your purchase.',
    scJourney4: 'And Dubai itself — the landmarks, the districts and the places you would actually live.',
    scJourneyFilmEyebrow: 'FROM THE LAST TRIP',
    scJourneyFilmTitle: 'See how it goes',
    scJourneyFilmNote: 'Starts muted — turn the sound on.',
    scTrust1: 'Principals invest alongside clients',
    scTrust2: 'Vetted listings only',
    scTrust3: 'Below-market deals sourced off-market',
    scLeadTitle: 'Request the full details',
    scLeadSub: 'Floor plan, payment schedule, service charges and a viewing slot — sent by our team, usually the same day.',
    scLeadName: 'Full name',
    scLeadEmail: 'Email address',
    scLeadPhone: 'Phone (optional)',
    scLeadMsg: 'Anything we should know? (optional)',
    scLeadSend: 'Request details',
    scLeadSending: 'Sending…',
    scLeadDone: 'Thank you — your request is with our team. We will come back to you with the full details on this unit.',
    scLeadErr: 'That did not go through. Please try again, or reach us on WhatsApp.',
    scLeadRequired: 'Please enter your name and a valid email address.',
    scLeadLegal: 'We use your details only to answer this enquiry. See the Privacy Policy on the welcome screen.',
    scStaysOpen: 'Now bookable',
    scStaysBook: 'Check dates & book',
    scStaysBookedVia: 'Booking handled on our reservation system',
    scStayA: 'Studio Aurora',
    scStayASub: '7 minutes to Dubai Mall',
    scStayB: 'Residence Aurelia',
    scStayBSub: 'Rashid Yachts & Marina, ocean view',
    scStaysMoreTitle: 'More apartments on the way',
    scStaysMore: 'Six more of our own apartments, ten minutes from Dubai Island and first row from the sea, are being furnished now and join the list as they open.',
    scImageImpression: 'Location image is an artist’s impression — photography of the apartments follows once they are furnished.',
    scFilterAll: 'All',
    scFilterDeals: 'Below market',
    scFilterStudio: 'Studio',
    scFilter1: '1 bed',
    scFilter2: '2 beds',
    scFilter3: '3+ beds',
    scShowMore: 'Show more',
    scNoMatch: 'No units match that filter yet.',
    scUnitCount: '{n} units',
    scExplore: 'Explore',
    scDocument: 'document',
    scDocuments: 'documents',

    // ---- Units for sale (live Dubai Sell inventory) ----
    scForSaleEyebrow: 'AVAILABLE NOW',
    scForSaleTitle: 'Units for sale',
    scForSaleLede: 'Live inventory in Dubai — every unit listed with us, priced and ready to view.',
    scBed: 'bed',
    scBeds: 'beds',
    scStudio: 'Studio',
    scSqft: 'sqft',
    scBelowMarket: 'below market',
    scMarketValue: 'Market value',
    scPriceOnRequest: 'Price on request',
    scUntitledUnit: 'Dubai unit',
    scShowingUnits: 'Showing {shown} of {total} units',
    scUnitsError: "We couldn't load the listings just now.",
    scUnitsEmpty: 'No units are listed at the moment. Talk to us about what you are looking for.',
    scRetry: 'Try again',

    // ---- Dubai Journey ----
    scJourneyEyebrow: 'COME AND SEE FOR YOURSELF',
    scJourneyTitle: 'The Dubai Journey',
    scJourneyLede: 'A few days in Dubai, run end to end by us — in front of the developers, inside the units, and out across the city itself. You travel with other investors and with the team who will handle your purchase.',
    scJourneyCta: 'Book your seat',
    scOpenInBrowser: 'Open in browser',

    // ---- Services section heading ----
    scServicesEyebrow: 'EVERYTHING AROUND THE PURCHASE',
    scServicesTitle: 'How we work with you',
    scServicesLede: 'Six stages, in the order you live them — from your first tax question to the day you sell.',

    // The six stages, in the order an investor lives them.
    scCh1Title: 'Before you buy',
    scCh1Lede: 'Structure and funding settled first, so the purchase itself is the simple part.',
    scCh2Title: 'The purchase',
    scCh2Lede: 'We carry the paperwork and the deadlines, so nothing stalls at the Land Department.',
    scCh3Title: 'Handover',
    scCh3Lede: 'Every defect found and put right before you accept the keys.',
    scCh4Title: 'Owning & earning',
    scCh4Lede: 'Your unit let, maintained and correctly taxed while you are thousands of kilometres away.',
    scCh5Title: 'Living in Dubai',
    scCh5Lede: 'The residency, the protection and the paperwork that turn an investment into a place to be.',
    scCh6Title: 'When you sell',
    scCh6Lede: 'Exit or hand it on — valued, marketed and transferred.',

    scComingSoon: 'SOON',
    scDraftCopy: 'DRAFT',
    scWhatWeNeed: "What we'll need from you",
    scAskOnWhatsApp: 'Ask on WhatsApp',
    scBookCall: 'Book a call',
    scHelpTitle: 'Not sure where to start?',
    scHelpSub: 'Tell us what you own or what you are planning, and we will point you to the right service.',

    // ============ ASSET DESK DEMO (login-free product tour) ============
    dmEyebrow: 'PRODUCT TOUR',
    dmTitle: 'Asset Desk',
    dmSub: 'What an owner sees for every unit they hold',
    dmBannerTitle: 'Sample data',
    dmBannerText: 'These two units are examples, not real properties. The figures are a snapshot from August 2026 — a live account values your own units against current DLD transactions and Ejari contracts.',
    dmHowToHint: 'Open the Valuation tab and tap a property for its full valuation report.',
    dmSampleInvestor: 'Sample Investor',

    // ---- Code gate ----
    dmGateEyebrow: 'GUEST ACCESS',
    dmGateTitle: 'Product Demo',
    dmGateSub: 'Enter your access code to explore every view with sample data',
    dmGateLabel: 'Access code',
    dmGateButton: 'Continue',
    dmGateWrong: 'That code is not right. Ask your Asset Desk contact for the current one.',
    dmGateFoot: 'Sample data only — no real investor, tenant or property is shown',

    // ---- View picker ----
    dmHubEyebrow: 'SAMPLE DATA',
    dmHubTitle: 'Choose a view',
    dmHubSub: 'See the product from every side',
    dmHubNotice: 'Every screen behind this point runs on sample data, and the app cannot reach live data while you are in demo mode. Nothing you tap here changes anything real.',
    dmHubOwner: 'Property Owner',
    dmHubOwnerSub: 'The full Asset Desk — portfolio, valuation, documents, payments',
    dmHubTenant: 'Tenant',
    dmHubTenantSub: 'Rent schedule, contract, maintenance requests',
    dmHubCeo: 'CEO',
    dmHubCeoSub: 'Company performance, receivables, VAT and compliance',
    dmHubGuest: 'Short Stay Guest',
    dmHubGuestSub: 'Short-stay booking for Asset Desk homes',
    dmAccountBannerText: 'You are signed in to a sample account. The properties shown are examples, not real holdings — use it to explore how the app works. Uploads, requests and notifications are switched off.',

    // ==================== PORTAL CHROME ====================
    pcRefresh: 'Refresh',
    pcChangeRole: 'Change role',

    // ==================== OWNER PM PREVIEW ====================
    opmTagline: 'Everything about your property, in one place.',
  },

  de: {
    // ==================== IMMOBILIENVERWALTUNG (EIGENTÜMER) ====================
    pmRented: 'Vermietet',
    // Lebenszyklus-Schritte
    pmStepSnagging: 'Mängelabnahme',
    pmStepSnaggingDesc: 'Mängelprüfung und Behebung.',
    pmStepHandover: 'Übergabe',
    pmStepHandoverDesc: 'Schlüssel erhalten, DEWA aktiviert.',
    pmStepPlacement: 'Mietervermittlung',
    pmStepPlacementDesc: 'Mieter gefunden und Ejari registriert.',
    pmStepContracts: 'Verträge',
    pmStepContractsDesc: 'Mietvertrag unterzeichnet.',
    pmStepRental: 'Mietverfolgung',
    pmStepRentalDesc: 'Scheckplan und Auszahlungen.',
    pmInProgressBadge: 'IN BEARBEITUNG',
    // Tabs & Rahmen
    pmOverview: 'Überblick',
    pmInspection: 'Inspektion',
    pmMaintenance: 'Instandhaltung',
    pmInventory: 'Inventar',
    pmRefresh: 'Aktualisieren',
    pmRefreshing: 'Wird aktualisiert…',
    pmLoadFailedTitle: 'Ihre Immobilien konnten nicht geladen werden',
    pmLoadFailedBody: 'Zeitüberschreitung der Verbindung. Ihr Portfolio ist sicher — nur die Verbindung zum Server ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
    pmSampleNote: 'Beispieldaten zur Vorschau — diese werden aus den Unterlagen Ihres Verwaltungsteams gefüllt.',
    // Dashboard
    pmNoPropsText: 'Sobald eine Immobilie in Verwaltung ist, erscheint sie hier mit ihren Einnahmen und Ausgaben.',
    pmTotalRentalIncome: 'Gesamte Mieteinnahmen',
    pmOf: 'von',
    pmRentedLower: 'vermietet',
    pmStatusActive: 'Aktiv',
    pmStatusRented: 'Vermietet',
    pmStatusSelling: 'Im Verkauf',
    pmStatusSold: 'Verkauft',
    pmNoneInStatus: 'Noch nichts hier',
    pmNoInspections: 'Noch keine Inspektionen',
    pmNoInspectionsText: 'Einzugs-, Zwischen- und Auszugsinspektionen erscheinen hier, sobald sie durchgefuehrt wurden.',
    pmNoMaintenance: 'Keine Wartung erfasst',
    pmNoMaintenanceText: 'Reparaturen und Einsaetze fuer diese Einheit erscheinen hier, mit den jeweiligen Kosten.',

    // Instandhaltungsuebersicht ueber das gesamte Portfolio. {n} wird im Code ersetzt.
    pmBoardTitle: 'Instandhaltungsanfragen',
    pmBoardOpenSub: '{n} von {total} noch abzuschliessen',
    pmBoardClearSub: 'Nichts offen — alle Anfragen sind abgeschlossen.',
    pmBoardEmptyText: 'Anfragen, die Sie oder Ihre Mieter zu einer Immobilie stellen, erscheinen hier — vom Tag der Meldung bis zum Abschluss.',
    pmBoardNoneInFilter: 'Keine Anfragen mit diesem Status.',
    pmProperty: 'Immobilie',
    pmPaid: 'Bezahlt',
    pmOutstanding: 'Offen',
    pmNoExpensesYet: 'Fuer diese Immobilie ist noch nichts erfasst. Servicegebuehren werden Ihnen direkt berechnet und hier oft nicht gebucht.',
    pmNoInventory: 'Kein Inventar erfasst',
    pmNoInventoryText: 'Sobald Moebel und Geraete dieser Einheit erfasst sind, erscheinen sie hier mit ihrem Zustand.',
    pmNoSampleUnit: 'Kein Beispiel-Datensatz fuer diese Immobilie',
    pmNoSampleUnitText: 'Die Demo enthaelt vollstaendige Verwaltungsdaten nur fuer einige Immobilien. Oeffnen Sie eine davon, um Mietverhaeltnis, Inspektionen, Wartung und Dokumente zu sehen.',
    pmSubmitDocs: 'Dokumente einreichen',
    pmSubmitDocsSub: 'Senden Sie uns Ihre Dateien',
    pmMonthsLeft: 'Monate verbleibend',
    pmOrEmailDocs: 'oder senden Sie Ihre Dokumente an',
    pmEmailDocs: 'Senden Sie Ihre Dokumente an',
    pmSmartUpload: 'KI Smart Upload',
    pmSmartUploading: 'Wird verarbeitet...',
    pmNoneInStatusText: 'Derzeit hat keine Immobilie diesen Status.',
    pmOccupiedSuffix: 'belegt',
    pmRentPerYear: 'Miete / Jahr',
    pmNetPerYear: 'Netto / Jahr',
    pmRentCollected: 'Eingegangene Miete',
    pmPaymentsDue: 'Offene Zahlungen',
    pmLeaseRenewals: 'Mietvertragsverlängerungen',
    pmDueSuffix: 'fällig',
    pmAllTenanciesCurrent: 'Alle Mietverhältnisse sind aktuell — keines läuft in den nächsten 3 Monaten aus.',
    pmExpiredPre: 'Vor',
    pmDayShort: 'T',
    pmAgo: 'abgelaufen',
    pmExpiresToday: 'Läuft heute ab',
    pmDay: 'Tag',
    pmDays: 'Tage',
    pmLeft: 'übrig',
    pmEnds: 'endet',
    pmRentalIncomeByProperty: 'Mieteinnahmen je Immobilie',
    pmNoEarning: 'Noch keine Immobilie erzielt Mieteinnahmen — Einnahmen erscheinen hier, sobald ein Mietvertrag aktiv ist.',
    pmOtherProperty: 'weitere Immobilie',
    pmOtherProperties: 'weitere Immobilien',
    pmNotYetEarning: 'ohne Mieteinnahmen',
    // --- Owner PM — Portfoliozahlen aus echten Objektdaten ---
    pmPortfolioValue: 'Portfoliowert',
    pmGrossYield: 'Bruttorendite',
    pmYield: 'Rendite',
    pmDueNext90: 'Fällig in 90 Tagen',
    pmAcrossN: 'aus',
    // --- Owner PM — Hinweise "Mehr aus dem Portfolio machen" ---
    pmGrowTitle: 'Mehr aus Ihrem Portfolio machen',
    pmGrowSub: 'Nur auf Basis dessen, was Ihr Portfolio heute zeigt.',
    pmGrowIdle: 'Immobilien erzielen noch keine Mieteinnahmen',
    pmGrowIdle1: 'Immobilie erzielt noch keine Mieteinnahmen',
    pmGrowIdleCta: 'Vermietung anfragen',
    pmGrowDocs: 'vermietete Immobilien haben keinen Mietvertrag hinterlegt',
    pmGrowDocs1: 'vermietete Immobilie hat keinen Mietvertrag hinterlegt',
    pmGrowDocsCta: 'Mietvertrag senden',
    pmGrowRenew: 'Mietverträge enden in 90 Tagen',
    pmGrowRenew1: 'Mietvertrag endet in 90 Tagen',
    pmGrowRenewCta: 'Verlängerung anfragen',
    pmGrowAllGood: 'Jede Immobilie ist vermietet, dokumentiert und hat einen laufenden Mietvertrag. Derzeit ist nichts zu tun.',
    // --- Owner PM — Angebot Immobilienverwaltung ---
    pmOfferTitle: 'Diese Immobilie von uns verwalten lassen',
    pmOfferBody: 'Mietersuche, Ejari, Mietinkasso, Inspektionen und Instandhaltung — übernommen von unserem Team, und alles erscheint auf diesem Bildschirm.',
    pmOfferCta: 'Sprechen Sie mit uns',
    // --- Owner PM — vorbefüllte Chat-Nachrichten zu den Hinweisen oben ---
    pmChatIdle: 'Ich habe Immobilien, die noch keine Mieteinnahmen erzielen. Kann Asset Desk sie vermieten und für mich verwalten?',
    pmChatDocs: 'Ich möchte Ihnen den Mietvertrag für meine vermieteten Immobilien senden.',
    pmChatRenew: 'Bei mir endet bald ein Mietvertrag. Können Sie die Verlängerung übernehmen?',
    pmChatManage: 'Ich möchte, dass Asset Desk diese Immobilie verwaltet:',
    pmOccupancyRate: 'Belegungsquote',
    pmOccupancySub: 'Beispieldaten — Durchschnitt über Ihre Immobilien.',
    pmRentReceived: 'Miete erhalten',
    pmPaidSuffix: 'bezahlt',
    pmExpense: 'Ausgabe',
    pmNoActivity: 'Noch keine Aktivitäten.',
    // Immobilienliste
    pmNoPropsManagedText: 'Immobilien in Verwaltung werden hier aufgeführt.',
    pmTenant: 'Mieter',
    pmLeaseEnds: 'Mietvertrag endet',
    pmMaintJob: 'Instandhaltungsauftrag',
    pmMaintJobs: 'Instandhaltungsaufträge',
    pmInProgressLower: 'in Bearbeitung',
    // Objektangaben — Bau, Kosten, Hypothek, Vermietungsart
    pmUnderConstruction: 'Im Bau',
    pmBuilt: 'fertiggestellt',
    pmConstruction: 'Bau',
    pmExpectedHandover: 'Voraussichtliche Übergabe',
    pmExpectedRent: 'Erwartete Miete',
    pmMarketEstimate: 'DLD-Marktschätzung',
    pmNoRentYet: 'Noch keine Miete',
    pmExpectedRentFrom: 'Geschätzt aus',
    pmRentalContracts: 'vergleichbaren DLD-Mietverträgen',
    pmConfHigh: 'hohe Verlässlichkeit',
    pmConfMedium: 'mittlere Verlässlichkeit',
    pmConfLow: 'geringe Verlässlichkeit',
    pmServiceCharge: 'Servicegebühr',
    pmPerYear: '/Jahr',
    pmPerSqftYear: 'AED/sqft/Jahr',
    pmFromHandover: 'ab Übergabe',
    pmServiceChargeNotYet: 'Servicegebühren fallen erst ab Übergabe an, daher wird noch nichts abgezogen.',
    pmNetAfterCosts: 'Nettoertrag nach allen Kosten',
    pmServiceChargeUnknown: 'Für dieses Gebäude ist noch keine Servicegebühr veröffentlicht, der Betrag oben enthält daher keine.',
    pmServiceChargeBooked: 'Aus der unter Ausgaben erfassten Servicegebühren-Rechnung übernommen.',
    pmMortgage: 'Hypothek',
    pmMortgageYes: 'Mit Hypothek',
    pmMortgageNo: 'Ohne Hypothek',
    pmMortgageLender: 'Bank / Kreditgeber',
    pmMortgageOutstanding: 'Restschuld',
    pmMortgageMonthly: 'Monatliche Rate',
    pmLettingMode: 'Vermietung',
    pmShortLet: 'Kurzzeitmiete',
    pmLongLet: 'Langzeitmiete',
    pmShortLetBrand: 'Betreiber (z. B. DreamHaven)',
    pmPropertyFacts: 'Objektangaben',
    pmPropertyFactsSub: 'Zwei Angaben, die kein Register pro Einheit veröffentlicht. Einmal eintragen — sie erscheinen in jedem Bericht.',
    pmNotStated: 'Keine Angabe',
    pmYes: 'Ja',
    pmNo: 'Nein',
    pmSave: 'Speichern',
    pmSaved: 'Gespeichert',
    pmSaveFailed: 'Konnte nicht gespeichert werden — bitte erneut versuchen.',
    pmCopy: 'Kopieren',
    pmCopied: 'Kopiert',
    pmCopyName: 'Objektnamen kopieren',
    pmCopyRef: 'Referenznummer kopieren',
    pmRegistryDetails: 'Register & Versorger',
    pmAddExpense: 'Ausgabe erfassen',
    pmWithTeam: 'Zur Bestätigung bei Ihrem Verwaltungsteam',
    pmSpendByCategory: 'Wofür ausgegeben',
    pmSpendByProperty: 'Kosten je Immobilie',
    pmCostliest: 'Ihre teuerste Immobilie',
    pmNoTenants: 'Noch keine Mietverhältnisse',
    pmNoTenantsText: 'Sobald eine Einheit vermietet und der Vertrag registriert ist, erscheinen Mieter und Miete hier.',
    pmAllRentOnTime: 'Alle Mieten pünktlich',
    pmOverdue: 'Überfällig',
    pmOnTime: 'Pünktlich',
    pmDaysLower: 'Tage',
    pmNextCheque: 'Nächster Scheck',
    pmExpenseWhat: 'Wofür war das?',
    pmExpenseSaveError: 'Die Ausgabe konnte nicht gespeichert werden. Bitte erneut versuchen.',
    pmProjectPhoto: 'Foto dieses Projekts',
    pmAddPhoto: 'Foto hinzufügen',
    pmUploading: 'Wird hochgeladen…',
    pmPhotoUploadFailed: 'Das Foto konnte nicht hochgeladen werden — bitte erneut versuchen.',
    pmNoPhotosYet: 'Noch keine Fotos dieser Immobilie.',
    pmPhotoHint: 'Sobald Sie eines hinzufügen, erscheint es auf Ihrer Objektkarte und in Ihren Berichten.',
    pmSourceUnit: 'aus Ihrem Kaufvertrag',
    pmSourceDldRegistry: 'DLD-Projektregister',
    pmSourceDldProgress: 'DLD-Baufortschritt',
    pmSourceFeed: 'Baubericht des Entwicklers',
    pmSourceIndex: 'DLD-Servicegebührenindex',
    pmSourceInvoice: 'aus der Gebäuderechnung',
    pmSourceManual: 'von Ihrer Verwaltung erfasst',
    // Dokumente
    pmNoDocsText: 'Ejari, Mietverträge und Emirates-ID-Kopien erscheinen hier, sobald sie hochgeladen wurden.',
    pmDocOpenError: 'Dieses Dokument konnte leider nicht geöffnet werden. Bitte versuchen Sie es erneut.',
    pmDocument: 'Dokument',
    pmView: 'Ansehen',
    pmDownload: 'Herunterladen',
    // Ausgaben
    pmTotalExpensesYear: 'Gesamtausgaben (Jahr)',
    pmNoExpenses: 'Keine Ausgaben erfasst',
    pmNoExpensesText: 'Servicegebühren und Instandhaltungskosten erscheinen hier.',
    pmDue: 'Fällig',
    // Immobiliendetail
    pmCurrentTenant: 'Aktueller Mieter',
    pmMaintainedBy: 'Gepflegt von Ihrem Verwaltungsteam',
    pmFinancialOverview: 'Finanzübersicht',
    pmOwnershipProgress: 'Eigentumsfortschritt',
    pmPhotosVideo: 'Fotos & Video',
    pmTenantAnalytics: 'Mieter-Analyse',
    pmPropertyNo: 'Objektnummer',
    pmPlotNo: 'Grundstücksnummer',
    pmStillNeeded: 'Noch erforderlich für den DLD-Mietindex',
    pmDewaPremises: 'DEWA (Objektnummer)',
    pmEjariNo: 'Ejari-Vertragsnummer',
    pmMakaniNo: 'Makani-Nr.',
    pmContractStart: 'Vertragsbeginn',
    pmContractEnd: 'Vertragsende',
    pmCurrentAnnualRent: 'Aktuelle Jahresmiete (AED)',
    pmUsage: 'Nutzung',
    pmFlat: 'Wohnung',
    pmRentDue: 'Offene Miete',
    pmOccupancy: 'Belegung',
    pmOpenRequests: 'Offene Anfragen',
    pmLeaseUntil: 'Mietvertrag bis',
    pmPhone: 'Telefon',
    pmNextPaymentApproval: 'Nächste Zahlung — Ihre Freigabe erforderlich',
    pmRentCollectedFull: 'Miete vollständig eingegangen',
    pmLastPayment: 'Letzte Zahlung',
    pmNextPayment: 'Nächste Zahlung',
    pmApprovePayment: 'Diese Zahlung freigeben',
    pmAllPaymentsConfirmed: 'Alle Zahlungen bestätigt',
    pmNetIncomeYear: 'Nettoeinnahmen (Jahr)',
    pmEstimated: 'Geschätzt',
    pmLifecycle: 'Lebenszyklus',
    pmTenants: 'Mieter',
    // Airbnb-Einnahmen von DreamHaven (AirbnbIncome.tsx)
    pmAirbnbTab: 'Airbnb',
    pmLongTermTab: 'Langzeitvermietung',
    // Wohnungen / Büros (PropertyManagementView)
    pmCatAll: 'Alle',
    pmCatApartments: 'Wohnungen',
    pmCatOffices: 'Büros',
    pmExpRental: 'Vermietung',
    pmExpPurchase: 'Kauf',
    pmAirbnbIncome: 'Airbnb-Einnahmen',
    pmAirbnbManagedBy: 'Verwaltet von DreamHaven',
    pmAirbnbNet12: 'Netto an Sie, letzte 12 Monate',
    pmAirbnbThisMonth: 'Dieser Monat bisher',
    pmAirbnbOccupancy: 'Auslastung',
    pmAirbnbNightsBooked: 'Gebuchte Nächte',
    pmAirbnbGross: 'Auszahlung der Plattform (brutto)',
    pmAirbnbCleaning: 'Reinigung',
    pmAirbnbTourism: 'Tourism Dirham',
    pmAirbnbFee: 'DreamHaven-Gebühr (20 %)',
    pmAirbnbNet: 'Netto an Sie',
    pmAirbnbProvisional: 'Vorläufig',
    pmAirbnbCorrected: 'Korrigiert',
    pmAirbnbMonths: 'Monat für Monat',
    pmAirbnbBookings: 'Buchungen',
    pmAirbnbBookingsEmpty: 'Keine bestätigten Aufenthalte in den letzten 12 Monaten.',
    pmAirbnbBookingsError: 'Die Buchungen konnten gerade nicht geladen werden.',
    pmAirbnbNights: 'Nächte',
    pmAirbnbNight: 'Nacht',
    pmAirbnbGuests: 'Gäste',
    pmAirbnbGuest: 'Gast',
    pmAirbnbBookingsNote: 'Eine Zeile pro bestätigtem Aufenthalt, gemeldet von DreamHaven. Der Betrag ist die Plattform-Auszahlung für den gesamten Aufenthalt, vor Reinigung, Tourism Dirham und Verwaltungsgebühr; Ihren Nettobetrag sehen Sie oben Monat für Monat.',
    pmAirbnbProvisionalNote: 'Der laufende Monat kann sich noch ändern, solange Buchungen hinzukommen oder storniert werden.',
    pmAirbnbHowNet: 'Netto = (Auszahlung − Reinigung − Tourism Dirham) × 80 %. Jeder Betrag zählt im Monat des Aufenthalts.',
    pmAirbnbEmpty: 'Noch keine Monate gemeldet. Sie erscheinen nach der nächsten täglichen Aktualisierung.',
    pmAirbnbSample: 'Beispieldaten',
    pmAirbnbUpdated: 'Aktualisiert',
    pmAirbnbOpen: 'Öffnen',
    pmAirbnbNetSince: 'Netto an Sie seit',
    pmAirbnbChartTitle: 'Netto an Sie pro Monat',
    pmAirbnbSoFar: 'bisher',
    pmAirbnbApartment: 'Wohnung',
    pmAirbnbTotal: 'Gesamt',
    pmAirbnbViewListing: 'Inserat auf Airbnb ansehen',
    pmRetained: 'Verlängert',
    pmRetention: 'Mieterbindung',
    pmWhyTenantsLeft: 'Warum Mieter ausgezogen sind',
    pmAllRenewed: 'Alle früheren Mieter haben verlängert — hervorragende Mieterbindung. 🎉',
    pmPrivacyNote: 'Instandhaltungsanfragen der Mieter werden direkt von unserem internen Team bearbeitet — Sie werden nur kontaktiert, wenn eine Entscheidung oder eine Kostenfreigabe erforderlich ist.',
    pmSignedInAs: 'Angemeldet als',
    pmOwner: 'Eigentümer',
    pmPreviewData: 'Vorschaudaten',
    // Inspektion
    pmInspectionReports: 'Prüfberichte',
    pmInspectionSub: 'Einzugs-, Auszugs- und regelmäßige Prüfungen durch Ihr Team.',
    pmReports: 'Berichte',
    pmPassed: 'Bestanden',
    pmAttention: 'Zu prüfen',
    pmPass: 'Bestanden',
    pmViewReport: 'Bericht ansehen',
    // Instandhaltung
    pmMaintSub: 'Wird direkt von Ihrem internen Team bearbeitet.',
    pmStatusOpen: 'Offen',
    pmOpen: 'Offen',
    pmInternalTeam: 'Internes Team',
    pmInternalMaintTeam: 'Internes Instandhaltungsteam',
    pmGeneral: 'Allgemein',
    pmMaintReport: 'Instandhaltungsbericht',
    // Inventar
    pmAssetInventory: 'Inventarverzeichnis',
    pmInventorySub: 'Möbel und Geräte, die mit der Einheit übergeben wurden.',
    pmRooms: 'Räume',
    pmItems: 'Gegenstände',
    pmQty: 'Anz.',
    pmCondGood: 'Gut',
    pmCondFair: 'Mittel',
    pmCondPoor: 'Schlecht',
    // Beispieldatensätze (bis die Unterlagen des Verwaltungsteams vorliegen)
    pmSampleInsp1Type: 'Einzugsprüfung',
    pmSampleInsp1Notes: '12 Mängel bei der Übergabe erfasst, 10 behoben. 2 kleinere Punkte offen: klapperndes Klimaanlagengitter im Wohnzimmer und Ausrichtung des Balkontürschlosses.',
    pmSampleInsp2Type: 'Regelmäßige Prüfung',
    pmSampleInsp2Notes: 'Immobilie in gutem Zustand. Klimaanlagenfilter und Reinigung aktuell. Keine neuen Mängel festgestellt.',
    pmSampleMaint1: 'Klimaanlagenwartung (jährlich)',
    pmSampleMaint1Cat: 'Klima & Heizung',
    pmSampleMaint2: 'Reparatur einer undichten Küchenspüle',
    pmSampleMaint2Cat: 'Sanitär',
    pmSampleMaint3: 'Austausch des Warmwasserboilers',
    pmSampleMaint3By: 'Warten auf Ihre Freigabe',
    pmRoomLiving: 'Wohnzimmer',
    pmRoomKitchen: 'Küche',
    pmRoomMaster: 'Hauptschlafzimmer',
    pmItemSofa: '3-Sitzer-Sofa',
    pmItemTv: 'Smart-TV 55"',
    pmItemCoffeeTable: 'Couchtisch',
    pmItemFridge: 'Kühlschrank',
    pmItemOven: 'Backofen & Kochfeld',
    pmItemWasher: 'Waschmaschine',
    pmItemBed: 'Kingsize-Bett & Matratze',
    pmItemWardrobe: 'Einbauschrank',
    pmItemCurtains: 'Vorhänge',

    // ==================== MIETERPORTAL ====================
    tpMyHome: 'Mein Zuhause',
    tpPreviewData: 'Vorschaudaten',
    tpStaff: 'Mitarbeiter',
    tpTenantWorkspace: 'Mieter-Arbeitsbereich',
    tpOwnerWorkspace: 'Eigentümer-Arbeitsbereich',
    // Anmeldung
    tpAccessEyebrow: 'MIETERZUGANG',
    tpLoginTitle: 'Mieter-Anmeldung',
    tpCheckInbox: 'Prüfen Sie Ihr Postfach',
    tpLoginSub: 'Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Anmeldecode',
    tpCodeSub: 'Geben Sie den 6-stelligen Code ein, den wir Ihnen per E-Mail gesendet haben',
    tpCodeLabel: '6-stelliger Code',
    tpSendCode: 'Code senden',
    tpVerifySignIn: 'Bestätigen & anmelden',
    tpChangeEmail: 'E-Mail-Adresse ändern',
    tpResendCode: 'Code erneut senden',
    tpEnterEmail: 'Bitte geben Sie Ihre E-Mail-Adresse ein',
    tpCodeSentTo: 'Wir haben einen 6-stelligen Code gesendet an',
    tpCodeSendFailed: 'Der Code konnte nicht gesendet werden. Bitte versuchen Sie es erneut.',
    tpEnterCode: 'Geben Sie den 6-stelligen Code ein',
    tpCodeInvalid: 'Falscher oder abgelaufener Code.',
    // Übersicht
    tpRentOverdueBy: 'Mietzahlung überfällig seit',
    tpRentDueToday: 'Mietzahlung heute fällig',
    tpRentDueIn: 'Mietzahlung fällig in',
    tpDay: 'Tag',
    tpDays: 'Tage',
    tpDaysDat: 'Tagen',
    tpDueLower: 'fällig am',
    tpTapToView: 'zum Ansehen tippen',
    tpDueToday: 'Heute fällig',
    tpDueIn: 'Fällig in',
    tpLeaseEnded: 'Mietvertrag beendet',
    tpDaysLeft: 'Tage übrig',
    tpMonthsLeft: 'Monate übrig',
    tpNextPaymentDue: 'Nächste fällige Zahlung',
    tpViewSchedule: 'Zahlungsplan ansehen',
    tpRentPaidThisYear: 'In diesem Jahr bezahlte Miete',
    tpPaidLower: 'bezahlt',
    tpTotalLower: 'gesamt',
    tpRecentRequest: 'Letzte Anfrage',
    tpNoOpenRequests: 'Keine offenen Anfragen',
    tpTapToReport: 'Tippen, um einen Mangel zu melden',
    tpLatestDocuments: 'Neueste Dokumente',
    tpReportIssue: 'Mangel melden',
    tpMyDocuments: 'Meine Dokumente',
    tpPaymentPlan: 'Zahlungsplan',
    tpContactTeam: 'Team kontaktieren',
    vrRecheckTitle: 'Sieht das falsch aus?',
    vrRecheckBody: 'Die Schätzung basiert auf registrierten DLD-Transaktionen und kann eine modernisierte Küche, eine Premium-Aussicht oder eine bereits vermietete Einheit nicht erkennen. Sagen Sie uns, was fehlt — unser Team prüft es manuell und sendet Ihnen eine schriftliche Bewertung.',
    vrRecheckCta: 'Bewertung überprüfen lassen',
    vrRecheckAction: 'Neubewertung anfordern',
    prTargetSet: 'Ziel festlegen',
    prTargetChange: 'Ziel ändern',
    prTargetLabel: 'Angestrebte jährliche Nettomiete',
    prTargetSave: 'Ziel speichern',
    prTargetCancel: 'Abbrechen',
    prTargetReset: 'Vorschlag nutzen',
    prTargetHint: 'Auf diesem Gerät gespeichert. Sagen Sie Ihrem Betreuer, wenn es im Konto hinterlegt werden soll.',
    vrDisclaimerBody: 'Dieser Bericht ist eine automatisierte Marktindikation zu Informationszwecken, erstellt aus den Transaktionsdaten des Dubai Land Department. Er ist kein RICS-zertifiziertes Gutachten und ersetzt keine professionelle Bewertung. Die Sch\u00e4tzung setzt voraus, dass die Immobilie in normalem Zustand und unvermietet ist; Modernisierungen, Ausbau, Etage, Aussicht und H\u00f6he der Service Charges sind nicht ber\u00fccksichtigt. Verwenden Sie sie nicht als Preis f\u00fcr eine Transaktion.',
    vrCostNote: 'Standardtarife Dubai \u2014 DLD-\u00dcbertragungsgeb\u00fchr 4% plus AED 580 Verwaltungsgeb\u00fchr, Treuhandgeb\u00fchr plus 5% VAT, Maklerprovision 2% plus 5% VAT. Hypothekenregistrierung, NOC und Abgrenzung der Service Charges sind nicht enthalten.',
    vrCostNoteThin1: 'Berechnet auf einem Indikationswert, der auf weniger als',
    vrCostNoteThin2: 'Vergleichsverk\u00e4ufen beruht \u2014 behandeln Sie die Summe als n\u00e4herungsweise.',
    vrThinCompare: 'Zu wenige aktuelle Weiterverk\u00e4ufe im selben Geb\u00e4ude mit gleicher Zimmerzahl, um Ihren Kaufpreis verl\u00e4sslich zu vergleichen. Die Marktsch\u00e4tzung oben gilt weiterhin als Indikation.',
    tpRenewalTitle: 'Vertragsverlängerung',
    tpRenewalEndsIn: 'Ihr Mietvertrag endet in',
    tpRenewalEndsOn: 'endet am',
    tpRenewalStepNotice: 'Frist für die Verlängerungsmitteilung beginnt',
    tpRenewalStepNoticeSub: '90 Tage vor Vertragsende — bis dahin muss eine Seite kündigen oder verlängern',
    tpRenewalStepDecide: 'Verlängern oder kündigen',
    tpRenewalStepDecideSub: 'Sagen Sie früh Bescheid, dann halten wir die Wohnung für Sie',
    tpRenewalStepNew: 'Neuer Vertrag beginnt',
    tpRenewalStepNewSub: 'Ejari verlängert, Schecks neu terminiert',
    tpRenewalTalk: 'Über die Verlängerung sprechen',
    tpRenewalOpenNow: 'Jetzt offen',
    tpRenewalDone: 'Erledigt',
    tpRenewalDoc: 'Ansehen',
    tpSampleDocTitle: 'Dokumentvorschau',
    tpSampleDocNote: 'Beispieldokument',
    tpSampleDocProperty: 'Immobilie',
    tpSampleDocTenant: 'Mieter',
    tpSampleDocPeriod: 'Vertragslaufzeit',
    tpSampleDocRent: 'Jahresmiete',
    tpSampleDocEjari: 'Ejari-Nummer',
    tpSampleDocClose: 'Schließen',
    // Zahlungen
    tpAnnualRent: 'Jahresmiete',
    tpCheque: 'Scheck',
    tpCheques: 'Schecks',
    tpChequeLabel: 'Scheck',
    tpPayment: 'Zahlung',
    tpPayments: 'Zahlungen',
    tpOverdueLower: 'überfällig',
    tpContactToSettle: 'Bitte kontaktieren Sie das Verwaltungsteam zur Begleichung.',
    tpChequeSchedule: 'Scheckplan',
    // Anfragen
    tpRequestsNote: 'Ihre Anfragen gehen direkt an unser internes Verwaltungsteam. Wir bearbeiten sie unmittelbar — der Eigentümer wird nicht behelligt.',
    tpNewRequest: 'Neue Instandhaltungsanfrage',
    tpMyRequests: 'Meine Anfragen',
    tpNoRequests: 'Noch keine Anfragen.',
    tpSubmitted: 'Eingereicht',
    tpInReview: 'In Prüfung',
    tpResolved: 'Erledigt',
    tpNewRequestTitle: 'Neue Anfrage',
    tpTitle: 'Titel',
    tpTitlePlaceholder: 'z. B. Klimaanlage kühlt nicht',
    tpDetails: 'Details',
    tpDetailsPlaceholder: 'Beschreiben Sie den Mangel...',
    tpSubmitToTeam: 'An das Verwaltungsteam senden',
    tpJustNow: 'Gerade eben',
    tpRecently: 'Kürzlich',
    tpTenant: 'Mieter',
    tpYourUnit: 'Ihre Einheit',
    tpDocument: 'Dokument',
    // Dokumente
    tpNeedAnotherDoc: 'Benötigen Sie ein weiteres Dokument?',
    tpMessageTeam: 'Verwaltungsteam kontaktieren',

    // ==================== CEO-PORTAL ====================
    ceoExecutive: 'Führung',
    ceoOwed: 'Forderungen',
    ceoVat: 'VAT',
    ceoCompliance: 'Compliance',
    ceoExecutiveSummary: 'Management-Übersicht',
    ceoMoneyOwed: 'Offene Forderungen',
    ceoVatPosition: 'VAT-Position',
    ceoPreviewData: 'Vorschaudaten',
    // Zugangsbildschirme
    ceoAccessEyebrow: 'FÜHRUNGSZUGANG',
    ceoOpening: 'Ihr Unternehmen wird geöffnet',
    ceoOneMoment: 'Einen Moment — wir verwenden Ihre Asset-Desk-Anmeldung',
    ceoNotAvailable: 'Noch nicht verfügbar',
    ceoNoCompanyLinked: 'Mit diesem Konto ist kein Unternehmen verknüpft',
    ceoNotOwner: 'Dieses Konto ist nicht als Unternehmensinhaber hinterlegt.',
    ceoUseDifferentEmail: 'Andere E-Mail-Adresse verwenden',
    ceoAccessTitle: 'CEO-Zugang',
    ceoCheckInbox: 'Prüfen Sie Ihr Postfach',
    ceoLoginSub: 'Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Anmeldecode',
    ceoCodeSub: 'Geben Sie den 6-stelligen Code ein, den wir Ihnen per E-Mail gesendet haben',
    ceoCodeLabel: '6-stelliger Code',
    ceoSendCode: 'Code senden',
    ceoVerifyContinue: 'Bestätigen & fortfahren',
    ceoChangeEmail: 'E-Mail-Adresse ändern',
    ceoResendCode: 'Code erneut senden',
    ceoViewPreview: 'Vorschau ansehen',
    ceoNotConfigured: 'Dieser Build ist noch nicht mit dem Unternehmensportal verbunden. Öffnen Sie die Vorschau, um die Führungsansichten zu sehen.',
    ceoEnterEmail: 'Bitte geben Sie Ihre E-Mail-Adresse ein',
    ceoCodeSentTo: 'Wir haben einen 6-stelligen Code gesendet an',
    ceoCodeSendFailed: 'Der Code konnte nicht gesendet werden. Bitte versuchen Sie es erneut.',
    ceoEnterCode: 'Geben Sie den 6-stelligen Code ein',
    ceoCodeInvalid: 'Falscher oder abgelaufener Code.',
    // Management-Übersicht
    ceoYearToDate: 'seit Jahresbeginn',
    ceoMargin: 'Marge',
    ceoRevenue: 'Umsatz',
    ceoOutstanding: 'Offen',
    ceoInvoice: 'Rechnung',
    ceoInvoices: 'Rechnungen',
    ceoNetVat: 'Netto-VAT',
    ceoDuePrefix: 'Fällig',
    ceoLicence: 'Lizenz',
    ceoCashAtBank: 'Bankguthaben',
    ceoAccountsInTotal: 'Konten insgesamt',
    ceoTotal: 'Gesamt',
    ceoBalancesUpdated: 'Salden zuletzt aktualisiert',
    ceoNetOfVat: 'netto, ohne VAT',
    ceoTopCustomers: 'Top-Kunden',
    ceoRevenueLower: 'Umsatz',
    ceoNoRevenue: 'Noch kein Umsatz erfasst.',
    ceoTopSuppliers: 'Top-Lieferanten',
    ceoSpendLower: 'Ausgaben',
    ceoNoExpenses: 'Noch keine Ausgaben erfasst.',
    ceoPeak: 'Höchstwert',
    ceoMonth: 'Monat',
    ceoMonths: 'Monate',
    ceoChartAria: 'Monatlicher Umsatz im Vergleich zu den Ausgaben',
    // Forderungen
    ceoTotalOutstanding: 'Gesamte offene Forderungen',
    ceoWithinTerms: 'innerhalb der Zahlungsfrist',
    ceoOverdueLower: 'überfällig',
    ceoPastDue: 'überfällig',
    ceoChaseFirst: 'Diese zuerst nachverfolgen — das ist die schnellste Liquidität im Unternehmen.',
    ceoLargestOverdue: 'Größte überfällige Beträge',
    ceoNothingOverdue: 'Nichts überfällig. Alle Rechnungen liegen innerhalb der Zahlungsfrist.',
    ceoDueLower: 'fällig am',
    ceoDayShort: 'T',
    ceoLate: 'verspätet',
    // VAT
    ceoNoVatPeriod: 'Kein VAT-Zeitraum konfiguriert',
    ceoNoVatPeriodSub: 'Bitten Sie Ihre Steuerberatung, den Abgabezyklus im Unternehmensportal zu hinterlegen.',
    ceoDueIn: 'Fällig in',
    ceoPayableFta: 'an die FTA zu zahlen',
    ceoRefundableFta: 'von der FTA zu erstatten',
    ceoFilingDue: 'Abgabe fällig am',
    ceoOutputVat: 'VAT auf Ausgangsleistungen',
    ceoChargedOnSales: 'auf Verkäufe berechnet',
    ceoInputVat: 'VAT auf Eingangsleistungen',
    ceoReclaimable: 'erstattungsfähig',
    ceoPeriod: 'Zeitraum',
    ceoCovers: 'Umfasst',
    ceoSpend: 'Ausgaben',
    ceoInclVat: '(inkl. VAT)',
    ceoNet: '(netto)',
    ceoVatNote: 'Die Zahlen stammen direkt aus dem Buchhaltungsjournal des Unternehmensportals. Die Erklärung selbst wird dort erstellt und eingereicht — dieser Bildschirm ist die Führungsansicht der Position.',
    // Compliance
    ceoEntity: 'Gesellschaft',
    ceoJurisdiction: 'Rechtsraum',
    ceoLicenceNo: 'Lizenznr.',
    ceoRenewals: 'Verlängerungen',
    ceoNext90Days: 'nächste 90 Tage',
    ceoNothingExpiring: 'In den nächsten 90 Tagen läuft nichts ab.',
    ceoNoDateOnFile: 'kein Datum hinterlegt',
    ceoNoDate: 'kein Datum',
    ceoExpiredSuffix: 'abgelaufen',
    ceoExpiresToday: 'läuft heute ab',
    ceoLeft: 'übrig',
    ceoAllClear: 'Alles in Ordnung',

    // ==================== GÄSTEPORTAL ====================
    gpAccessEyebrow: 'KURZZEIT-GAST',
    gpBookStay: 'Aufenthalt buchen',
    gpSub: 'Kurzaufenthalte in Asset-Desk-Wohnungen',
    gpFeaturedStays: 'Empfohlene Aufenthalte',
    gpGuests: 'Gäste',
    gpPerNight: '/ Nacht',
    gpAllStays: 'Alle Aufenthalte',
    gpBookingSoon: 'Buchung folgt in Kürze',
    gpBookingSoonText: 'Der vollständige Buchungsablauf für Kurzaufenthalte (Termine, Gäste, Zahlung) wird derzeit finalisiert. Sagen Sie uns, wie Gäste buchen sollen, und wir setzen es hier um.',
    gpReserve: 'Reservieren',

    // ==================== NACHRICHTEN (Eigentümer <-> Verwaltung) ====================
    omTitle: 'Nachrichten',
    omSectionTitle: 'Ihr Verwaltungsteam',
    omSubtitle: 'Direkter Draht zu Ihrer Hausverwaltung',
    omEmptyTitle: 'Noch keine Nachrichten',
    omEmptyText: 'Schreiben Sie hier direkt an Ihr Verwaltungsteam. Die Antwort kommt in der App, alles bleibt in einem Verlauf.',
    omPlaceholder: 'Nachricht schreiben…',
    omSend: 'Senden',
    omYou: 'Sie',
    omLoadFailed: 'Ihre Nachrichten konnten nicht geladen werden. Bitte erneut versuchen.',
    omSendFailed: 'Ihre Nachricht wurde nicht gesendet. Bitte erneut versuchen.',

    // ==================== ADMIN-POSTEINGANG ====================
    inbFilterOpen: 'Posteingang',
    inbFilterArchived: 'Archiviert',
    inbFilterAll: 'Alle',
    inbSearch: 'Unterhaltungen suchen...',
    inbSearchOwners: 'Eigentümer suchen...',
    inbClear: 'Leeren',
    inbNew: 'Neu',
    inbNewTitle: 'Neue Unterhaltung',
    inbEmptyTitle: 'Nichts offen',
    inbEmptyText: 'Nachrichten von Eigentümern landen hier. Mit Neu starten Sie selbst eine.',
    inbThreadEmptyTitle: 'Noch keine Nachrichten',
    inbThreadEmptyText: 'Schreiben Sie die erste Nachricht. Der Eigentümer sieht sie sofort in seiner App.',
    inbPlaceholder: 'Dem Eigentümer antworten…',
    inbConversation: 'Unterhaltung',
    inbYouPrefix: 'Sie:',
    inbNewCount: 'neu',
    inbStatusOpen: 'Offen',
    inbNoMessagesYet: 'Noch keine Nachrichten',
    inbArchive: 'Archivieren',
    inbUnarchive: 'Zurückholen',
    inbCancel: 'Zurück',
    inbNoOwners: 'Keine Eigentümer gefunden',
    inbNoOwnersText: 'Eigentümer erscheinen hier, sobald sie im System angelegt sind.',
    inbNoEmail: 'Keine E-Mail hinterlegt',
    inbHasThread: 'Verlauf vorhanden',
    inbLoadFailed: 'Der Posteingang konnte nicht geladen werden. Bitte erneut versuchen.',
    inbSendFailed: 'Ihre Antwort wurde nicht gesendet. Bitte erneut versuchen.',

    // ==================== EA-ADMINKONSOLE ====================
    eaTabMessages: 'Nachrichten',
    eaBackToConsole: 'Adminkonsole',
    eaTabCustomers: 'Kunden',
    eaTabAssetDesk: 'Asset Desk',
    eaTabPm: 'Hausverwaltung',
    eaTabChat: 'Chat',
    eaTabIssues: 'Issues',
    eaTabAccess: 'Zugriffsprotokoll',
    eaOpenFullScreen: 'Vollbild',
    eaOpenConsole: 'Konsole öffnen',
    adFilterWaiting: 'Zu prüfen',
    adFilterVerified: 'Bestätigt',
    adFilterRejected: 'Abgelehnt',
    adNoneHere: 'Nichts vorhanden',
    adAllClearText: 'Alle Dokumente sind geprüft. Neue Uploads erscheinen hier.',
    adVerify: 'Bestätigen',
    adRevoke: 'Widerrufen',
    adReject: 'Ablehnen',
    adConfirm: 'Bestätigen',
    adRejectReason: 'Begründung für den Investor',
    adDecisionFailed: 'Das wurde nicht gespeichert. Bitte erneut versuchen.',
    isStart: 'Starten',
    isResolve: 'Erledigen',
    isReopen: 'Wieder öffnen',
    isUpdateFailed: 'Der Status wurde nicht gespeichert. Bitte erneut versuchen.',
    adSignIn: 'Anmelden',
    adSigningIn: 'Anmeldung läuft…',
    adSignOut: 'Abmelden',
    adPassword: 'Admin-Passwort',
    adEnterPassword: 'Bitte das Admin-Passwort eingeben.',
    adSignInFailed: 'Anmeldung fehlgeschlagen. Bitte Passwort prüfen.',
    adSignInWhy: 'Asset Desk benötigt das Admin-Passwort — dasselbe wie in der Konsole. Es wird einmal abgefragt und auf diesem Gerät gemerkt.',
    adFullConsole: 'Vollständige Konsole',
    adUsers: 'Nutzer',
    adLogins: 'Anmeldungen',
    adPageViews: 'Seitenaufrufe',
    adDocViews: 'Dokumentaufrufe',
    adAwaiting: 'zur Prüfung',
    adTotalDocs: 'Dokumente',
    adNothingWaiting: 'Nichts offen',
    adNothingWaitingText: 'Zu prüfende Dokumente erscheinen hier.',
    adLoadFailed: 'Asset Desk konnte nicht geladen werden. Bitte erneut versuchen.',
    pdOverview: 'Übersicht',
    pdProperties: 'Objekte',
    pdRented: 'Vermietet',
    pdVacant: 'Leerstand',
    pdOffplan: 'Off-Plan',
    pdRenewals: 'Verlängerungen in 3 Monaten',
    pdAnnualRent: 'Jahresmiete',
    pdOverdue: 'Miete überfällig',
    pdAcross: 'über',
    pdUnit: 'Einheit',
    pdUnits: 'Einheiten',
    pdDefaulters: 'Säumige',
    pdDefaultersSub: 'mit überfälligem Scheck',
    pdRentCollection: 'Mieteingang',
    pdInvoiced: 'In Rechnung gestellt',
    pdCollected: 'Eingegangen',
    pdOutstanding: 'Offen',
    pdServiceRequests: 'Serviceanfragen',
    pdIncomeByProperty: 'Mieteinnahmen je Objekt',
    apSearch: 'Einheiten, Eigentümer, Mieter suchen...',
    apUnits: 'Objekte',
    apNoUnits: 'Noch keine Objekte',
    apNoUnitsText: 'Einheiten erscheinen hier, sobald sie im System angelegt sind.',
    apStatusUnknown: 'Kein Status',
    apVacant: 'Leerstand',
    apVacantText: 'In diesem Objekt wohnt derzeit niemand.',
    apOpenJobs: 'offen',
    apAllUnits: 'Alle Objekte',
    apTenant: 'Mieter',
    apName: 'Name',
    apEmail: 'E-Mail',
    apPhone: 'Telefon',
    apContract: 'Vertrag',
    apEjari: 'Ejari',
    apRent: 'Miete eingegangen',
    apCheque: 'Scheck',
    apPaid: 'Bezahlt',
    apDue: 'Fällig',
    apNoRent: 'Kein Zahlungsplan für diese Einheit.',
    apMaintenance: 'Wartung',
    apNoMaintenance: 'Nichts gemeldet.',
    apNoDocuments: 'Keine Dokumente zu dieser Einheit.',
    apNoInspections: 'Keine Begehungen erfasst.',
    apNoExpenses: 'Keine Kosten erfasst.',
    apEditInConsole: 'Uploads und Änderungen in der Konsole',
    apLoadFailed: 'Die Objekte konnten nicht geladen werden. Bitte erneut versuchen.',
    isOpenCases: 'Offene Fälle',
    isResolved: 'Erledigt',
    isSearch: 'Vorgänge, Einheiten, Mieter suchen...',
    isNoneOpen: 'Nichts offen',
    isNoneResolved: 'Noch nichts erledigt',
    isEmptyText: 'Wartung und Mieteranfragen aus allen Objekten erscheinen hier, neueste zuerst.',
    isUnknownUnit: 'Keine Einheit zugeordnet',
    isFromTenant: 'vom Mieter gemeldet',
    isStatusNew: 'Neu',
    isStatusOpen: 'Offen',
    isStatusInProgress: 'In Arbeit',
    isStatusResolved: 'Erledigt',
    isLoadFailed: 'Die Vorgänge konnten nicht geladen werden. Bitte erneut versuchen.',
    eaConsoleNotEmbedded: 'Wird vorerst in einem neuen Tab geöffnet. Die Anzeige in der App erfordert gelockerte Framing-Header — eine Sicherheitsänderung, die noch aussteht.',
    eaAssetDeskNote: 'Asset Desk · Investoren, Einheiten, Dokumente',
    eaPmNote: 'Hausverwaltung · Mietverhältnisse, Miete, Übergabe',
    eaConsoleNarrow: 'Diese Konsole ist für einen Desktop-Bildschirm gebaut. Sie funktioniert hier, lässt sich am Laptop aber leichter bedienen.',
    eaConsoleTitle: 'Admin · EA-Konsole',
    eaMyView: 'Meine Ansicht',
    eaExit: 'Beenden',
    eaExitViewAs: 'Ansicht verlassen',
    eaViewingAs: 'Ansicht als',
    eaSearchCustomers: 'Kunden suchen...',
    eaCustomers: 'Kunden',
    eaUnit: 'Einheit',
    eaUnits: 'Einheiten',
    eaTenant: 'Mieter',
    eaPropertyOwner: 'Eigentümer',
    eaPmActive: 'Verwaltung aktiv',
    eaAllCustomers: 'Alle Kunden',
    eaOpenAsCustomer: 'App als dieser Kunde öffnen',
    eaSeeExactly1: 'Sehen Sie genau, was',
    eaSeeExactly2: 'sieht — mit dessen Live-Daten.',
    eaOpenAppPre: '',
    eaOpenAppPost: '-App öffnen',
    eaDocsOnFile: 'Hinterlegte Dokumente',
    eaNoUnitLinked: 'Diesem Kunden ist noch keine Einheit zugeordnet.',
    eaUploadDocument: 'Dokument hochladen',
    eaFile: 'Datei',
    eaChooseFile: 'Tippen, um eine Datei auszuwählen',
    eaUploadTo: 'Hochladen für',

    // ==================== ROLLENAUSWAHL ====================
    rsWelcome: 'Willkommen',
    rsChooseHow: 'Bitte wählen Sie, wie Sie fortfahren möchten',
    rsSecurityNote: 'Verschlüsselte Verbindung · Passwörter nie im Klartext gespeichert',
    rsLegal: 'Rechtliches',
    rsTerms: 'Nutzungsbedingungen',
    rsPrivacy: 'Datenschutzerklärung',
    rsOwnerTitle: 'Eigentümer',
    rsOwnerSub: 'Portfolio, Dokumente und Immobilienverwaltung',
    rsTenantTitle: 'Mieter',
    rsTenantSub: 'Ihr Zuhause, Mietvertrag, Zahlungen und Anfragen',
    rsGuestTitle: 'Kurzzeit-Gast',
    rsGuestSub: 'Kurzaufenthalt buchen',
    rsDemoTitle: 'Gastzugang',
    rsDemoSub: 'Zugangscode erforderlich — alle Ansichten mit Beispieldaten',
    rsCeoTitle: 'CEO',
    rsCeoSub: 'Unternehmenszahlen, Forderungen, VAT und Compliance',
    rsPartnerTitle: 'Partner',
    rsPartnerSub: 'Kooperations- und Empfehlungspartnerschaften, Provisionen',
    rsServicesTitle: 'Unser Leistungsangebot',
    rsServicesSub: 'Alle Leistungen — von der Firmengründung bis zum Verkauf Ihrer Immobilie',

    rsTourTitle: 'So funktioniert die App',
    rsTourSub: 'Ein kurzer Film, jeder Bildschirm erklärt und wie wir Sie starten lassen',

    // ==================== WILLKOMMEN (erster Bildschirm) ====================
    weHeadline: 'Ihr Immobilienvermögen. An einem Ort.',
    wePoint1: 'Wissen, was Sie besitzen',
    wePoint2: 'Sehen, was Sie verdienen',
    wePoint3: 'Erkennen, was als Nächstes zu tun ist',
    weSub: 'Für Eigentümer von Immobilien in Dubai — von der ersten Wohnung bis zum ganzen Portfolio.',
    weGetStarted: 'Jetzt starten',
    weTour: 'So funktioniert es',
    weEyebrow: 'Privater Kundenzugang',
    weHeadlineA: 'Ihr Immobilienvermögen,',
    weHeadlineB: 'an einem Ort.',
    weSignIn: 'Anmelden',
    weCreateAccount: 'Konto erstellen',
    weTourSub: 'Eine kurze geführte Tour durch die App',

    // ==================== ROLLENWECHSEL (auf den Anmeldebildschirmen) ====================
    rwCaption: 'Wählen Sie, wer Sie sind, und legen Sie los',

    // ==================== APP-TOUR (vor dem Login) ====================
    tourBack: 'Zurück',
    tourEyebrow: 'Eine geführte Tour',
    tourTitle: 'Sehen Sie die App, bevor Sie sich anmelden',
    tourSub: 'Was wir für Ihre Immobilie tun — und der Bildschirm, auf dem alles zusammenläuft.',
    tourPlay: 'Tour ansehen',
    tourSilent: 'Bildschirmaufnahme ohne Ton — was jeder Bildschirm kann, steht unten geschrieben.',

    // Beispielzahlen direkt nach dem Film, als Beispiel gekennzeichnet.
    tourKpiLabel: 'Beispielportfolio',
    tourKpiValueLabel: 'Portfoliowert',
    tourKpiValue: 'AED 24.850.000',
    tourKpiGrowthLabel: 'Wertentwicklung',
    tourKpiGrowth: '+ AED 3.420.000 · +15,9 %',
    tourKpiRentLabel: 'Nettomiete',
    tourKpiRent: 'AED 782.000\u00a0/\u00a0Jahr',
    tourKpiNextLabel: 'Nächste Zahlung',
    tourKpiNext: 'AED 425.000 in\u00a023\u00a0Tagen',
    tourKpiNote: 'Beispielzahlen. Nach der Anmeldung sehen Sie hier Ihr eigenes Portfolio.',

    tourWhyKicker: 'Drei Fragen',
    tourWhyTitle: 'Nicht mehr Funktionen. Drei Antworten.',
    tourWhyLede: 'Sie sind in Europa, Ihre Immobilie nicht. Alles in der App beantwortet eine von drei Fragen — und die Bewertung stammt aus den Unterlagen des Dubai Land Department, nicht aus unserer Meinung über Ihre Immobilie.',
    tourQ1Title: 'Was besitze ich?',
    tourQ1a: 'Alle Einheiten in einer Liste',
    tourQ1b: 'Verträge, Title Deeds und Zahlungspläne',
    tourQ1c: 'Marktwert aus echten DLD-Transaktionen',
    tourQ2Title: 'Was verdiene ich?',
    tourQ2a: 'Eingegangene und offene Miete',
    tourQ2b: 'Nettomiete pro Jahr und Rendite',
    tourQ2c: 'Wertentwicklung seit Kauf',
    tourQ2d: 'Gewinne aus verkauften Einheiten',
    tourQ3Title: 'Was muss ich tun?',
    tourQ3a: 'Die nächste Rate an den Developer',
    tourQ3b: 'Mietverträge, die auslaufen',
    tourQ3c: 'Übergabe und fehlende Dokumente',
    tourQ3d: 'Was ein Verkauf netto bringen würde',

    tourPmFullTitle: 'Vollständige Immobilienverwaltung',
    tourPmFullSub: 'Ab dem Tag der Schlüsselübergabe erledigt unser Team die Arbeit — und alles davon landet auf einem Bildschirm, den Sie jederzeit öffnen können.',
    tourPmMeansLabel: 'Was Ihnen bleibt:',
    tourPmMeans: 'Eine Immobilie, die verdient, eine Entscheidung nur dann, wenn sie wirklich Ihre ist — und ein schriftlicher Nachweis über alles andere.',
    tourPmSeeAll: 'Die vollständige Verwaltung',

    tourBenKicker: 'Und die App selbst',
    tourBenTitle: 'Was die Software dazugibt',
    tourBenLede: 'Die Arbeit oben erledigen Menschen. Diese vier Dinge kann nur die App leisten.',
    tourBen1Title: 'Bewertet aus echten Transaktionen',
    tourBen1Body: 'Jede Einheit bewertet auf Basis der Kaufverträge des Dubai Land Department — mit Vergleichsverkäufen, Sechs-Monats-Trend und Preis pro sqft hinter der Zahl. Und einer Schaltfläche, um sie von Hand prüfen zu lassen, wenn Sie anderer Meinung sind.',
    tourBen2Title: 'Ein Bildschirm für alles, was auf Sie wartet',
    tourBen2Body: 'Übergabetermine, fällige Raten, DEWA-Aktivierung, Mietvertragsverlängerungen — sortiert danach, was als Nächstes passiert.',
    tourBen3Title: 'Beide Währungen, beide Sprachen',
    tourBen3Body: 'Jede Zahl in AED und EUR, jeder Bildschirm und jeder Bericht auf Deutsch oder Englisch — damit Ihr Steuerberater zu Hause dieselben Zahlen liest wie Sie.',
    tourBen4Title: 'Berichte, die Sie einer Bank vorlegen können',
    tourBen4Body: 'Portfolio- und Einzelbewertungsberichte als PDF — geschrieben, um von jemandem gelesen zu werden, der nicht dabei war.',

    tourShotsKicker: 'Was drin ist',
    tourShotsTitle: 'Die Bildschirme, einer nach dem anderen',
    tourShotsLede: 'Echte Aufnahmen aus dem Eigentümerportal — dieselben Ansichten, mit denen Ihr Verwaltungsteam arbeitet. Wischen Sie sich durch.',
    tourShotDashTitle: 'Dashboard',
    tourShotDashCap: 'Was das Portfolio wert ist — und alles, was eine Entscheidung von Ihnen braucht.',
    tourShotPortTitle: 'Portfolio auf einen Blick',
    tourShotPortCap: 'Gesamtinvestition, jährliche Nettomiete, Fortschritt zu Ihrem Mietziel und Erlöse aus verkauften Einheiten.',
    tourShotValTitle: 'Bewertung',
    tourShotValCap: 'DLD-Marktwert je Einheit, der Trend der letzten sechs Monate und die Vergleichsverkäufe dahinter.',
    tourShotRepTitle: 'Portfoliobericht',
    tourShotRepCap: 'Ein privater Bericht in Euro oder Dirham, zum Lesen gemacht — und als PDF exportierbar.',
    tourShotPmTitle: 'Immobilienverwaltung',
    tourShotPmCap: 'Eingezogene gegen offene Miete, Belegung, Ausgaben und Service Charges über alle Einheiten.',
    tourShotUnitTitle: 'Jede Immobilie',
    tourShotUnitCap: 'Übersicht, Abnahme, Instandhaltung, Ausgaben und Dokumente — je Einheit, mit Mieter und Scheckterminen.',
    tourShotLifeTitle: 'Eigentums-Lebenszyklus',
    tourShotLifeCap: 'Snagging, Übergabe, Mietervermittlung, Verträge und Mietverfolgung — abgehakt, sobald erledigt.',
    tourShotTenantTitle: 'Mieter und Zahlungen',
    tourShotTenantCap: 'Wer in Ihrer Einheit wohnt, bis wann, und der nächste Scheck mit Fälligkeitsdatum.',
    tourShotSignTitle: 'Anmeldung',
    tourShotSignCap: 'E-Mail und Passwort, nach dem ersten Mal Face ID — oder ein Code per WhatsApp.',

    tourStepsKicker: 'Der Start',
    tourStepsTitle: 'So nehmen wir Sie an die Hand',
    tourStepsLede: 'Vier Schritte — und nur einer davon ist Ihrer.',
    tourStep1Title: 'Wir laden Sie ein',
    tourStep1Body: 'Eine Einladung geht an die E-Mail-Adresse, die wir von Ihnen haben. Nichts zu installieren — es läuft im Browser und als App auf iPhone und Android.',
    tourStep2Title: 'Sie melden sich an und setzen ein Passwort',
    tourStep2Body: 'Einmal. Danach genügt Face ID oder ein WhatsApp-Code.',
    tourStep3Title: 'Wir laden Ihr Portfolio',
    tourStep3Body: 'Ihre Einheiten, Verträge, Zahlungspläne und Dokumente tragen wir aus den Unterlagen ein, die uns vorliegen. Sie tippen nichts ein.',
    tourStep4Title: 'Wir halten es aktuell',
    tourStep4Body: 'Neue Raten, Übergabetermine, Verlängerungen und Bewertungen erscheinen, sobald sie anfallen. Offene Fragen gehen per WhatsApp an Ihren Betreuer.',

    tourEndTitle: 'Bereit, wenn Sie es sind',
    tourEndSub: 'Melden Sie sich mit unserer Einladung an — oder fragen Sie uns, was die Verwaltung für Ihre Einheit kosten würde. Es antwortet ein echter Mensch.',
    tourEndSignIn: 'Weiter zur Anmeldung',
    tourEndAsk: 'Fragen per WhatsApp',
    tourWaMessage: 'Hallo, ich habe gerade die App-Tour gesehen. Ich möchte mehr über die Immobilienverwaltung und das Eigentümerportal für meine Immobilie erfahren: ',
    tourAiKicker: 'Fragen Sie die Asset Desk KI',
    tourAiFab: 'Asset Desk KI fragen',
    tourAiTitle: 'Fragen? Die Asset Desk KI antwortet.',
    tourAiLede: 'Unser KI-Assistent zeigt Ihnen, wie die App funktioniert, und beantwortet danach Ihre Fragen zu Immobilien in Dubai – mit echten Zahlen und ehrlichen Risiken.',
    tourAiGreeting: 'Guten Tag. Ich bin die Asset Desk KI, Ihr persönlicher AI Wealth Co-Pilot von Asset Desk in Dubai. Ich zeige Ihnen gern, was die App für Eigentümer tut, und beantworte danach Ihre Fragen zu Immobilien in Dubai.\n\nWomit möchten Sie beginnen?\n1. Wie die App funktioniert\n2. Wie wird eine Immobilie bewertet?\n3. Ich überlege, in Dubai zu kaufen',
    tourAiPlaceholder: 'Ihre Frage …',
    tourAiSend: 'Senden',
    tourAiThinking: 'Die Asset Desk KI denkt nach …',
    tourAiError: 'Die KI konnte gerade nicht antworten. Bitte versuchen Sie es erneut.',
    tourAiLimit: 'Sie haben die Anzahl der Fragen für den Moment erreicht. Bitte versuchen Sie es später erneut oder schreiben Sie uns auf WhatsApp.',
    tourAiInterested: 'Interessiert mich',
    tourAiNote: 'KI-Assistent: Antworten können Fehler enthalten und ersetzen keine Anlage-, Steuer- oder Rechtsberatung. Bitte geben Sie keine sensiblen Daten ein. Wenn Sie Kontaktdaten teilen, nutzen wir sie nur, um Sie zu Ihrer Anfrage zu kontaktieren.',


    // ==================== PARTNERPORTAL ====================
    // Rahmen
    pnPartner: 'Partner',
    pnProgramme: 'Programm',
    pnCooperation: 'Kooperation',
    pnReferral: 'Empfehlung',
    pnSignOut: 'Abmelden',

    // --- Das Angebot (PartnerPitch) ---
    pnPitchEyebrow: 'EMPFEHLUNGSPARTNER-PROGRAMM',
    pnPitchTitle: 'Sie kennen einen Käufer?',
    pnPitchTitleEm: 'Empfehlen Sie ihn. Verdienen Sie.',
    pnPitchSub: 'Werden Sie Asset Desk Empfehlungspartner. Eine Vereinbarung unterschreiben, dann Käufer direkt aus der App empfehlen — Name, E-Mail, Telefonnummer — und jede Empfehlung verfolgen. Kostenlos, und Immobilienkenntnisse sind nicht erforderlich.',
    pnStatDevelopers: 'Entwicklerpartner',
    pnStatCost: 'Kosten für den Beitritt',
    pnStatSteps: 'Schritte zum Start',
    pnBenefitsEyebrow: 'IHRE VORTEILE',
    pnBenefitsTitle: 'In Sekunden empfehlen, dann mitverfolgen',
    pnBenefitsLede: 'Alles, um jemanden zu empfehlen und zu erfahren, was daraus wurde — ohne jemandem hinterherzutelefonieren.',
    pnBenefit1: 'Eine Empfehlung vom Handy in drei Feldern — Name, E-Mail und Telefonnummer.',
    pnBenefit2: 'Ihre Empfehlung erreicht unser Vertriebsteam sofort, erfasst unter Ihrem Namen.',
    pnBenefit3: 'Ein Dashboard mit allen empfohlenen Personen und dem jeweiligen Stand.',
    pnBenefit4: 'Wir sagen Ihnen sofort, wenn ein Kontakt bereits bei uns erfasst ist — so arbeiten Sie nie an einer toten Spur.',
    pnBenefit5: 'Zugang zum Dubai-Portfolio, das wir verkaufen — Emaar, Meraas, Nakheel, Omniyat, Sobha und weitere.',
    pnStepsEyebrow: 'SO FUNKTIONIERT ES',
    pnStepsTitle: 'Vier Schritte, kein Konto nötig',
    pnStep1Title: 'In einer Minute bewerben',
    pnStep1: 'Name, E-Mail und Telefonnummer. Kein Konto und kein Passwort nötig, um zu starten.',
    pnStep2Title: 'Vereinbarung unterschreiben',
    pnStep2: 'Wir senden Ihnen die Partnerschaftsvereinbarung zwischen Asset Desk und Ihnen per E-Mail. Online prüfen und unterschreiben — nichts zum Ausdrucken.',
    pnStep3Title: 'Mit Ihrer E-Mail anmelden',
    pnStep3: 'Sobald die Vereinbarung unterschrieben ist, ist Ihr Konto aktiv. Wir senden Ihnen jedes Mal einen Code — kein Passwort zu merken.',
    pnStep4Title: 'Empfehlen und verfolgen',
    pnStep4: 'Eine Empfehlung in Sekunden einreichen und den Fortschritt im Dashboard verfolgen.',
    pnClosingTitle: 'Bereit für Ihre erste Empfehlung?',
    pnClosingLede: 'Die Bewerbung ist kostenlos und dauert etwa eine Minute.',
    pnCtaApply: 'Empfehlungspartner werden',
    pnCtaContinue: 'Zu meiner Bewerbung',

    // ---- Empfehlungspartner (rf*) ----
    rfPartnerTitle: 'Empfehlungspartner',
    rfNavRefer: 'Empfehlen',
    rfNavReferrals: 'Meine Empfehlungen',
    rfNoteTitle: 'IHRE VERG\u00dcTUNG',
    rfNote: 'Ihre Provision ist in der Vereinbarung geregelt, die wir Ihnen senden, und steht fest, bevor Sie jemanden empfehlen. Wir nennen hier bewusst keinen Satz \u2014 er geh\u00f6rt in einen unterschriebenen Vertrag, nicht in eine Schlagzeile.',
    rfAlreadyPartner: 'Ich bin bereits Partner',
    rfApplyEyebrow: 'BEWERBUNG ALS EMPFEHLUNGSPARTNER',
    rfApplyTitle: 'Bewerben Sie sich',
    rfApplySub: 'Sagen Sie uns, wer Sie sind. Ihre Vereinbarung senden wir Ihnen sofort per E-Mail.',
    rfApplyCta: 'Als Partner bewerben',
    rfApplyFailed: 'Ihre Bewerbung konnte nicht gesendet werden. Bitte erneut versuchen.',
    rfName: 'Vollst\u00e4ndiger Name',
    rfPhone: 'Telefonnummer',
    rfCompany: 'Unternehmen (optional)',
    rfEmailHint: 'Hierhin senden wir Ihre Vereinbarung \u2014 und damit melden Sie sich sp\u00e4ter an.',
    rfNeedNameEmail: 'Bitte geben Sie Ihren Namen und eine g\u00fcltige E-Mail-Adresse ein.',
    rfSending: 'Wird gesendet\u2026',
    rfBack: 'Zur\u00fcck',
    rfAppliedTitle: 'Bewerbung erhalten',
    rfAppliedBody: 'Vielen Dank. Unser Team bereitet Ihre Partnerschaftsvereinbarung vor und sendet sie Ihnen in K\u00fcrze per E-Mail.',
    rfContractSentTitle: 'Ihre Vereinbarung ist unterwegs',
    rfContractSentBody: 'Wir haben Ihnen die Partnerschaftsvereinbarung zwischen Asset Desk und Ihnen per E-Mail gesendet. Unterschreiben Sie online, und Ihr Konto ist sofort aktiv \u2014 ohne Drucken, ohne Scannen.',
    rfContractSpamHint: 'Nach ein paar Minuten nichts angekommen? Bitte pr\u00fcfen Sie Ihren Spam-Ordner, bevor Sie eine neue Kopie anfordern.',
    rfAlreadySigned: 'Ich habe unterschrieben \u2014 anmelden',
    rfSignInNote: 'Vereinbarung schon unterschrieben? Nutzen Sie dieselbe E-Mail-Adresse wie bei der Bewerbung.',
    rfReferEyebrow: 'NEUE EMPFEHLUNG',
    rfReferTitle: 'Jemanden empfehlen',
    rfReferSub: 'Drei Angaben gen\u00fcgen. Den Rest \u00fcbernimmt unser Team.',
    rfReferCta: 'Empfehlung senden',
    rfReferNeedAll: 'Bitte Name, g\u00fcltige E-Mail-Adresse und Telefonnummer angeben.',
    rfReferOk: 'Empfehlung gesendet. Den Fortschritt sehen Sie in Ihrem Dashboard.',
    rfReferFailed: 'Die Empfehlung konnte nicht gesendet werden. Bitte erneut versuchen.',
    rfReferConsentNote: 'Bitte empfehlen Sie nur Personen, die von uns h\u00f6ren m\u00f6chten. Wir kontaktieren sie zu Immobilien in Dubai; ein Widerspruch ist jederzeit m\u00f6glich.',
    rfDashEyebrow: 'IHRE EMPFEHLUNGEN',
    rfDashTitle: 'Von Ihnen empfohlen',
    rfDashOne: 'Empfehlung',
    rfDashMany: 'Empfehlungen',
    rfDashEmptyTitle: 'Noch keine Empfehlungen',
    rfDashEmptyBody: 'Sobald Sie Ihre erste Empfehlung senden, erscheint sie hier \u2014 mit ihrem Fortschritt.',
    rfDashFailed: 'Ihre Empfehlungen konnten nicht geladen werden.',
    rfLoading: 'Wird geladen\u2026',
    rfRefresh: 'Aktualisieren',
    rfActivePartner: 'Aktiver Empfehlungspartner',
    rfDuplicateFlag: 'Bereits erfasst',
    rfStatus_submitted: 'Eingereicht',
    rfStatus_contacted: 'Kontaktiert',
    rfStatus_in_progress: 'In Bearbeitung',
    rfStatus_closed: 'Abgeschlossen',
    rfStatus_not_proceeding: 'Nicht weiterverfolgt',
    rfStatus_duplicate_claim: 'In Pr\u00fcfung',


    // --- Anmeldung ---
    pnAccessEyebrow: 'PARTNERZUGANG',
    pnAccessTitle: 'Partnerportal',
    pnLoginSubCode: 'Geben Sie Ihre E-Mail-Adresse ein, wir senden Ihnen einen sechsstelligen Code. Kein Passwort und kein bestehendes Konto nötig.',
    pnLoginSubPassword: 'Melden Sie sich mit dem Asset-Desk-Konto an, das Sie bereits für den Eigentümerbereich nutzen.',
    pnYourName: 'Vollständiger Name',
    pnNamePlaceholder: 'Wie dürfen wir Sie ansprechen?',
    pnPassword: 'Passwort',
    pnSendCode: 'Code senden',
    pnSignIn: 'Anmelden',
    pnUsePassword: 'Ich habe ein Passwort',
    pnUseCode: 'Stattdessen Code per E-Mail',
    pnSameAccountNote: 'Es ist dasselbe Konto wie im Eigentümerbereich — eine Anmeldung hier gilt auch dort.',
    pnConsentPre: 'Ich stimme den',
    pnConsentMid: ' und der ',
    pnConsentPost: ' zu.',
    pnConsentRequired: 'Bitte akzeptieren Sie Nutzungsbedingungen und Datenschutzerklärung.',
    pnEnterEmail: 'Bitte geben Sie Ihre E-Mail-Adresse ein.',
    pnEnterCredentials: 'Bitte geben Sie E-Mail und Passwort ein.',
    pnSignInFailed: 'Anmeldung fehlgeschlagen. Bitte prüfen Sie Ihre Angaben.',
    pnCheckInbox: 'Postfach prüfen',
    pnCheckAuthenticator: 'Zwei-Faktor-Prüfung',
    pnCodeSub: 'Geben Sie den sechsstelligen Code ein, den wir Ihnen gerade gesendet haben. Er ist 10 Minuten gültig.',
    pn2faSub: 'Geben Sie den sechsstelligen Code aus Ihrer Authenticator-App ein.',
    pnCodeLabel: 'Bestätigungscode',
    pnVerifyContinue: 'Bestätigen & fortfahren',
    pnCodeInvalid: 'Der Code wurde nicht akzeptiert. Bitte erneut versuchen.',
    pnEnterCode: 'Bitte geben Sie den sechsstelligen Code ein.',
    pnBackToSignIn: 'E-Mail ändern',
    pnResendCode: 'Code erneut senden',
    pnCodeSentTo: 'Code gesendet an',
    pnBackToProgramme: 'Zurück zum Programm',

    // ============ LEISTUNGSÜBERSICHT (vor dem Login) ============
    scBack: 'Zurück',
    scEyebrow: 'UNSER ANGEBOT',
    scHeroEyebrow: 'IMMOBILIEN-INVESTMENT IN DUBAI',
    scDevsTitle: 'Aktuell Verfügbar Für Ausgewählte Bauträger',
    scDevsAlt: 'Emaar, Meraas, Nakheel, Omniyat, Select Group, Sobha, Beyond, Imtiaz, Samana und Avenew',
    scTitle: 'Unsere Leistungen',
    scIntro: 'Geprüfte Dubai-Angebote, Deals unter Marktwert und ein Team von der ersten Steuerfrage bis zum Verkaufstag.',
    scHeroTitleA: 'Alles,',
    scHeroTitleB: 'der Reihe nach.',
    scClose: 'Schließen',
    scStatServices: 'Leistungen',
    scStatStages: 'Etappen',
    scStatTeam: 'Team',
    scStatUnits: 'Einheiten im Verkauf',
    scTabFractional: 'Antavio',
    scTabFractionalCap: 'Anteile',

    // ---- Antavio: Bruchteilseigentum (Warteliste, Lizenz ausstehend) ----
    scFrEyebrow: 'TOKENISIERTE DUBAI-IMMOBILIEN · F\u00dcR DEUTSCHLAND, \u00d6STERREICH & DIE SCHWEIZ',
    scFrTitleA: 'Dubai-Immobilien.',
    scFrTitleB: 'Ab',
    scFrTitleC: 'AED 4.300.',
    scFrStakeCap: 'Anteile \u2014 Ihr Anteil',
    scFrShareIllus: 'Illustrativ \u2014 Ihr Anteil ist ein Bruchteil des Ganzen, kein zugesagter Betrag.',
    scFrViewProject: 'Dieses Projekt ansehen',
    scFrWhyS1: '35',
    scFrWhyS1Sub: 'Jahre am Markt',
    scFrWhyS2: '~20.000',
    scFrWhyS2Sub: 'Investoren',
    scFrWhyS3: '~\u20ac350M',
    scFrWhyS3Sub: 'j\u00e4hrlich platziert',
    scFrWhyS4: '100%',
    scFrWhyS4Sub: 'digital \u00b7 deutschsprachig',
    scFrTeamAlt: 'Das Antavio-Team in Dubai',
    scFrLede: 'Das vertrauensw\u00fcrdigste Dubai-Immobilienunternehmen im deutschsprachigen Raum wird Ihnen erm\u00f6glichen, digital in erstklassige Dubai-Immobilien zu investieren — ab AED 4.300 — und an den Erträgen teilzuhaben.',
    scFrOneProperty: 'EINE IMMOBILIE, VIELE ANTEILE',

    scFrStat1: '35 Jahre',
    scFrStat1Sub: 'im Dubai-Markt',
    scFrStat2: '~20.000',
    scFrStat2Sub: 'Investoren',
    scFrStat3: '~\u20ac350M',
    scFrStat3Sub: 'j\u00e4hrlich platziert',

    scFrStat4: 'EU-reguliert',
    scFrStat4Sub: 'Wertpapierstruktur',
    scFrCrestCaption: 'RUHE ENTLANG DER K\u00dcSTE',
    scFrAppEyebrow: 'DIE ANTAVIO APP',
    scFrAppTitle: 'Alles an einem Ort verfolgen',
    scFrAppWatch: 'Intro ansehen',
    scFrApp1: 'Portfolio-Updates rund um die Uhr',
    scFrApp2: 'Sichere Face-ID-Anmeldung',
    scFrApp3: 'Portfolio-Verwaltung',
    scFrMediaNote: 'Video und Immobilienbilder werden aus dem Antavio-Projekt geladen.',
    scFrNoticeTitle: 'Noch nicht verf\u00fcgbar — auf die Early-Access-Liste',
    scFrNotice: 'Lizenzierung und Struktur werden noch abgeschlossen. Nichts auf dieser Seite ist ein Angebot oder eine Aufforderung zur Zeichnung; alle Zahlen sind illustrativ. Tragen Sie sich ein — wir melden uns, sobald es startet.',

    scFrMissionEyebrow: 'UNSERE MISSION',
    scFrMission: 'Einen der dynamischsten Immobilienm\u00e4rkte der Welt f\u00fcr alle zu \u00f6ffnen. Seit 35 Jahren helfen wir Investoren beim Kauf von Dubai-Immobilien — bislang aber nur jenen, die eine ganze Einheit finanzieren konnten. Wir wollen diese H\u00fcrde beseitigen, damit jeder ab AED 4.300 in erstklassige Dubai-Immobilien investieren kann, mit der Transparenz, Sicherheit und dem Vertrauen eines etablierten Maklerhauses.',

    scFrConceptEyebrow: 'DAS KONZEPT',
    scFrConceptTitle: 'Ein kleiner Anteil an einem Ganzen',
    scFrGridAria: 'Illustration: ein Anteil von vierundzwanzig an einer Immobilie.',
    scFrHeroGridAria: 'Illustration: vier Anteile von vierundzwanzig an einer Immobilie.',
    scFrConcept: 'Sie beginnen mit einem Bruchteil — und partizipieren an Ertrag und Wertentwicklung der gesamten Immobilie. Ihr Anteil ist einer von vielen; der Rest wird gemeinsam mit Ihnen gehalten. Kapitalrisiko; Renditen sind nicht garantiert.',

    scFrHowEyebrow: 'SO FUNKTIONIERT ES',
    scFrHowTitle: 'Vier einfache Schritte',
    scFrHow1Cap: 'ANSEHEN',
    scFrHow1Title: 'Gepr\u00fcfte Immobilien entdecken',
    scFrHow1: 'Gepr\u00fcfte Dubai-Immobilien mit klaren Zahlen und Prognosen.',
    scFrHow2Cap: 'INVESTIEREN',
    scFrHow2Title: 'Betrag w\u00e4hlen',
    scFrHow2: 'W\u00e4hlen Sie Ihren Betrag ab AED 4.300 und melden Sie sich schnell und sicher an.',
    scFrHow3Cap: 'ERTRAG',
    scFrHow3Title: 'Aussch\u00fcttungen erhalten',
    scFrHow3: 'Erhalten Sie Ihren Anteil an den Erträgen der Immobilie als Aussch\u00fcttung.',
    scFrHow4Cap: 'VERFOLGEN & VERKAUFEN',
    scFrHow4Title: 'Verfolgen & verkaufen',
    scFrHow4: 'Verfolgen Sie Ihre Anlage im Dashboard und verkaufen Sie auf dem Zweitmarkt, wann Sie m\u00f6chten.',
    scFrHowNote: 'Prognosen sind rein illustrativ. Kapitalrisiko; Renditen sind nicht garantiert.',

    scFrProjectEyebrow: 'UNSER ERSTES PROJEKT',
    scFrProjectName: 'SAMANA Ocean Crest',
    scFrProjectLede: 'Ein Wasserfront-Tower mit nur 56 Residenzen auf Dubai Islands, von SAMANA Developers. Die erste Immobilie, in die Sie investieren k\u00f6nnen — ab AED 4.300.',
    scFrProjectLoc: 'Dubai Islands, Dubai · ~20 Min. nach Downtown, Flughafen & DIFC',
    scFrProjectTitle: 'Eine Symphonie am Meer',
    scFrProjectBody: 'Geschwungene, geschichtete Architektur, inspiriert von der Bewegung des Meeres — mit privaten Balkonpools, einem Rooftop-Infinity-Deck und einer kompletten Wellness-Etage. Wohnungen von 1 bis 4 Schlafzimmern in einer der spannendsten neuen Inselgemeinden Dubais.',
    scFrProjS1: '56', scFrProjS1Sub: 'Residenzen',
    scFrProjS2: '~11,5%', scFrProjS2Sub: 'Zielrendite p.a.',
    scFrProjS3: '6,5%', scFrProjS3Sub: 'Prognostizierte Rendite',
    scFrProjS4: 'AED 4.300', scFrProjS4Sub: 'Mindestens ab',
    scFrAmen1: 'Rooftop-Infinity-Pool',
    scFrAmen2: 'Private Balkonpools',
    scFrAmen3: 'Gym innen & au\u00dfen',
    scFrAmen4: 'Dampfbad & Sauna',
    scFrAmen5: 'Joggingstrecke',
    scFrAmen6: 'Kinderpool & Splash Pad',
    scFrProjectNote: 'Prognosen sind Zielwerte und rein illustrativ. Kapitalrisiko; Renditen sind nicht garantiert. Sie w\u00fcrden in ein digitales Wertpapier investieren, das an die Erträge der Immobilie gekoppelt ist — nicht in direktes Eigentum an einer Wohnung.',

    scFrWhyEyebrow: 'WARUM ANTAVIO',
    scFrWhyTitle: 'Die Nr. 1 der deutschsprachigen Makler in Dubai',
    scFrWhyLede: 'Ein Familienunternehmen und ein etabliertes Maklerhaus — vor Ort in Dubai, wir beschaffen, pr\u00fcfen und verwalten jede Immobilie selbst.',
    scFrWhy1: 'Vor Ort in Dubai — wir beschaffen, pr\u00fcfen und verwalten die Immobilien selbst.',
    scFrWhy2: 'Deutschsprachige Betreuung und ein vollst\u00e4ndig digitaler Ablauf.',
    scFrWhy3: 'Transparente Prognosen — nie garantierte Renditen.',

    scFrChooseEyebrow: 'WARUM INVESTOREN UNS W\u00c4HLEN',
    scFrChooseTitle: 'Alles spricht f\u00fcr Sie',
    scFrChoose1: 'Schon ab AED 4.300',
    scFrChoose2: 'Ertragsstarke Dubai-Immobilien',
    scFrChoose3: '100% digital — kein Krypto n\u00f6tig',
    scFrChoose4: 'Kuratiert und professionell gepr\u00fcft',
    scFrChoose5: 'Weiterverkauf f\u00fcr Flexibilit\u00e4t',
    scFrChoose6: 'Deutschsprachiges Team vor Ort',

    scFrTrustEyebrow: 'VERTRAUEN & SICHERHEIT',
    scFrTrustTitle: 'Verl\u00e4ssliche Struktur, von Anfang bis Ende',
    scFrTrust1Title: '35 Jahre Maklerhaus',
    scFrTrust1: 'Immobilien, beschafft und verwaltet von einem etablierten Dubai-Maklerhaus — jeden Tag vor Ort.',
    scFrTrust2Title: 'Transparente Berichte',
    scFrTrust2: 'Nachpr\u00fcfbare Unterlagen und klare Berichte in jedem Schritt Ihrer Anlage.',
    scFrTrust3Title: 'Struktur in Vorbereitung',
    scFrTrust3: 'Die regulierte Struktur und ihre aufsichtsrechtliche Formulierung werden mit unserem Emissionspartner und unseren Juristen finalisiert. Wir ver\u00f6ffentlichen die Details, bevor etwas startet.',

    scFrRisk: 'Kapitalrisiko. Renditen sind nicht garantiert. Dies ist keine Anlageberatung. Alle gezeigten Zahlen sind illustrativ und stellen kein Angebot und keine Aufforderung dar.',

    // ---- Early-Access-Formular ----
    scFrFaqEyebrow: 'GUT ZU WISSEN',
    scFrFaqTitle: 'H\u00e4ufige Fragen',
    scFrQ1: 'Besitze ich die Wohnung?',
    scFrA1: 'Nicht die Wohnung selbst. Sie halten ein digitales Wertpapier, das Ihnen einen Anteil an der wirtschaftlichen Entwicklung der Immobilie gibt — an den Ertr\u00e4gen und an einem m\u00f6glichen Gewinn beim Verkauf. Eine moderne, regulierte Art, in Immobilien zu investieren, ohne Kosten, Papierkram und Pflichten eines ganzen Objekts. Eine eigene Gesellschaft h\u00e4lt die Immobilie, und Ihr Wertpapier verbrieft Ihren Anteil daran.',
    scFrQ2: 'Wie viel brauche ich zum Start?',
    scFrA2: 'Ab AED 4.300. Genau das ist die Idee — statt mehrere Hunderttausend Euro f\u00fcr eine ganze Dubai-Wohnung aufzubringen, investieren Sie einen kleinen Betrag in eine erstklassige Immobilie und partizipieren dennoch an den Ertr\u00e4gen. Sie entscheiden, wie viel, ab AED 4.300.',
    scFrQ3: 'Wie erhalte ich meine Erträge?',
    scFrA3: 'Durch regelm\u00e4\u00dfige Aussch\u00fcttungen auf Ihr Konto. Bei einer Off-Plan-Immobilie wie unserem Pilotprojekt erhalten Sie w\u00e4hrend der Bauphase eine prognostizierte, vom Bautr\u00e4ger getragene Rendite; nach Fertigstellung und \u00dcbergabe stammt Ihre Rendite aus Mieteinnahmen und einer m\u00f6glichen Wertsteigerung. Alle Renditen sind Prognosen, nach Kosten und nicht garantiert.',
    scFrQ4: 'Muss ich Krypto verstehen?',
    scFrA4: 'Nein. Im Hintergrund kommt sichere Technologie f\u00fcr digitale Wertpapiere zum Einsatz, aber Sie m\u00fcssen nie Krypto anfassen, eine Wallet verwalten oder Technisches lernen. Sie investieren, verfolgen und erhalten Aussch\u00fcttungen \u00fcber eine einfache App — wie beim Online-Banking.',
    scFrQ5: 'Kann ich meine Anlage verkaufen?',
    scFrA5: 'Solche Anlagen sind auf mittel- bis langfristige Haltedauer ausgelegt. Wo ein Weiterverkauf m\u00f6glich ist, k\u00f6nnen Sie Ihren Anteil \u00fcber unsere Plattform anderen Investoren anbieten, sofern ein K\u00e4ufer vorhanden ist — Liquidit\u00e4t ist also nicht garantiert. Wir nennen bei jedem Angebot die erwartete Haltedauer und m\u00f6gliche Ausstiegsoptionen, bevor Sie investieren.',
    scFrQ6: 'Ist mein Geld einem Risiko ausgesetzt?',
    scFrA6: 'Ja — wie bei jeder Immobilien- oder Anlageform ist Ihr Kapital einem Risiko ausgesetzt und Renditen sind nicht garantiert. Der Wert kann fallen und steigen, Renditen k\u00f6nnen unter der Prognose liegen, und im schlimmsten Fall k\u00f6nnen Sie einen Teil oder Ihr gesamtes Kapital verlieren. Wir legen die konkreten Risiken jeder Immobilie klar dar, bevor Sie investieren.',
    scFrJoin: 'Auf die Early-Access-Liste',
    scFrCtaEyebrow: 'ALS ERSTE DABEI',
    scFrCtaTitle: 'Fr\u00fchzugang zu unseren ersten Dubai-Immobilien',
    scFrCtaBody: 'Wir \u00f6ffnen den Zugang f\u00fcr eine begrenzte Gruppe von Investoren. Tragen Sie sich ein — wir informieren Sie, sobald unsere ersten Dubai-Immobilien live gehen.',
    scFrStep: 'Frage {n} von {total}',
    scFrLastStep: 'Letzter Schritt',
    scFrBack: 'Zur\u00fcck',
    scFrQCountry: 'Welcher Markt interessiert Sie am meisten?',
    scFrQCountryO1: 'Vereinigte Arabische Emirate (Dubai)',
    scFrQCountryO2: 'Deutschland',
    scFrQCountryO3: 'Spanien',
    scFrQCountryO4: 'Keine Pr\u00e4ferenz',
    scFrQInterest: 'Was ist Ihnen am wichtigsten?',
    scFrQInterestO1: 'Wertsteigerung',
    scFrQInterestO2: 'Aussch\u00fcttungen aus Mieteinnahmen',
    scFrQInterestO3: 'Eine regulierte Struktur',
    scFrQInterestO4: 'Alles davon',
    scFrQAmount: 'Welche Gr\u00f6\u00dfenordnung k\u00e4me f\u00fcr Sie infrage?',
    scFrQAmountO1: '1.000 \u20ac – 10.000 \u20ac',
    scFrQAmountO2: '10.000 \u20ac – 100.000 \u20ac',
    scFrQAmountO3: '\u00dcber 100.000 \u20ac',
    scFrQAmountO4: 'Noch unklar',
    scFrQTiming: 'Wann m\u00f6chten Sie starten?',
    scFrQTimingO1: 'Sobald es startet',
    scFrQTimingO2: '1–3 Monate',
    scFrQTimingO3: '3–6 Monate',
    scFrQTimingO4: 'Ich schaue mich nur um',
    scFrNote: 'M\u00f6chten Sie uns noch etwas mitteilen? (optional)',
    scFrConsent: 'Sie d\u00fcrfen mich zu Antavio Bruchteilseigentum kontaktieren. Ich kann dies jederzeit widerrufen.',
    scFrSubmit: 'Auf die Early-Access-Liste',
    scFrSubmitting: 'Wird gesendet…',
    scFrDoneTitle: 'Sie stehen auf der Liste',
    scFrDone: 'Vielen Dank — wir melden uns, sobald unsere ersten Dubai-Immobilien live gehen.',
    scFrRequired: 'Bitte Namen, eine g\u00fcltige E-Mail-Adresse und die Einwilligung angeben.',
    scFrError: 'Das hat nicht funktioniert. Bitte erneut versuchen oder per WhatsApp melden.',
    scViewAllShort: 'Öffnen',
    scHomeSoon: 'bald',
    scHomeFilmEyebrow: 'DER FILM',
    scHomeFilmTitle: 'Sechs Momente, von Anfang bis Ende',
    scHomeFilmLede: 'Fünfzehn Sekunden: das erste Licht über der Stadt, die Struktur, die Schlüssel, ein Zuhause mit Ertrag, ein Ort für die Familie und die Jahre danach.',
    scHomeBeat1: 'Dubai im ersten Licht',
    scHomeBeat2: 'Zuerst die Struktur',
    scHomeBeat3: 'Die Schlüssel in Ihrer Hand',
    scHomeBeat4: 'Ein Zuhause, das Erträge bringt',
    scHomeBeat5: 'Und ein Ort für die Familie',
    scHomeBeat6: 'Für die Jahre danach',
    scHomeFilmPlay: 'Film ansehen',
    scLanguage: 'Sprache',
    scTabHome: 'Start',
    scTabHomeCap: '\u00dcberblick',
    scHomeEyebrow: 'VON ANFANG BIS ENDE',
    scHomeTitle: 'Ihr Dubai-Investment, vollst\u00e4ndig begleitet',
    scHomeLede: 'Einheit kaufen, \u00dcbergabe abnehmen, langfristig oder kurzfristig vermieten, Gesellschaft gr\u00fcnden, Golden Visa sichern \u2014 und wenn Sie verkaufen, \u00fcbernehmen wir auch das. Ein Team, ein Ansprechpartner, jeder Schritt im Haus.',
    scHomeCall: 'Termin buchen',
    scHomeSee: 'So funktioniert es',
    scHomeProof1Sub: 'Leistungen im Haus',
    scHomeProof2Sub: 'Etappen, von Anfang bis Ende',
    scHomeProof3: 'Ein',
    scHomeProof3Sub: 'Ansprechpartner',
    scHomeChainEyebrow: 'DER GESAMTE WEG',
    scHomeChainTitle: 'Nichts wechselt auf halbem Weg den Besitzer',
    scHomeChainLede: 'Die meisten kaufen in Dubai \u00fcber eine Firma und suchen dann die n\u00e4chste f\u00fcr die Abnahme, die n\u00e4chste f\u00fcr die Vermietung, die n\u00e4chste f\u00fcr die Gesellschaft, die n\u00e4chste f\u00fcr die Aufenthaltsgenehmigung. An jeder \u00dcbergabe geht etwas verloren. Wir machen alles.',
    scHomeStageServices: '{n} Leistungen',
    scHomeExplore: 'Alle Leistungen ansehen',
    scHomeWhoEyebrow: 'WER WIR SIND',
    scHomeWhoTitle: 'Von Investoren. F\u00fcr Investoren.',
    scHomeWhoLede: 'Die Menschen, die diese Immobilien pr\u00fcfen, haben eigenes Kapital darin. Wir betreiben die Plattform nicht nur \u2014 wir kaufen selbst dar\u00fcber.',
    scHomeWhoPoint1: 'Die Gesellschafter investieren gemeinsam mit den Kunden',
    scHomeWhoPoint2: 'Nur gepr\u00fcfte Objekte',
    scHomeWhoPoint3: 'Vor Ort in Dubai',
    scHomeWhoCta: 'Sprechen Sie mit uns',
    scHomeWhoNote: 'Mitglieder des Teams k\u00f6nnen Immobilien halten oder in auf der Plattform gelistete Objekte investieren.',
    scHomeRoleChairman: 'Chairman',
    scHomeRoleFounder: 'Co-Founder',
    scGateEyebrow: 'SO WÄHLEN WIR AUS',
    scGateTitle: 'Nicht jeder Entwickler kommt in diese App.',
    scGateLede: 'Dubai hat Hunderte Entwickler und Tausende Projektstarts. Unser Investmentkomitee prüft jeden von ihnen nach denselben vier Punkten, in derselben Reihenfolge, bevor eine einzige Einheit in diese App gelangt. Die Liste ist bewusst kurz.',
    scGate1Title: 'Die Historie',
    scGate1: 'Was tatsächlich übergeben wurde — und wann. Angekündigte Türme zählen nicht, fertiggestellte schon. Wir sehen uns an, wie spät die letzten Fertigstellungen waren und in welchem Zustand das Gebäude am Tag der Schlüsselübergabe war.',
    scGate2Title: 'Die Bilanz',
    scGate2: 'Wer die aktuelle Bauphase finanziert, ob das Treuhandkonto so geführt wird, wie es das Gesetz vorschreibt, und was mit dem Projekt geschieht, wenn der Verkauf ins Stocken gerät.',
    scGate3Title: 'Der Bau',
    scGate3: 'Jemand aus unserem Team in Dubai steht in einer fertigen Einheit desselben Entwicklers. Das Rendering im Prospekt und die gelieferte Ausführung sind zwei verschiedene Produkte — verkauft wird nur eines davon.',
    scGate4Title: 'Die Zahlen',
    scGate4: 'Preis im Vergleich zu registrierten Transaktionen, Servicegebühr pro Quadratfuß und wie leicht sich eine vergleichbare Einheit weiterverkaufen ließ. Ist der Ausstieg eng, muss der Einstiegspreis das tragen.',
    scGateVerdict: 'Ein Entwickler kann drei dieser Punkte bestehen und am vierten scheitern. Das ist trotzdem ein Nein. Und auf der Liste zu stehen ist keine Kaufempfehlung — es heißt, dass wir Ihnen die Einheit sonst nicht vorgelegt hätten. Die Entscheidung und das Risiko bleiben bei Ihnen.',
    scGateStat1Sub: 'Entwickler auf der Liste',
    scGateStat2Sub: 'Einheiten derzeit gelistet',
    scGateCta: 'Sehen, was es auf die Liste geschafft hat',
    scGateAsk: 'Nach einem Entwickler fragen',
    scGateNote: 'Diese Prüfung beschreibt, wie wir auswählen. Sie ist keine Garantie für Fertigstellung, Rendite oder Wiederverkaufswert — das kann keine Prüfung sein. Ihre eigene Due Diligence bleibt notwendig.',
    scTabPm: 'Immobilien',
    scTabPmCap: 'Verwaltung',
    scPmEyebrow: 'IMMOBILIENVERWALTUNG',
    scPmTitle: 'Der Kauf dauert einen Nachmittag. Das Eigentum zehn Jahre.',
    scPmLede: 'Ein Mieter muss gefunden, ein Ejari registriert, Schecks eingelöst, eine Servicegebühr hinterfragt und ein Spülkasten zu einer Uhrzeit repariert werden, die niemandem passt. Das übernehmen wir ab dem Tag der Übergabe — und alles, was wir tun, landet auf einem Bildschirm, den Sie jederzeit öffnen können.',
    scPmGetEyebrow: 'WAS SIE BEKOMMEN',
    scPmGetTitle: 'Alles, was ein Vermieter sonst selbst tun müsste.',
    scPmGetLede: 'Kein Makler, der nach der Unterschrift verschwindet. Die Liste unten ist die gesamte Arbeit — und sie läuft bereits hinter dem Bildschirm auf dieser Seite.',
    scPmGrp1Title: 'Vermietet bekommen',
    scPmB1Title: 'Mietervermittlung',
    scPmB1: 'Wir vermarkten die Einheit, prüfen den Mieter samt Referenzen und registrieren das Ejari auf Ihren Namen.',
    scPmB2Title: 'Mieteinzug',
    scPmB2: 'Schecks termingerecht eingelöst — mit Übersicht je Objekt, was eingegangen und was noch offen ist.',
    scPmB3Title: 'Verlängerung und Mietanpassung',
    scPmB3: 'Jeder Mietvertrag neunzig Tage im Voraus markiert, mit der heute erzielbaren Miete statt einer Verlängerung zum Vorjahreswert.',
    scPmB4Title: 'Mieterbindung',
    scPmB4: 'Wer verlängert hat, wer gegangen ist und warum — damit der nächste Vertrag auf Fakten beruht und nicht auf Hoffnung.',
    scPmGrp2Title: 'In Ordnung halten',
    scPmB5Title: 'Mängelabnahme und Übergabe',
    scPmB5: 'Mängel gegenüber dem Bauträger dokumentiert, bevor Sie die Schlüssel annehmen, danach DEWA aktiviert und die Einheit bezugsfertig gemacht.',
    scPmB6Title: 'Inspektionen',
    scPmB6: 'Einzug, Auszug und regelmäßige Kontrollen — jeweils mit Bericht zum Öffnen statt eines Anrufs, dem Sie glauben müssen.',
    scPmB7Title: 'Instandhaltung im eigenen Haus',
    scPmB7: 'Unser eigenes Team, kein Subunternehmer eines Maklers. Jeder Auftrag wird vor Beginn kalkuliert.',
    scPmB8Title: 'Inventarverzeichnis',
    scPmB8: 'Jedes Möbelstück und jedes Gerät, Raum für Raum, mit Zustand — damit beim Auszug gegen eine Liste abgerechnet wird und nicht gegen Erinnerung.',
    scPmGrp3Title: 'Ruhe für Sie',
    scPmB9Title: 'Ein Bildschirm, jederzeit',
    scPmB9: 'Einnahmen, Vermietungsstand, Dokumente und Kosten an einem Ort — von überall aus.',
    scPmB10Title: 'Nichts wird ohne Sie ausgegeben',
    scPmB10: 'Mieteranfragen gehen an unser Team, nicht an Ihr Telefon. Sie werden nur gefragt, wenn eine Entscheidung oder eine Ausgabe freizugeben ist.',
    scPmB11Title: 'Unterlagen dort, wo sie hingehören',
    scPmB11: 'Ejari, Mietvertrag, Eigentumsurkunde und Emirates ID beim Objekt selbst abgelegt — nicht in irgendeinem Postfach.',
    scPmB12Title: 'Netto heißt netto',
    scPmB12: 'Servicegebühren, DEWA und Instandhaltung gegen die Miete gerechnet — die Zahl, die Sie lesen, ist die Zahl, die bleibt.',
    scPmScreenEyebrow: 'WAS SIE SEHEN',
    scPmScreenTitle: 'Ihre Immobilie auf einem Bildschirm.',
    scPmScreenLede: 'Kein PDF einmal im Monat. Das sind die Übersichten, mit denen Ihr Verwaltungsteam arbeitet — für Sie jederzeit einsehbar und aus deren Unterlagen im Jahresverlauf befüllt.',
    scPmScreen1Title: 'Miete im Blick',
    scPmScreen1: 'Eingegangene Miete, offene Zahlungen und Netto pro Jahr — je Objekt statt als eine Summe für das ganze Portfolio.',
    scPmScreen2Title: 'Vermietungsstand und Rendite',
    scPmScreen2: 'Welche Einheiten Ertrag bringen, welche leer stehen, und die Bruttorendite über Ihren gesamten Bestand.',
    scPmScreen3Title: 'Mietvertragsverlängerungen',
    scPmScreen3: 'Alle Enddaten in einer Liste, alles innerhalb von neunzig Tagen ganz oben — damit eine Verlängerung eine Entscheidung ist und keine Überraschung.',
    scPmScreen4Title: 'Dokumente',
    scPmScreen4: 'Ejari, Mietverträge und Emirates-ID-Kopien beim jeweiligen Objekt abgelegt statt irgendwo in einem Postfach.',
    scPmScreen5Title: 'Instandhaltung',
    scPmScreen5: 'Aufträge erfasst, Kostenvoranschlag eingeholt — und zur Freigabe vorgelegt, bevor jemand Ihr Geld ausgibt.',
    scPmScreen6Title: 'Inventar',
    scPmScreen6: 'Was in welchem Raum steht, Raum für Raum, damit beim Auszug gegen eine Liste geprüft wird und nicht gegen jemandes Erinnerung.',
    scPmNotice: 'Servicegebühren, DEWA, Ejari-Registrierung und behördliche Gebühren bleiben Kosten des Eigentümers — sie erscheinen in der Kostenübersicht und werden nicht stillschweigend übernommen. Was die Verwaltung selbst kostet, hängt vom Objekt ab und wird vor Beginn mit Ihnen vereinbart.',
    scPmCloseEyebrow: 'SPRECHEN SIE MIT DEM TEAM',
    scPmCloseTitle: 'Fragen Sie zu Ihrer Immobilie.',
    scPmCloseLede: 'Die Immobilienverwaltung hat eine eigene Nummer — beantwortet von den Menschen, die die Gebäude tatsächlich betreuen, nicht vom Vertrieb. Nennen Sie uns Turm und Einheit, und Sie bekommen eine klare Auskunft darüber, was sie braucht.',
    scPmWhats: 'Schreiben Sie uns per WhatsApp',
    scPmNumberLabel: 'Leitung der Immobilienverwaltung',
    scPmKeysAlt: 'Messingschlüssel und ein geschnürtes Bündel Unterlagen auf einer dunklen Steinkonsole im tiefen Abendlicht.',
    scPmStagesLabel: 'Die fünf Phasen der Verwaltung',
    scPmDeskSnagAlt: 'Der Mängelbildschirm: zwölf Mängel gefunden, zehn behoben, zwei offen, Bauträger informiert, dazu eine Tabelle der Mängel nach Raum mit Fotos und Status.',
    scPmDeskSnagCap: 'Phase I — die Mängelliste, gegenüber dem Bauträger dokumentiert',
    scPmDeskHandAlt: 'Der Übergabebildschirm: eine fünfteilige Checkliste, vollständig erledigt, sowie der Objektdatensatz mit DEWA-Premise, Makani-Nummer, Grundstücksnummer und Übergabedatum.',
    scPmDeskHandCap: 'Phase II — Schlüssel, Zähler und Objektdatensatz',
    scPmDeskTenAlt: 'Der Bildschirm zur Mietervermittlung: der Ablauf von der Inserierung bis zur Ejari-Registrierung, eine Prüfcheckliste sowie der Mietvertrag mit Ejari-Nummer, Laufzeit, Jahresmiete und Schecks.',
    scPmDeskTenCap: 'Phase III — Prüfung, dann das Ejari',
    scPmDeskConAlt: 'Der Dokumentenbildschirm: Mietvertrag, Ejari-Zertifikat, Eigentumsurkunde, Emirates ID des Mieters und Scheckplan, jeweils mit Datum, Größe und Ansichtsschaltfläche.',
    scPmDeskConCap: 'Phase IV — jedes Dokument, beim Objekt abgelegt',
    scPmDeskAlt: 'Das Eigentümer-Dashboard auf einem Desktop-Monitor: Portfoliowert, eingegangene Miete, Vermietungsstand und Bruttorendite; ein Balkendiagramm der Mieteinnahmen pro Monat; eine Kostenübersicht bis zum Nettoergebnis; und eine Objekttabelle mit Mieter, Vertragsende, Miete und Status.',
    scPmDeskCap: 'Phase V — Miete, Kosten und Netto, Monat für Monat',
    scPmShotDashCap: 'Ihr Dashboard',
    scPmShotInsCap: 'Inspektionsberichte',
    scPmShotExpCap: 'Kosten und Netto',
    scPmShotInsAlt: 'Der Inspektionsbildschirm: ein Einzugsbericht mit Hinweis und eine bestandene Regelkontrolle, beide zum Öffnen, mit Fotos und dem Hinweis, dass die Instandhaltung vom Team übernommen wird.',
    scPmShotExpAlt: 'Der Kostenbildschirm: Nettoeinnahmen des Jahres, mit Servicegebühr, Instandhaltung, DEWA und Verwaltungsgebühr einzeln gegen die eingegangene Miete gerechnet.',
    scPmShotAlt: 'Der Bildschirm der Immobilienverwaltung: eingegangene Miete und Vermietungsstand, das Objekt mit seinem Mietstatus, eine anstehende Vertragsverlängerung und ein Kostenvoranschlag zur Freigabe.',
    scPmShotNote: 'Eine Illustration des Eigentümer-Bildschirms, gezeichnet statt aufgenommen — ein echter Screenshot trüge Namen, Einheit und Miete einer realen Person. Die Bereiche sind die tatsächlichen; die Zahlen gehören niemandem.',
    scHomeVoicesEyebrow: 'IN IHREN WORTEN',
    scHomeVoicesNote: 'Videos starten stumm \u2014 Ton einschalten zum Anh\u00f6ren.',
    scHomeInsideEyebrow: 'WO SIE STARTEN',
    scHomeInsideTitle: 'Sechs Einstiege',
    scHomeInsideUnits: 'Aktuelle Einheiten aus unseren eigenen Listings \u2014 mit dem Abschlag zum Marktpreis bei jeder Einheit.',
    scHomeInsideFractional: 'Ab AED 4.300 einen Anteil an einer gepr\u00fcften Dubai-Immobilie halten, \u00fcber ein digitales Wertpapier.',
    scHomeInsideStays: 'Wohnen Sie vor dem Kauf in einem unserer eigenen Apartments \u2014 Termine w\u00e4hlen und direkt buchen.',
    scHomeInsidePm: '\u00dcbergeben Sie uns die Bewirtschaftung \u2014 Mieter, Ejari, Miete, Inspektionen und Reparaturen auf einem Bildschirm.',
    scHomeInsideJourney: 'Sehen Sie es selbst: eine begleitete Investorenreise durch Markt, Objekte und Beh\u00f6rdenwege.',
    scHomeInsideServices: 'Der vollst\u00e4ndige Katalog \u2014 Gesellschaftsgr\u00fcndung, Treuhand, Abnahme, Vermietung, Steuern, Aufenthalt und Nachfolge.',
    scHomeRouteHint: 'Etappe antippen, um dorthin zu springen',
    scHomeStageAria: 'Etappe {n} von 6',
    scHomeFamEyebrow: 'MEHR ALS EINE ANLAGE',
    scHomeFamTitle: 'Ein Ort, an dem Ihre Familie wirklich leben kann',
    scHomeFamLede: 'Viele unserer Eigent\u00fcmer beginnen mit einer Einheit und verbringen am Ende echte Zeit hier. Dubai ist daf\u00fcr gemacht \u2014 kurze Schulwege, Stra\u00dfen, auf denen man auch abends gern unterwegs ist, Str\u00e4nde und Parks wenige Minuten von dem Turm entfernt, in den Sie investiert haben, und Sonne fast das ganze Jahr.',
    scHomeFam1Title: 'Schulen, die Sie kennen',
    scHomeFam1: 'Deutsche, britische, amerikanische und IB-Lehrpl\u00e4ne werden in der ganzen Stadt unterrichtet \u2014 ein Umzug bedeutet keinen Neuanfang in der Schule.',
    scHomeFam1Alt: 'Der Innenhof eines Schulcampus in Dubai im Morgenlicht',
    scHomeFam2Title: 'Alltag im Freien',
    scHomeFam2: 'Gepflegte Quartiersparks, Promenaden zum Spazieren und ein Abend mit Kindern, der keine Planung braucht.',
    scHomeFam2Alt: 'Ein Quartierspark mit Spielplatz im Abendlicht',
    scHomeFam3Title: 'Aufenthalt f\u00fcr alle',
    scHomeFam3: 'Ein immobilienbasiertes Golden Visa kann Ehepartner und Kinder einschlie\u00dfen. Wir \u00fcbernehmen Antrag, medizinische Untersuchung und Emirates ID.',
    scHomeFam3Alt: 'Die Promenade der Dubai Marina zur blauen Stunde',
    scHomeFamCta: 'Sehen Sie es sich selbst an',
    scHomeFamHeroAlt: 'Eine Familie auf einer Strandpromenade in Dubai bei Sonnenuntergang, dahinter die Skyline',
    scHomeFamNote: 'Die Voraussetzungen f\u00fcr das Golden Visa h\u00e4ngen von der Immobilie und den aktuellen Regeln der VAE ab. Wir pr\u00fcfen vorab, was f\u00fcr Sie gilt.',
    scHomeCloseEyebrow: 'DER N\u00c4CHSTE SCHRITT',
    scHomeCloseTitle: 'Sagen Sie uns, wo Sie stehen \u2014 wir \u00fcbernehmen ab dort',
    scHomeCloseLede: 'Ob Sie in Dubai bereits besitzen oder es erst abw\u00e4gen: Ein kurzes Gespr\u00e4ch zeigt am schnellsten, was f\u00fcr Sie gilt.',
    scHomeWhats: 'Per WhatsApp schreiben',
    scHomeHeroAlt: 'Die Skyline von Dubai in der Abendd\u00e4mmerung von einer privaten Terrasse',
    scTabUnits: 'Einheiten',
    scTabUnitsCap: 'Kaufen',
    scTabStaysCap: 'Mieten',
    scTabJourneyCap: 'Ablauf',
    scTabServicesCap: 'Inhouse',
    scViewAll: 'Alle {n} Einheiten',
    scTabStays: 'Aufenthalte',
    scTabJourney: 'Reise',
    scTabServices: 'Leistungen',

    // ---- Kurzzeitvermietung (Airbnb-Firma in Gründung) ----
    scStaysEyebrow: 'AUFENTHALT BUCHEN',
    scStaysTitle: 'Kurzaufenthalte am Meer',
    scStaysLede: 'Möblierte Wohnungen in Dubai, direkt bei uns gebucht.',
    scStaysStat1: 'Wohnungen',
    scStaysStat2: 'Erwartete Nettorendite',
    scStaysStat3: 'Zum Meer',
    scStaysStat3Val: '1. Reihe',
    scStaysPoint1: 'Komplett ausgestattet nach Kurzzeitmiet-Standard — die Ausstattung mit den höchsten Renditen.',
    scStaysPoint2: 'Zehn Minuten von Dubai Island, direkt am Strand.',
    scStaysPoint3: 'Betrieben von der Asset Desk Airbnb-Firma, in Gründung unter unserem eigenen Dach.',
    scStaysPoint4: 'Möblierte Kurzzeitvermietung erzielt 5–12 % höhere Preise als Langzeitmiete.',
    scStaysNoteTitle: 'Noch nicht buchbar',
    scStaysNote: 'Die Wohnungen werden noch ausgestattet und das Buchungssystem ist nicht aktiv. Nennen Sie uns Ihren Zeitraum — wir melden uns, sobald es losgeht.',
    scStaysCta: 'Nach einem Aufenthalt fragen',

    // ---- Vertrauensmerkmale + Lead-Erfassung ----
    scHeroCtaUnits: 'Einheiten ansehen',

    // ---- Marketing-Band. Jede Aussage beruht auf echten Daten. ----
    scValueEyebrow: 'WARUM INVESTOREN ZU UNS KOMMEN',
    scValueTitle: 'Kein Anzeigenportal.',
    scValueLede: 'Was am Markt ist, zeigt Ihnen jeder. Wir zeigen Ihnen, was sich zu besitzen lohnt — und führen es dann.',
    scValue1Title: 'Unter Marktwert, nicht nur am Markt',
    scValue1: 'Einheiten unter unabhängiger Bewertung. Die schärfste auf der Liste liegt heute {n}% unter Marktwert.',
    scValue1Fallback: 'Einheiten unter unabhängiger Bewertung, gesichert bevor sie auf den freien Markt kommen.',
    scValue2Title: 'Wir investieren selbst mit',
    scValue2: 'Die Partner halten eigenes Kapital in Dubai-Immobilien und handeln über dieselbe Plattform.',
    scValue3Title: 'Ein Team, der ganze Weg',
    scValue3: 'Firmengründung, Treuhand, Mängelprüfung, Vermietung, Steuern und Verkauf — {n} Leistungen, keine Übergaben.',
    scValue4Title: 'Möbliert bringt mehr',
    scValue4: 'Kurzzeitmiete liegt 5–12% über Langzeitmiete, mit rund 11,3% erwarteter Nettorendite im ersten Jahr.',

    // ---- Reise-Details ----
    scJourney1: 'Projektbesichtigungen mit den Bauträgern, kein Rundgang im Verkaufsbüro.',
    scJourney2: 'Marktbriefings zu Preisen, Renditen und wo der Wert liegt.',
    scJourney3: 'Zeit vor Ort mit dem Team, das Ihren Kauf abwickelt.',
    scJourney4: 'Und Dubai selbst — die Wahrzeichen, die Viertel und die Orte, an denen Sie wirklich leben würden.',
    scJourneyFilmEyebrow: 'VON DER LETZTEN REISE',
    scJourneyFilmTitle: 'So läuft es ab',
    scJourneyFilmNote: 'Startet stumm — Ton einschalten.',
    scTrust1: 'Wir investieren selbst mit',
    scTrust2: 'Nur geprüfte Angebote',
    scTrust3: 'Off-Market-Deals unter Marktwert',
    scLeadTitle: 'Vollständige Unterlagen anfordern',
    scLeadSub: 'Grundriss, Zahlungsplan, Service Charges und ein Besichtigungstermin — von unserem Team, meist am selben Tag.',
    scLeadName: 'Vollständiger Name',
    scLeadEmail: 'E-Mail-Adresse',
    scLeadPhone: 'Telefon (optional)',
    scLeadMsg: 'Sollen wir etwas wissen? (optional)',
    scLeadSend: 'Unterlagen anfordern',
    scLeadSending: 'Wird gesendet…',
    scLeadDone: 'Vielen Dank — Ihre Anfrage liegt unserem Team vor. Wir melden uns mit allen Unterlagen zu dieser Einheit.',
    scLeadErr: 'Das hat nicht funktioniert. Bitte erneut versuchen oder per WhatsApp melden.',
    scLeadRequired: 'Bitte Namen und eine gültige E-Mail-Adresse eingeben.',
    scLeadLegal: 'Wir verwenden Ihre Daten nur zur Beantwortung dieser Anfrage. Datenschutzerklärung auf dem Startbildschirm.',
    scStaysOpen: 'Jetzt buchbar',
    scStaysBook: 'Termine prüfen & buchen',
    scStaysBookedVia: 'Buchung über unser Reservierungssystem',
    scStayA: 'Studio Aurora',
    scStayASub: '7 Minuten zur Dubai Mall',
    scStayB: 'Residence Aurelia',
    scStayBSub: 'Rashid Yachts & Marina, Meerblick',
    scStaysMoreTitle: 'Weitere Wohnungen folgen',
    scStaysMore: 'Sechs weitere eigene Wohnungen, zehn Minuten von Dubai Island und erste Reihe am Meer, werden gerade ausgestattet und kommen nach und nach dazu.',
    scImageImpression: 'Standortbild ist eine Illustration — Fotos der Wohnungen folgen, sobald sie ausgestattet sind.',
    scFilterAll: 'Alle',
    scFilterDeals: 'Unter Marktwert',
    scFilterStudio: 'Studio',
    scFilter1: '1 Zimmer',
    scFilter2: '2 Zimmer',
    scFilter3: '3+ Zimmer',
    scShowMore: 'Mehr anzeigen',
    scNoMatch: 'Noch keine Einheiten zu diesem Filter.',
    scUnitCount: '{n} Einheiten',
    scExplore: 'Ansehen',
    scDocument: 'Dokument',
    scDocuments: 'Dokumente',

    // ---- Einheiten im Verkauf (live aus Dubai Sell) ----
    scForSaleEyebrow: 'JETZT VERFÜGBAR',
    scForSaleTitle: 'Einheiten im Verkauf',
    scForSaleLede: 'Aktuelles Angebot in Dubai — jede bei uns gelistete Einheit, mit Preis und besichtigungsbereit.',
    scBed: 'Zimmer',
    scBeds: 'Zimmer',
    scStudio: 'Studio',
    scSqft: 'sqft',
    scBelowMarket: 'unter Marktwert',
    scMarketValue: 'Marktwert',
    scPriceOnRequest: 'Preis auf Anfrage',
    scUntitledUnit: 'Einheit in Dubai',
    scShowingUnits: '{shown} von {total} Einheiten',
    scUnitsError: 'Die Angebote konnten gerade nicht geladen werden.',
    scUnitsEmpty: 'Derzeit sind keine Einheiten gelistet. Sprechen Sie uns an, wonach Sie suchen.',
    scRetry: 'Erneut versuchen',

    // ---- Dubai-Reise ----
    scJourneyEyebrow: 'ÜBERZEUGEN SIE SICH SELBST',
    scJourneyTitle: 'Die Dubai-Reise',
    scJourneyLede: 'Einige Tage in Dubai, von uns durchgehend organisiert — bei den Bauträgern, in den Einheiten und unterwegs in der Stadt selbst. Sie reisen mit anderen Investoren und mit dem Team, das Ihren Kauf abwickelt.',
    scJourneyCta: 'Platz buchen',
    scOpenInBrowser: 'Im Browser öffnen',

    // ---- Überschrift Leistungen ----
    scServicesEyebrow: 'ALLES RUND UM DEN KAUF',
    scServicesTitle: 'So begleiten wir Sie',
    scServicesLede: 'Sechs Etappen, in der Reihenfolge, die Sie durchlaufen — von der ersten Steuerfrage bis zum Verkaufstag.',

    // Die sechs Etappen, in der Reihenfolge, die ein Investor durchläuft.
    scCh1Title: 'Bevor Sie kaufen',
    scCh1Lede: 'Struktur und Finanzierung zuerst geklärt, damit der Kauf selbst der einfache Teil ist.',
    scCh2Title: 'Der Kauf',
    scCh2Lede: 'Wir übernehmen Unterlagen und Fristen, damit beim Land Department nichts ins Stocken gerät.',
    scCh3Title: 'Die Übergabe',
    scCh3Lede: 'Jeder Mangel gefunden und behoben, bevor Sie die Schlüssel annehmen.',
    scCh4Title: 'Besitzen & verdienen',
    scCh4Lede: 'Ihre Einheit vermietet, instand gehalten und korrekt versteuert, während Sie tausende Kilometer entfernt sind.',
    scCh5Title: 'Leben in Dubai',
    scCh5Lede: 'Aufenthalt, Absicherung und Papiere, die aus einer Investition einen Lebensort machen.',
    scCh6Title: 'Wenn Sie verkaufen',
    scCh6Lede: 'Verkauf oder Weitergabe — bewertet, vermarktet und übertragen.',

    scComingSoon: 'BALD',
    scDraftCopy: 'ENTWURF',
    scWhatWeNeed: 'Was wir von Ihnen benötigen',
    scAskOnWhatsApp: 'Per WhatsApp fragen',
    scBookCall: 'Termin buchen',
    scHelpTitle: 'Sie wissen nicht, wo Sie anfangen sollen?',
    scHelpSub: 'Sagen Sie uns, was Sie besitzen oder planen — wir zeigen Ihnen die passende Leistung.',

    // ============ ASSET DESK DEMO (Produkttour ohne Login) ============
    dmEyebrow: 'PRODUKTTOUR',
    dmTitle: 'Asset Desk',
    dmSub: 'Was ein Eigentümer zu jeder seiner Einheiten sieht',
    dmBannerTitle: 'Beispieldaten',
    dmBannerText: 'Diese zwei Einheiten sind Beispiele, keine echten Immobilien. Die Zahlen sind eine Momentaufnahme von August 2026 — in einem echten Konto werden Ihre eigenen Einheiten anhand aktueller DLD-Transaktionen und Ejari-Verträge bewertet.',
    dmHowToHint: 'Öffnen Sie den Tab „Bewertung“ und tippen Sie auf eine Immobilie, um den vollständigen Bewertungsbericht zu sehen.',
    dmSampleInvestor: 'Beispiel-Investor',

    // ---- Zugangscode ----
    dmGateEyebrow: 'GASTZUGANG',
    dmGateTitle: 'Produkt-Demo',
    dmGateSub: 'Geben Sie Ihren Zugangscode ein, um alle Ansichten mit Beispieldaten zu sehen',
    dmGateLabel: 'Zugangscode',
    dmGateButton: 'Weiter',
    dmGateWrong: 'Dieser Code ist nicht korrekt. Bitte fragen Sie Ihren Asset-Desk-Ansprechpartner nach dem aktuellen Code.',
    dmGateFoot: 'Nur Beispieldaten — es werden keine echten Investoren, Mieter oder Immobilien gezeigt',

    // ---- Ansicht wählen ----
    dmHubEyebrow: 'BEISPIELDATEN',
    dmHubTitle: 'Ansicht wählen',
    dmHubSub: 'Das Produkt aus jeder Perspektive',
    dmHubNotice: 'Alle Ansichten hinter diesem Punkt laufen mit Beispieldaten, und die App kann im Demo-Modus keine echten Daten abrufen. Nichts, was Sie hier antippen, verändert etwas Echtes.',
    dmHubOwner: 'Eigentümer',
    dmHubOwnerSub: 'Das komplette Asset Desk — Portfolio, Bewertung, Dokumente, Zahlungen',
    dmHubTenant: 'Mieter',
    dmHubTenantSub: 'Zahlungsplan, Mietvertrag, Wartungsanfragen',
    dmHubCeo: 'CEO',
    dmHubCeoSub: 'Unternehmenszahlen, Forderungen, VAT & Compliance',
    dmHubGuest: 'Kurzzeit-Gast',
    dmHubGuestSub: 'Kurzaufenthalte in Asset-Desk-Wohnungen buchen',
    dmAccountBannerText: 'Sie sind in einem Beispielkonto angemeldet. Die angezeigten Immobilien sind Beispiele und kein echter Bestand — nutzen Sie es, um die App kennenzulernen. Uploads, Anfragen und Benachrichtigungen sind deaktiviert.',

    // ==================== PORTAL-RAHMEN ====================
    pcRefresh: 'Aktualisieren',
    pcChangeRole: 'Rolle wechseln',

    // ==================== VORSCHAU IMMOBILIENVERWALTUNG ====================
    opmTagline: 'Alles zu Ihrer Immobilie an einem Ort.',
  },
};
